// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ==========================================================================
   MedGen / Medical Assistant — App.css
   Enterprise Research Theme v2
   --------------------------------------------------------------------------
   Goal:
   - One central theme using CSS variables.
   - Compact, enterprise-table UI inspired by investigation/research dashboards.
   - No hard-coded green dependency. Change --primary / --primary-dark once.
   - Keeps existing class names used across your React app.
   ========================================================================== */

/* Fonts: system enterprise stack. No external dependency, sharper rendering on Mac/Windows/Linux. */

:root {
  /* Brand palette — change these 6 values to re-skin the full app */
  --primary: #0f1876;
  --primary-600: #0d9488;
  --primary-700: #290f76;
  --primary-800: #115e59;
  --primary-900: #134e4a;
  --primary-soft: rgba(15, 118, 110, 0.11);

  /* Neutral UI */
  --bg: #eef1f4;
  --bg-page: #f4f6f8;
  --bg-elevated: #ffffff;
  --bg-soft: #f7f9fb;
  --surface: #ffffff;
  --surface-strong: #ffffff;
  --surface-soft: #f8fafc;
  --surface-muted: #eef2f6;
  --surface-dark: #111827;

  /* Text */
  --heading: #0f172a;
  --text: #1f2937;
  --text-soft: #64748b;
  --text-muted: #8492a6;
  --text-inverse: #ffffff;

  /* Borders / shadows */
  --border: #d7dee8;
  --border-strong: #b8c3d1;
  --grid-line: rgba(100, 116, 139, 0.16);
  --shadow-xs: 0 1px 2px rgba(15, 23, 42, 0.06);
  --shadow-sm: 0 4px 12px rgba(15, 23, 42, 0.07);
  --shadow-md: 0 10px 26px rgba(15, 23, 42, 0.10);
  --shadow-lg: 0 20px 48px rgba(15, 23, 42, 0.16);

  /* Status */
  --success: #16a34a;
  --success-soft: #dcfce7;
  --warning: #d97706;
  --warning-soft: #fef3c7;
  --danger: #dc2626;
  --danger-soft: #fee2e2;
  --info: #2563eb;
  --info-soft: #dbeafe;

  /* Layout */
  --max-width: 100%;
  --topbar-height: 74px;
  --radius-xs: 6px;
  --radius-sm: 8px;
  --radius-md: 10px;
  --radius-lg: 14px;
  --radius-xl: 18px;
  --radius-2xl: 24px;

  /* Typography */
  --font-main: "Inter", "Noto Sans Devanagari", Arial, Helvetica, sans-serif;
  --fs-xs: 11px;
  --fs-sm: 12px;
  --fs-md: 13px;
  --fs-base: 14px;
  --fs-lg: 16px;
  --fs-xl: 20px;
  --fs-2xl: 26px;
  --fs-3xl: 32px;

  /* Tables */
  --table-header-bg: var(--primary-800);
  --table-header-text: #ffffff;
  --table-row-bg: #ffffff;
  --table-row-alt: #f8fafc;
  --table-row-hover: #eef7f6;
  --table-cell-border: #cfd8e3;
}

/* Optional dark mode still supported */
html[data-theme="dark"] {
  --primary: #14b8a6;
  --primary-600: #2dd4bf;
  --primary-700: #14b8a6;
  --primary-800: #0f766e;
  --primary-900: #134e4a;
  --primary-soft: rgba(45, 212, 191, 0.13);

  --bg: #0b1020;
  --bg-page: #0f172a;
  --bg-elevated: #111827;
  --bg-soft: #111827;
  --surface: #121a2c;
  --surface-strong: #111827;
  --surface-soft: #182235;
  --surface-muted: #1f2937;
  --surface-dark: #030712;

  --heading: #f8fafc;
  --text: #e5e7eb;
  --text-soft: #a7b0c0;
  --text-muted: #7d8aa1;
  --border: rgba(148, 163, 184, 0.22);
  --border-strong: rgba(203, 213, 225, 0.30);
  --table-row-bg: #111827;
  --table-row-alt: #0f172a;
  --table-row-hover: #143b3a;
  --table-cell-border: rgba(148, 163, 184, 0.24);
}

/* ==========================================================================
   Base reset
   ========================================================================== */

*,
*::before,
*::after {
  box-sizing: border-box;
  min-width: 0;
}

html,
body,
#root {
  margin: 0;
  min-height: 100%;
  width: 100%;
}

html,
body {
  max-width: 100%;
  overflow-x: hidden;
}

body {
  font-family: var(--font-main);
  font-size: var(--fs-base);
  line-height: 1.45;
  color: var(--text);
  background:
    radial-gradient(circle at 4% 0%, rgba(15, 118, 110, 0.08), transparent 26%),
    linear-gradient(180deg, #f8fafc 0%, var(--bg-page) 100%);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

html[data-theme="dark"] body {
  background:
    radial-gradient(circle at 8% 0%, rgba(20, 184, 166, 0.12), transparent 28%),
    linear-gradient(180deg, #111827 0%, var(--bg-page) 100%);
}

button,
input,
select,
textarea {
  font-family: inherit;
}

button {
  border: 0;
  cursor: pointer;
}

button:disabled,
input:disabled,
select:disabled,
textarea:disabled {
  opacity: 0.58;
  cursor: not-allowed;
}

a {
  color: inherit;
}

h1,
h2,
h3,
h4,
h5,
h6,
p {
  margin-top: 0;
}

h1,
h2,
h3,
h4,
h5,
h6 {
  color: var(--heading);
  letter-spacing: -0.025em;
}

p {
  color: var(--text-soft);
}

strong {
  color: var(--heading);
}

small {
  color: var(--text-soft);
}

/* ==========================================================================
   Global form controls
   ========================================================================== */

input,
select,
textarea,
.research-input,
.filter-input,
.filter-select {
  width: 100%;
  min-height: 38px;
  padding: 9px 11px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface-strong);
  color: var(--text);
  font-size: var(--fs-md);
  font-weight: 500;
  outline: none;
  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

textarea {
  min-height: 92px;
  resize: vertical;
}

input::placeholder,
textarea::placeholder {
  color: var(--text-muted);
}

input:focus,
select:focus,
textarea:focus,
.research-input:focus,
.filter-input:focus,
.filter-select:focus {
  border-color: var(--primary-600);
  box-shadow: 0 0 0 3px var(--primary-soft);
}

/* ==========================================================================
   Buttons
   ========================================================================== */

.btn,
.research-primary,
.impact-next-btn,
.impact-secondary-btn,
.ask-llm-btn,
.admin-primary-btn,
.admin-filter-card button,
.admin-pagination button,
.admin-modal-actions button,
.admin-actions button,
.chemical-search-bar button,
.chemical-pagination button,
.bio-library-search button,
.selected-drugs-open-btn,
.face-actions button,
.avatar-test-actions button,
.molecular-studio-actions button,
.studio-preview-actions button,
.studio-library-open-btn {
  min-height: 36px;
  padding: 8px 13px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-md);
  font-weight: 700;
  letter-spacing: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.12s ease, box-shadow 0.16s ease;
}

.btn:hover,
.research-primary:hover,
.admin-primary-btn:hover,
.chemical-search-bar button:hover,
.bio-library-search button:hover {
  transform: translateY(-1px);
}

.btn-primary,
.research-primary,
.impact-next-btn,
.admin-primary-btn,
.chemical-search-bar button,
.chemical-pagination button,
.bio-library-search button,
.avatar-test-actions button.primary,
.molecular-studio-actions button.primary,
.studio-preview-actions button.primary {
  color: var(--text-inverse);
  background: var(--primary-800);
  box-shadow: var(--shadow-xs);
}

.btn-primary:hover,
.research-primary:hover,
.impact-next-btn:hover,
.admin-primary-btn:hover {
  background: var(--primary-900);
}

.btn-secondary,
.impact-secondary-btn,
.chemical-search-bar button.secondary {
  color: var(--heading);
  background: var(--surface-muted);
  border: 1px solid var(--border);
}

.btn-secondary:hover,
.impact-secondary-btn:hover {
  background: #e7edf4;
}

.btn-ghost {
  color: var(--text);
  background: transparent;
  border: 1px solid var(--border);
}

.btn-block {
  width: 100%;
}

.ask-llm-btn {
  color: white;
  background: #111827;
}

/* ==========================================================================
   App shell / topbar / nav
   ========================================================================== */

.app-shell {
  min-height: 100vh;
}

.app,
.app-shell,
.dashboard-shell,
.session-page,
.main-content,
.app-layout-main {
  width: 100%;
  max-width: 100vw;
}

.app-layout-shell {
  width: 100%;
  margin: 0;
  padding: 10px 18px 24px;
}

.topbar {
  width: 100%;
  max-width: var(--max-width);
  min-height: var(--topbar-height);
  margin: 0;
  padding: 10px 18px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  position: relative;
  z-index: 130;
  background: transparent;
}

.topbar-left,
.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand-mark {
  width: 42px;
  height: 42px;
  border-radius: var(--radius-md);
  display: grid;
  place-items: center;
  color: white;
  background: var(--primary-800);
  font-weight: 800;
  font-size: var(--fs-sm);
  box-shadow: var(--shadow-sm);
}

.topbar-title {
  margin: 0;
  font-size: 22px;
  line-height: 1.05;
  font-weight: 800;
  color: var(--heading);
}

.topbar-subtitle {
  margin: 3px 0 0;
  color: var(--text-soft);
  font-size: var(--fs-sm);
}

.hamburger-btn {
  width: 36px;
  height: 36px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface);
  display: grid;
  place-items: center;
  gap: 3px;
  padding: 8px;
  flex: 0 0 auto;
}

.hamburger-btn span {
  display: block;
  width: 17px;
  height: 2px;
  border-radius: 999px;
  background: var(--text);
  transition: transform 0.22s ease, opacity 0.22s ease;
}

.hamburger-btn.open span:nth-child(1) {
  transform: translateY(5px) rotate(45deg);
}

.hamburger-btn.open span:nth-child(2) {
  opacity: 0;
}

.hamburger-btn.open span:nth-child(3) {
  transform: translateY(-5px) rotate(-45deg);
}

.user-chip {
  display: flex;
  align-items: center;
  gap: 9px;
  min-height: 40px;
  padding: 6px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--shadow-xs);
}

.user-avatar,
.patient-avatar {
  display: grid;
  place-items: center;
  font-weight: 800;
}

.user-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  color: var(--primary-900);
  background: var(--primary-soft);
  font-size: var(--fs-xs);
}

.user-name {
  font-size: var(--fs-sm);
  font-weight: 800;
  color: var(--heading);
}

.user-role {
  font-size: 10px;
  color: var(--text-soft);
  text-transform: capitalize;
}

.theme-toggle {
  min-width: 128px;
}

/* Sidebar / drawers */
.desktop-sidebar-backdrop,
.mobile-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.34);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.18s ease;
  z-index: 110;
}

.desktop-sidebar-backdrop.show,
.mobile-drawer-backdrop.show {
  opacity: 1;
  pointer-events: auto;
}

.desktop-sidebar-panel {
  position: fixed;
  top: 78px;
  left: 18px;
  width: 250px;
  z-index: 120;
  transform: translateX(-115%);
  transition: transform 0.22s ease;
}

.desktop-sidebar-panel.open {
  transform: translateX(0);
}

.mobile-drawer {
  position: fixed;
  top: 0;
  left: 0;
  width: min(320px, 86vw);
  height: 100vh;
  padding: 16px;
  background: var(--surface);
  border-right: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  transform: translateX(-100%);
  transition: transform 0.22s ease;
  z-index: 1000;
  overflow-y: auto;
}

.mobile-drawer.open {
  transform: translateX(0);
}

.mobile-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.app-sidebar {
  width: 100%;
}

.app-sidebar-inner {
  padding: 14px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border);
  background: var(--surface);
  box-shadow: var(--shadow-md);
}

.app-sidebar-title,
.sidebar-section-title {
  margin-bottom: 10px;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.app-sidebar-nav {
  display: grid;
  gap: 6px;
}

.app-nav-link {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  text-decoration: none;
  color: var(--text);
  background: transparent;
  border: 1px solid transparent;
  font-size: var(--fs-md);
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
}

.app-nav-link:hover {
  background: var(--surface-soft);
  border-color: var(--border);
}

.app-nav-link.active {
  color: white;
  background: var(--primary-800);
}

/* ==========================================================================
   Generic cards / grids
   ========================================================================== */

.card,
.auth-card,
.wizard-modal,
.pdf-modal,
.sidebar-card,
.brain-canvas-card,
.brain-side-card,
.brain-selected-panel,
.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card,
.brain-page-meta-card,
.brain-source-card,
.settings-section-card,
.reviews-queue-card,
.reviews-detail-card,
.research-card,
.admin-stat-card,
.admin-table-card,
.voice-chat-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-sm);
}

.card,
.sidebar-card {
  padding: 16px;
}

.dark-card {
  color: #eef2ff;
  background: var(--surface-dark);
  border-color: rgba(148, 163, 184, 0.24);
}

.dark-card h1,
.dark-card h2,
.dark-card h3,
.dark-card h4,
.dark-card strong,
.dark-card span {
  color: #f8fafc;
}

.dark-card p,
.dark-card small,
.dark-card .muted,
.dark-muted {
  color: rgba(226, 232, 240, 0.78);
}

.subtitle,
.muted {
  color: var(--text-soft);
}

.eyebrow,
.research-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-bottom: 6px;
  font-size: var(--fs-xs);
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--primary-800);
}

.section-head,
.page-header,
.research-card-head,
.patient-card-top,
.patient-card-bottom,
.session-card-head,
.review-actions,
.pdf-modal-header,
.pdf-modal-actions,
.wizard-header,
.wizard-footer,
.session-top-actions {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.section-head {
  margin-bottom: 12px;
}

.section-head h2,
.research-card h2 {
  margin: 0 0 4px;
  font-size: var(--fs-xl);
  font-weight: 800;
}

.dashboard-shell {
  width: 100%;
  margin: 0;
  padding: 18px 0 0;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 16px;
}

.single-page-shell {
  grid-template-columns: 1fr;
}

.dashboard-sidebar,
.dashboard-main,
.full-width-main,
.research-left,
.research-right,
.relation-graph-side,
.relation-detail-stack,
.settings-grid,
.settings-actions-stack,
.brain-side-stack {
  min-width: 0;
}

.dashboard-sidebar,
.stats-stack,
.sidebar-actions,
.status-stack,
.relation-graph-side,
.relation-detail-stack,
.settings-actions-stack,
.brain-side-stack {
  display: grid;
  gap: 12px;
}

.metric-grid,
.summary-grid,
.analysis-grid,
.question-grid,
.session-top-grid,
.compact-summary-grid,
.page-stats-grid {
  display: grid;
  gap: 12px;
}

.metric-grid {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-grid-compact {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.summary-grid,
.compact-summary-grid,
.analysis-grid,
.question-grid,
.two-col,
.session-top-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.stat-tile,
.status-item,
.summary-item,
.metric-card,
.analysis-block,
.analysis-summary-card,
.analysis-doctor-note,
.empty-state-box {
  padding: 13px;
  border: 1px solid var(--border);
  background: var(--surface-soft);
  border-radius: var(--radius-md);
}

.stat-tile span,
.status-item span,
.summary-item span,
.metric-card span {
  display: block;
  margin-bottom: 5px;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}

.stat-tile strong,
.status-item strong,
.summary-item strong,
.metric-card strong {
  font-size: var(--fs-md);
}

.metric-card-primary,
.metric-card-blue,
.metric-card-pink,
.metric-card-green {
  color: white;
  background: var(--primary-800);
  border-color: transparent;
}

.metric-card-primary *,
.metric-card-blue *,
.metric-card-pink *,
.metric-card-green * {
  color: white !important;
}

/* ==========================================================================
   Research page main structure
   ========================================================================== */

.research-page {
  padding: 18px;
  color: var(--text);
}

.research-hero {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: stretch;
  margin-bottom: 16px;
  padding: 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
}

.research-hero-pro {
  position: relative;
  overflow: hidden;
}

.research-hero-pro::after {
  content: "";
  position: absolute;
  right: 260px;
  top: -90px;
  width: 230px;
  height: 230px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--primary-soft), transparent 70%);
  pointer-events: none;
}

.research-hero h1 {
  margin: 0;
  font-size: var(--fs-2xl);
  line-height: 1.1;
  font-weight: 850;
}

.research-hero p {
  margin: 5px 0 0;
  color: var(--text-soft);
  font-size: var(--fs-md);
}

.research-warning {
  min-width: 250px;
  padding: 15px;
  border-radius: var(--radius-md);
  background: #111827;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.research-warning strong {
  color: white;
}

.research-warning span {
  color: #cbd5e1;
  margin-top: 6px;
  font-size: var(--fs-sm);
}

.research-mini-stats {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.research-mini-stats span,
.pill,
.saved-pill,
.selected-patient-chip,
.bmi-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 5px 8px;
  border-radius: 999px;
  font-size: var(--fs-xs);
  font-weight: 800;
  background: var(--primary-soft);
  color: var(--primary-900);
  border: 1px solid rgba(15, 118, 110, 0.16);
}

.pill.soft,
.saved-pill,
.selected-patient-chip {
  background: var(--success-soft);
  color: #166534;
  border-color: #bbf7d0;
}

.pill.warning {
  background: var(--warning-soft);
  color: #92400e;
}

.pill.danger {
  background: var(--danger-soft);
  color: #991b1b;
}

.pill.neutral {
  background: var(--surface-muted);
  color: var(--text-soft);
  border-color: var(--border);
}

.research-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.research-tabs button,
.session-tab,
.wizard-step,
.bio-library-tabs button,
.bio-workbench-mode-tabs button,
.impact-step-tabs button,
.chemical-az-filter button {
  min-height: 34px;
  padding: 7px 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface-muted);
  color: var(--text);
  font-size: var(--fs-md);
  font-weight: 750;
}

.research-tabs button.active,
.session-tab.active,
.wizard-step.active,
.bio-library-tabs button.active,
.bio-workbench-mode-tabs button.active,
.impact-step-tabs button.active,
.chemical-az-filter button.active {
  color: white;
  background: var(--primary-800);
  border-color: var(--primary-800);
  box-shadow: none;
}

.research-grid,
.research-layout-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 16px;
  align-items: start;
}

.research-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.research-right,
.patient-context-pro,
.sticky-panel {
  position: sticky;
  top: 14px;
}

.research-card {
  padding: 16px;
}

.research-card.wide,
.result-card {
  grid-column: 1 / -1;
}

.research-card-head span {
  background: var(--primary-soft);
  color: var(--primary-900);
  border: 1px solid rgba(15, 118, 110, 0.15);
  height: fit-content;
  border-radius: 999px;
  padding: 6px 9px;
  font-weight: 800;
  font-size: var(--fs-xs);
}

.compact-builder {
  min-height: 320px;
}

.simulation-canvas-card {
  min-height: 520px;
}

.result-card-pro {
  margin-top: 0;
  background: var(--surface);
}

/* ==========================================================================
   Tables — app-wide normalized look
   ========================================================================== */

table {
  border-collapse: collapse;
}

.admin-table-card,
.organ-impact-table-wrap,
.impact-table-wrap {
  width: 100%;
  overflow-x: auto;
  border-radius: var(--radius-sm);
  border: 1px solid var(--table-cell-border);
  background: var(--surface);
}

.admin-table,
.organ-impact-table,
.impact-table {
  width: 100%;
  min-width: 900px;
  border-collapse: collapse;
  table-layout: fixed;
  background: var(--surface);
}

.admin-table th,
.organ-impact-table th,
.impact-table th {
  height: 34px;
  padding: 7px 9px;
  text-align: left;
  background: var(--table-header-bg);
  color: var(--table-header-text);
  border-right: 1px solid rgba(255, 255, 255, 0.22);
  border-bottom: 1px solid var(--primary-900);
  font-size: 11px;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
  vertical-align: middle;
}

.admin-table td,
.organ-impact-table td,
.impact-table td {
  min-height: 38px;
  padding: 8px 9px;
  vertical-align: top;
  border-right: 1px solid var(--table-cell-border);
  border-bottom: 1px solid var(--table-cell-border);
  color: var(--text);
  font-size: var(--fs-sm);
  line-height: 1.35;
  background: var(--table-row-bg);
}

.admin-table tbody tr:nth-child(even) td,
.organ-impact-table tbody tr:nth-child(even) td,
.impact-table tbody tr:nth-child(even) td {
  background: var(--table-row-alt);
}

.admin-table tbody tr:hover td,
.organ-impact-table tbody tr:hover td,
.impact-table tbody tr:hover td {
  background: var(--table-row-hover);
}

.admin-table td strong,
.admin-table td small {
  display: block;
}

.admin-table td small {
  margin-top: 3px;
  color: var(--text-soft);
}

.organ-impact-table-card {
  margin-top: 16px;
  padding: 14px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-sm);
}

.organ-impact-table-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.organ-impact-table-head h3 {
  margin: 0;
  color: var(--heading);
  font-size: var(--fs-lg);
}

.organ-impact-table-head p {
  margin: 3px 0 0;
  color: var(--text-soft);
  font-size: var(--fs-sm);
}

.organ-cell-name {
  display: flex;
  align-items: center;
  gap: 8px;
}

.organ-cell-name > span {
  width: 8px;
  height: 30px;
  border-radius: 999px;
  flex: 0 0 auto;
}

.organ-cell-name strong,
.organ-cell-name small {
  display: block;
}

.organ-cell-name strong {
  color: var(--heading);
  font-size: var(--fs-sm);
}

.organ-cell-name small {
  margin-top: 2px;
  color: var(--text-soft);
  font-size: 10px;
  font-weight: 700;
}

.organ-risk-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  padding: 4px 7px;
  border-radius: 999px;
  border: 1px solid currentColor;
  font-size: 10px;
  font-weight: 850;
  text-transform: uppercase;
}

.organ-score-cell {
  display: grid;
  grid-template-columns: minmax(110px, 1fr) 42px;
  gap: 8px;
  align-items: center;
}

.organ-score-track,
.organ-bar,
.score-meter {
  overflow: hidden;
  border-radius: 999px;
  background: #e2e8f0;
}

.organ-score-track {
  height: 8px;
}

.organ-score-track i,
.organ-bar div,
.score-meter div {
  display: block;
  height: 100%;
  border-radius: 999px;
}

.score-meter {
  height: 10px;
  margin-bottom: 14px;
}

.score-meter div {
  background: var(--primary-800);
}

.organ-score-cell b {
  font-size: var(--fs-xs);
  color: var(--heading);
}

.organ-inline-meta,
.organ-row-reason {
  display: block;
  color: var(--text-soft);
  font-size: var(--fs-sm);
  line-height: 1.4;
}

.organ-inline-meta {
  font-weight: 750;
  color: var(--text);
  text-transform: capitalize;
}

/* ==========================================================================
   Admin pages
   ========================================================================== */

.admin-page {
  padding: 18px;
  color: var(--text);
}

.admin-page-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
}

.admin-page-header h1 {
  margin: 0;
  font-size: var(--fs-2xl);
  font-weight: 850;
}

.admin-page-header p {
  margin-top: 5px;
  color: var(--text-soft);
}

.admin-stats-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.admin-stat-card {
  padding: 14px;
}

.admin-stat-card span {
  display: block;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  margin-bottom: 5px;
}

.admin-stat-card strong {
  font-size: var(--fs-2xl);
}

.admin-filter-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 12px;
  display: grid;
  grid-template-columns: 1fr 160px 200px 100px;
  gap: 10px;
  margin-bottom: 14px;
}

.admin-filter-card button {
  color: white;
  background: #111827;
}

.admin-error {
  background: var(--danger-soft);
  color: #991b1b;
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  margin-bottom: 12px;
}

.admin-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 22px;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: var(--fs-xs);
  font-weight: 800;
  background: var(--surface-muted);
  color: var(--text);
}

.admin-badge-green {
  background: var(--success-soft);
  color: #166534;
}

.admin-badge-yellow {
  background: var(--warning-soft);
  color: #92400e;
}

.admin-badge-red {
  background: var(--danger-soft);
  color: #991b1b;
}

.admin-badge-dark {
  background: #111827;
  color: white;
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.admin-actions button,
.admin-modal-actions button {
  background: var(--surface-muted);
  color: var(--heading);
}

.admin-actions .green,
.admin-modal-actions .green {
  background: var(--success);
  color: white;
}

.admin-actions .red,
.admin-modal-actions .red {
  background: var(--danger);
  color: white;
}

.admin-actions .orange,
.admin-modal-actions .orange {
  background: #f97316;
  color: white;
}

.admin-actions .blue,
.admin-modal-actions .blue {
  background: var(--info);
  color: white;
}

.admin-actions .dark,
.admin-modal-actions .dark {
  background: #111827;
  color: white;
}

.admin-empty {
  padding: 24px;
  text-align: center;
  color: var(--text-soft);
}

.admin-pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}

.admin-modal-backdrop,
.modal-backdrop,
.pdf-backdrop,
.selected-drugs-modal,
.studio-picker-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.52);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9998;
  padding: 18px;
  backdrop-filter: blur(8px);
}

.admin-modal,
.wizard-modal,
.pdf-modal,
.selected-drugs-card,
.studio-picker-modal {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
}

.admin-modal {
  width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 16px;
}

.admin-modal.large {
  width: min(920px, 96vw);
}

.admin-modal-header,
.selected-drugs-head,
.studio-picker-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
}

.admin-modal-header h2 {
  margin: 0;
  font-size: var(--fs-xl);
}

.admin-modal-header button,
.selected-drugs-head button,
.studio-picker-head > button {
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  background: var(--surface-muted);
  color: var(--heading);
  font-weight: 800;
}

.admin-details-grid,
.admin-form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 10px;
}

.admin-form-grid textarea {
  min-height: 88px;
  grid-column: span 2;
}

.admin-detail {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 10px;
}

.admin-detail span {
  display: block;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  margin-bottom: 4px;
}

.admin-detail strong {
  word-break: break-word;
}

.admin-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

/* ==========================================================================
   Chemical / bio library
   ========================================================================== */

.chemical-search-bar {
  display: grid;
  grid-template-columns: 1fr 88px 78px;
  gap: 8px;
  margin: 12px 0 10px;
  align-items: center;
}

.chemical-search-bar input {
  min-height: 38px;
}

.chemical-pagination-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 4px 0 10px;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
  color: var(--text-soft);
  font-size: var(--fs-xs);
  font-weight: 800;
}

.chemical-grid,
.bio-card-grid,
.history-grid-pro,
.queue-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 10px;
}

.chemical-card,
.bio-select-card,
.history-tile,
.queue-card,
.library-item,
.bio-library-item,
.selected-drug-item {
  padding: 12px;
  border-radius: var(--radius-sm);
  border: 1px solid var(--border);
  background: var(--surface);
  cursor: pointer;
  overflow: hidden;
  box-shadow: none;
}

.chemical-card {
  min-height: 105px;
}

.chemical-card:hover,
.bio-select-card:hover,
.history-tile:hover,
.bio-library-item:hover {
  border-color: var(--primary-600);
  box-shadow: var(--shadow-xs);
}

.chemical-card.selected,
.bio-select-card.selected,
.history-tile.active,
.bio-library-item.active {
  border-color: var(--primary-800);
  background: var(--table-row-hover);
  box-shadow: 0 0 0 2px var(--primary-soft);
}

.chemical-card-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.chemical-card-top strong,
.bio-select-card strong,
.bio-library-item strong,
.history-tile strong,
.queue-card strong {
  display: block;
  color: var(--heading);
  font-size: var(--fs-md);
  line-height: 1.25;
  font-weight: 800;
  overflow-wrap: anywhere;
}

.chemical-card-top span,
.bio-library-item em {
  flex-shrink: 0;
  max-width: 92px;
  padding: 4px 6px;
  border-radius: 999px;
  background: var(--surface-muted);
  color: var(--text-soft);
  font-size: 10px;
  font-weight: 800;
  line-height: 1;
  white-space: normal;
  overflow-wrap: anywhere;
  text-align: center;
}

.chemical-card p,
.bio-select-card p,
.bio-select-card small,
.bio-library-item span,
.history-tile span,
.queue-card span,
.queue-card p,
.queue-card small {
  display: block;
  margin: 7px 0 0;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  line-height: 1.35;
  overflow-wrap: anywhere;
}

.chemical-pagination {
  margin-top: 12px;
  padding: 10px;
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.chemical-pagination span {
  min-width: 88px;
  text-align: center;
  font-weight: 800;
  color: var(--text);
  font-size: var(--fs-xs);
}

.chemical-az-filter {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin: 8px 0 10px;
}

.chemical-az-filter button {
  width: 28px;
  height: 28px;
  padding: 0;
}

.bio-lab-library-page {
  display: grid;
  gap: 16px;
}

.bio-library-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bio-library-search {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.bio-library-search input {
  flex: 1 1;
}

.digital-avatar-reason-card,
.llm-suggestion-box,
.assist-result-box,
.research-empty,
.empty-studio-state {
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  background: var(--surface-soft);
  padding: 12px;
}

.digital-avatar-reason-card strong,
.empty-studio-state strong {
  display: block;
  color: var(--heading);
  margin-bottom: 5px;
}

.digital-avatar-reason-card p,
.empty-studio-state span {
  margin: 0;
  color: var(--text-soft);
  font-size: var(--fs-sm);
}

.bio-library-layout {
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 14px;
  align-items: start;
}

.bio-library-list {
  max-height: 650px;
  overflow-y: auto;
  display: grid;
  gap: 8px;
  padding-right: 3px;
}

/* ==========================================================================
   MedGen / impact panels
   ========================================================================== */

.impact-workbench {
  max-width: 1180px;
}

.impact-workbench-header {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: var(--shadow-sm);
}

.impact-workbench-header h2 {
  margin: 0;
  font-size: var(--fs-xl);
}

.impact-workbench-header p {
  color: var(--text-soft);
  margin: 5px 0 0;
}

.impact-step-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.impact-step-card {
  max-width: 980px;
}

.impact-pro-grid,
.impact-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(240px, 1fr));
  gap: 10px;
}

.impact-field label,
.research-label,
.dose-row label {
  display: block;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  font-weight: 800;
  margin-bottom: 5px;
}

.impact-field.wide,
.impact-grid textarea {
  grid-column: 1 / -1;
}

.impact-action-row {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 14px;
}

.impact-save-btn {
  width: auto;
  min-width: 190px;
}

.impact-validate-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 390px;
  gap: 14px;
  align-items: start;
}

.medgen-side-panel,
.studio-medgen-console {
  background: #0f172a;
  color: white;
  border-radius: var(--radius-lg);
  padding: 14px;
  min-height: 420px;
  box-shadow: var(--shadow-sm);
}

.medgen-side-panel h3 {
  margin: 0 0 6px;
  font-size: var(--fs-lg);
  color: white;
}

.medgen-side-panel p {
  color: #cbd5e1;
  line-height: 1.55;
}

.medgen-stream-box,
.medgen-final-box {
  margin-top: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: var(--radius-sm);
  padding: 10px;
  color: #cbd5e1;
  max-height: 360px;
  overflow-y: auto;
  font-size: var(--fs-sm);
  line-height: 1.55;
}

.medgen-stream-box pre,
.medgen-final-box pre,
.analysis-stream-box pre {
  white-space: pre-wrap;
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: var(--fs-sm);
}

.medgen-final-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

/* ==========================================================================
   HRA / body viewer / graph panels
   ========================================================================== */

.hra-body-viewer,
.brain-canvas-wrap,
.relation-graph-canvas-wrap,
.avatar-3d-container {
  width: 100%;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border);
  position: relative;
  background:
    linear-gradient(90deg, rgba(148, 163, 184, 0.10) 1px, transparent 1px),
    linear-gradient(rgba(148, 163, 184, 0.10) 1px, transparent 1px),
    radial-gradient(circle at center, rgba(15, 118, 110, 0.12), transparent 42%),
    #0b1020;
  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%;
}

.hra-body-viewer {
  height: 100%;
  min-height: 720px;
}

.hra-result-layout {
  display: grid;
  grid-template-columns: minmax(390px, 0.9fr) minmax(520px, 1.25fr);
  gap: 14px;
  align-items: stretch;
}

.hra-result-layout > .hra-body-viewer {
  height: 100%;
  min-height: 720px;
}

.d3-organ-intel-panel {
  min-height: 720px;
}

.hra-loading {
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: rgba(15, 23, 42, 0.92);
  color: #e0f2fe;
  font-weight: 800;
  border: 1px solid rgba(125, 211, 252, 0.35);
}

.hra-face-mask-label,
.hra-viewer-chip {
  padding: 6px 9px;
  border-radius: 999px;
  border: 1px solid rgba(45, 212, 191, 0.35);
  background: rgba(2, 6, 23, 0.74);
  color: #ccfbf1;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-align: center;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
  pointer-events: none;
}

.hra-viewer-chip {
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 5;
}

.brain-map-layout,
.relation-graph-layout,
.reviews-layout {
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.75fr);
  gap: 14px;
}

.brain-canvas-card {
  padding: 14px;
  min-height: 640px;
}

.brain-canvas-title-group,
.brain-canvas-toolbar,
.brain-chip-list,
.relation-graph-meta,
.brain-selected-head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.brain-canvas-wrap,
.relation-graph-canvas-wrap {
  min-height: 560px;
}

.relation-graph-svg {
  width: 100%;
  height: 560px;
  display: block;
}

.brain-view-toggle {
  display: flex;
  gap: 6px;
  align-items: center;
}

.brain-view-toggle button {
  min-height: 30px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 6px 10px;
  font-size: var(--fs-xs);
  font-weight: 800;
}

.brain-view-toggle button.active {
  background: var(--primary-800);
  color: white;
  border-color: var(--primary-800);
}

/* ==========================================================================
   Molecular studio legacy classes
   ========================================================================== */

.bio-studio-page {
  gap: 14px;
}

.bio-studio-shell {
  overflow: hidden;
}

.bio-studio-hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 190px;
  gap: 14px;
  align-items: stretch;
  margin-bottom: 12px;
  padding: 16px;
  border-radius: var(--radius-lg);
  background: var(--surface);
  border: 1px solid var(--border);
}

.bio-studio-kicker,
.compound-designer-head p,
.molecular-studio-header p,
.studio-picker-head p {
  margin: 0 0 5px;
  color: var(--primary-800);
  font-size: var(--fs-xs);
  font-weight: 850;
  letter-spacing: 0.09em;
  text-transform: uppercase;
}

.bio-studio-hero h2,
.molecular-studio-header h2,
.compound-designer-head h2,
.studio-picker-head h2 {
  margin: 0;
  color: var(--heading);
  font-size: clamp(22px, 3vw, 30px);
  line-height: 1.1;
}

.bio-studio-hero span,
.molecular-studio-header span,
.compound-designer-head span,
.studio-picker-head span {
  display: block;
  margin-top: 8px;
  color: var(--text-soft);
  line-height: 1.45;
  font-size: var(--fs-md);
}

.bio-studio-metrics {
  display: grid;
  align-content: center;
  padding: 14px;
  border-radius: var(--radius-md);
  background: #111827;
  color: white;
}

.bio-studio-metrics strong {
  font-size: var(--fs-2xl);
  line-height: 1;
  color: #99f6e4;
}

.bio-studio-metrics span {
  margin-bottom: 10px;
  color: #cbd5e1;
  font-size: var(--fs-xs);
  font-weight: 750;
}

.bio-workbench-mode-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 10px 0 12px;
  padding: 6px;
  border-radius: var(--radius-md);
  background: var(--surface-muted);
  border: 1px solid var(--border);
}

.avatar-test-queue-strip {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin: 12px 0 14px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: #111827;
  color: white;
}

.avatar-test-queue-strip strong {
  display: block;
  color: white;
  font-size: var(--fs-base);
}

.avatar-test-queue-strip span {
  display: block;
  margin-top: 3px;
  color: #cbd5e1;
  font-size: var(--fs-xs);
}

.avatar-test-actions,
.molecular-studio-actions,
.studio-preview-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.studio-status-banner {
  margin-bottom: 12px;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #155e75;
  font-weight: 750;
  font-size: var(--fs-sm);
}

.molecular-studio-card,
.avatar-queue-card,
.studio-control-panel,
.studio-elements-panel,
.studio-side-help {
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface);
  box-shadow: var(--shadow-sm);
}

.molecular-studio-card,
.avatar-queue-card {
  padding: 14px;
  margin-top: 12px;
}

.molecular-studio-header,
.compound-designer-head {
  display: flex;
  justify-content: space-between;
  gap: 14px;
  align-items: flex-start;
  margin-bottom: 14px;
}

.molecular-studio-layout {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr) 240px;
  gap: 12px;
  align-items: stretch;
}

.studio-control-panel,
.studio-elements-panel,
.studio-side-help {
  padding: 12px;
  background: var(--surface-soft);
}

.studio-control-panel label {
  display: block;
  margin: 10px 0 5px;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  font-weight: 800;
}

.studio-bond-source-card {
  display: grid;
  gap: 6px;
  margin-top: 12px;
  padding: 10px;
  border-radius: var(--radius-sm);
  background: var(--primary-soft);
  border: 1px solid rgba(15, 118, 110, 0.18);
}

.studio-mini-list {
  display: grid;
  gap: 6px;
  margin-top: 12px;
}

.studio-mini-list button,
.studio-bond-source-card button {
  min-height: 32px;
  border-radius: var(--radius-sm);
  padding: 7px 9px;
  background: #111827;
  color: white;
  font-weight: 800;
}

.studio-graph-panel {
  min-width: 0;
  position: relative;
}

.studio-graph-shell {
  height: 100%;
  min-height: 620px;
  padding: 12px;
  border-radius: var(--radius-lg);
  background:
    linear-gradient(90deg, rgba(148, 163, 184, 0.13) 1px, transparent 1px),
    linear-gradient(rgba(148, 163, 184, 0.13) 1px, transparent 1px),
    radial-gradient(circle at center, rgba(15, 118, 110, 0.16), transparent 40%),
    #050816;
  background-size: 32px 32px, 32px 32px, 100% 100%, 100% 100%;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);
}

.studio-graph-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
  color: white;
}

.studio-graph-toolbar strong,
.studio-graph-toolbar span,
.studio-graph-toolbar small {
  display: block;
}

.studio-graph-toolbar span,
.studio-graph-toolbar small {
  color: #94a3b8;
  font-size: var(--fs-xs);
}

.studio-molecule-svg {
  width: 100%;
  min-height: 540px;
  border-radius: var(--radius-md);
  overflow: hidden;
  display: block;
  background: transparent;
}

.studio-atom-node {
  cursor: grab;
}

.studio-elements-scroll {
  max-height: 560px;
  overflow: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  padding-right: 3px;
}

.studio-elements-scroll button {
  min-height: 50px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  cursor: pointer;
  text-align: left;
  padding: 8px;
}

.studio-elements-scroll button:hover {
  border-color: var(--primary-600);
  background: var(--table-row-hover);
}

.studio-elements-scroll strong {
  display: block;
  color: var(--heading);
  font-size: var(--fs-base);
}

.studio-elements-scroll span {
  display: block;
  color: var(--text-soft);
  font-size: 10px;
  font-weight: 750;
}

.studio-medgen-console {
  min-height: auto;
  margin-top: 12px;
  padding: 12px;
  border: 1px solid rgba(45, 212, 191, 0.24);
}

.studio-medgen-console strong,
.studio-medgen-console span {
  display: block;
}

.studio-medgen-console span {
  margin-top: 5px;
  color: #cbd5e1;
  font-size: var(--fs-sm);
}

.studio-node-context-menu {
  position: fixed;
  z-index: 9999;
  width: 220px;
  max-width: calc(100vw - 28px);
  padding: 8px;
  border-radius: var(--radius-md);
  background: rgba(17, 24, 39, 0.98);
  border: 1px solid rgba(148, 163, 184, 0.30);
  box-shadow: var(--shadow-lg);
  color: #e5e7eb;
}

.studio-node-context-head {
  padding: 8px;
  border-radius: var(--radius-sm);
  background: rgba(15, 118, 110, 0.24);
  border: 1px solid rgba(45, 212, 191, 0.24);
  margin-bottom: 6px;
}

.studio-node-context-head strong,
.studio-node-context-head span {
  display: block;
}

.studio-node-context-head strong {
  color: white;
  font-size: var(--fs-sm);
}

.studio-node-context-head span,
.studio-node-context-menu small {
  color: #cbd5e1;
  font-size: 10px;
}

.studio-node-context-menu button {
  width: 100%;
  min-height: 30px;
  border-radius: var(--radius-xs);
  margin-top: 5px;
  padding: 6px 8px;
  background: rgba(148, 163, 184, 0.14);
  color: #eef2ff;
  font-size: var(--fs-xs);
  font-weight: 750;
  text-align: left;
}

.studio-node-context-menu button:hover {
  background: rgba(45, 212, 191, 0.18);
}

.studio-node-context-menu button.danger {
  background: rgba(239, 68, 68, 0.16);
  color: #fecaca;
}

.studio-context-section {
  margin-top: 8px;
  padding-top: 7px;
  border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.studio-picker-modal {
  width: min(1080px, 96vw);
  max-height: 88vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.studio-picker-head {
  margin: 0;
  padding: 14px 16px;
  border-bottom: 1px solid var(--border);
}

.studio-picker-tabs {
  display: flex;
  gap: 7px;
  padding: 12px 14px 0;
  flex-wrap: wrap;
}

.studio-picker-tabs button {
  min-height: 32px;
  border-radius: var(--radius-sm);
  padding: 6px 10px;
  background: var(--surface-muted);
  color: var(--text);
  font-weight: 800;
  text-transform: capitalize;
}

.studio-picker-tabs button.active {
  color: white;
  background: var(--primary-800);
}

.studio-picker-bond-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 12px 14px 0;
  padding: 10px;
  border-radius: var(--radius-sm);
  background: #111827;
  color: #e0f2fe;
}

.studio-picker-bond-row select {
  width: auto;
  min-height: 30px;
  border: 0;
  border-radius: var(--radius-xs);
  padding: 6px 8px;
  background: white;
  color: #111827;
  font-weight: 750;
}

.studio-picker-search {
  margin: 12px 14px;
}

.studio-picker-grid {
  overflow: auto;
  padding: 0 14px 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.studio-picker-card {
  min-height: 96px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 10px;
  text-align: left;
  background: var(--surface);
  cursor: pointer;
}

.studio-picker-card:hover {
  border-color: var(--primary-600);
  background: var(--table-row-hover);
}

.studio-picker-card strong,
.studio-picker-card span,
.studio-picker-card small {
  display: block;
}

.studio-picker-card strong {
  color: var(--heading);
  font-size: var(--fs-md);
}

.studio-picker-card span {
  color: var(--text);
  margin-top: 5px;
  font-weight: 700;
  font-size: var(--fs-xs);
}

.studio-picker-card small {
  color: var(--text-soft);
  margin-top: 6px;
  line-height: 1.35;
  font-size: var(--fs-xs);
}

/* ==========================================================================
   Auth / wizard / PDF / voice
   ========================================================================== */

.loading-screen,
.auth-screen {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 18px;
}

.loading-card,
.auth-card {
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-md);
}

.loading-card {
  border-radius: var(--radius-lg);
  padding: 18px 24px;
  font-weight: 750;
}

.auth-card {
  width: min(480px, 100%);
  padding: 28px;
  border-radius: var(--radius-xl);
}

.auth-card h2 {
  font-size: var(--fs-2xl);
  margin: 12px 0 8px;
}

.auth-note {
  margin: 0 0 14px;
  color: var(--text-soft);
}

.form-grid {
  display: grid;
  gap: 12px;
  margin-top: 16px;
}

.form-field {
  display: grid;
  gap: 5px;
}

.form-field label,
.field-block label {
  font-size: var(--fs-sm);
  font-weight: 750;
  color: var(--heading);
}

.auth-actions {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  margin-top: 18px;
}

.switch-link {
  font-weight: 750;
  color: var(--primary-800);
  cursor: pointer;
  margin: 0;
}

.wizard-modal {
  width: min(720px, calc(100vw - 28px));
  padding: 18px;
}

.wizard-steps,
.session-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.wizard-steps {
  margin: 14px 0 16px;
}

.wizard-step {
  min-width: 128px;
}

.wizard-step span {
  display: inline-grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  background: var(--surface-muted);
  font-weight: 750;
  margin-bottom: 6px;
}

.wizard-step p {
  margin: 0;
  font-size: var(--fs-xs);
  font-weight: 750;
}

.wizard-step.done {
  border-color: rgba(22, 163, 74, 0.24);
}

.questionnaire-shell {
  padding: 0;
  overflow: hidden;
}

.questionnaire-header {
  padding: 18px 18px 10px;
}

.questionnaire-mode-switch,
.questionnaire-step-strip {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.questionnaire-mode-switch {
  margin-bottom: 12px;
  margin-left: 0;
}

.questionnaire-step-strip {
  padding: 0 18px 14px;
  border-bottom: 1px solid var(--border);
}

.compact-step {
  min-width: 118px;
}

.questionnaire-form-body {
  padding: 18px 18px 96px;
}

.questionnaire-sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 4;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 18px 14px;
  border-top: 1px solid var(--border);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
}

html[data-theme="dark"] .questionnaire-sticky-footer {
  background: rgba(17, 24, 39, 0.92);
}

.pdf-modal {
  width: min(1120px, calc(100vw - 28px));
  padding: 16px;
}

.pdf-frame-wrap {
  margin-top: 14px;
  border-radius: var(--radius-md);
  overflow: hidden;
  min-height: 72vh;
  border: 1px solid var(--border);
  background: var(--surface-soft);
}

.pdf-frame,
.pdf-loading {
  width: 100%;
  min-height: 72vh;
}

.pdf-frame {
  border: 0;
}

.pdf-loading {
  display: grid;
  place-items: center;
  color: var(--text-soft);
}

.voice-chat-card {
  padding: 14px;
}

.voice-chat-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.voice-chat-header h3 {
  margin: 0;
  font-size: var(--fs-lg);
  color: var(--heading);
}

.voice-lang-select {
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 8px 10px;
  background: var(--surface-soft);
  color: var(--text);
  font-weight: 700;
}

.voice-progress {
  margin-bottom: 12px;
}

.voice-progress span {
  display: block;
  font-size: var(--fs-xs);
  color: var(--text-soft);
  font-weight: 800;
  margin-bottom: 5px;
}

.voice-progress div {
  height: 7px;
  background: var(--surface-muted);
  border-radius: 999px;
  overflow: hidden;
}

.voice-progress i {
  display: block;
  height: 100%;
  background: var(--primary-800);
  border-radius: 999px;
}

.voice-chat-window {
  height: 380px;
  overflow-y: auto;
  padding: 12px;
  background: var(--surface-soft);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  margin-bottom: 12px;
}

.voice-msg {
  display: flex;
  margin-bottom: 10px;
}

.voice-msg.assistant {
  justify-content: flex-start;
}

.voice-msg.user {
  justify-content: flex-end;
}

.voice-bubble {
  max-width: 78%;
  padding: 10px 12px;
  border-radius: var(--radius-md);
  font-size: var(--fs-md);
  line-height: 1.45;
  white-space: pre-wrap;
}

.voice-msg.assistant .voice-bubble {
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
}

.voice-msg.user .voice-bubble {
  background: var(--primary-800);
  color: white;
}

.voice-input-row {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.voice-input-row textarea {
  flex: 1 1;
  resize: none;
  min-height: 42px;
}

.voice-mic-btn {
  width: 42px;
  min-width: 42px;
  height: 38px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
  cursor: pointer;
  font-weight: 850;
}

.voice-mic-btn.recording {
  width: auto;
  min-width: 64px;
  background: var(--danger-soft);
  color: #b91c1c;
  border-color: #fecaca;
}

/* ==========================================================================
   Face / simple body fallback
   ========================================================================== */

.face-panel {
  margin-top: 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 12px;
  background: var(--surface-soft);
}

.face-panel h2 {
  font-size: var(--fs-lg);
}

.face-panel p {
  color: var(--text-soft);
  font-size: var(--fs-sm);
}

.face-video-wrap {
  display: grid;
  gap: 8px;
  margin: 10px 0;
}

.face-video-wrap video,
.face-video-wrap img {
  width: 100%;
  border-radius: var(--radius-sm);
  background: #111827;
  min-height: 170px;
  object-fit: cover;
}

.face-actions {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.face-actions button {
  flex: 1 1;
  color: white;
  background: #111827;
}

.face-consent {
  display: flex;
  gap: 8px;
  color: var(--text);
  font-size: var(--fs-sm);
  margin-bottom: 8px;
}

.face-status {
  margin-top: 8px;
  background: var(--info-soft);
  color: #075985;
  padding: 8px;
  border-radius: var(--radius-sm);
  font-size: var(--fs-sm);
}

.human-heatmap-card {
  display: grid;
  place-items: center;
  padding: 16px;
}

.human-body {
  position: relative;
  width: 300px;
  height: 580px;
  perspective: 900px;
}

.body-head,
.body-neck,
.body-torso,
.body-arm,
.body-leg {
  position: absolute;
  background: linear-gradient(145deg, #dbeafe, #eff6ff);
  border: 1px solid #bfdbfe;
  box-shadow: inset 0 0 18px rgba(59, 130, 246, 0.14);
}

.body-head {
  width: 76px;
  height: 86px;
  border-radius: 50%;
  top: 0;
  left: 112px;
}

.body-neck {
  width: 38px;
  height: 38px;
  top: 78px;
  left: 131px;
  border-radius: 14px;
}

.body-torso {
  width: 170px;
  height: 290px;
  top: 112px;
  left: 65px;
  border-radius: 48px 48px 72px 72px;
}

.body-arm {
  width: 48px;
  height: 235px;
  top: 132px;
  border-radius: 36px;
}

.body-arm.left {
  left: 8px;
  transform: rotate(7deg);
}

.body-arm.right {
  right: 8px;
  transform: rotate(-7deg);
}

.body-leg {
  width: 64px;
  height: 196px;
  top: 384px;
  border-radius: 38px;
}

.body-leg.left {
  left: 78px;
}

.body-leg.right {
  right: 78px;
}

.organ-node {
  position: absolute;
  width: 48px;
  height: 48px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  border: 2px solid white;
  z-index: 10;
  display: grid;
  place-items: center;
  color: white;
  font-size: 9px;
  text-align: center;
  font-weight: 850;
  cursor: pointer;
}

.organ-node span {
  position: absolute;
  top: 52px;
  color: var(--heading);
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 2px 6px;
  border-radius: 999px;
  white-space: nowrap;
  font-size: 10px;
}

.heatmap-legend {
  display: flex;
  gap: 12px;
  margin-top: 14px;
  justify-content: center;
  flex-wrap: wrap;
}

.heatmap-legend span {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--text-soft);
  font-size: var(--fs-xs);
  font-weight: 800;
}

.heatmap-legend i {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.heatmap-legend .low {
  background: var(--success);
}

.heatmap-legend .moderate {
  background: var(--warning);
}

.heatmap-legend .high {
  background: var(--danger);
}

/* ==========================================================================
   Utilities
   ========================================================================== */

.full-span {
  grid-column: 1 / -1;
}

.compact {
  padding: 10px 12px;
}

.capitalize {
  text-transform: capitalize;
}

.section-spacer {
  margin-top: 24px;
}

.analysis-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 10px;
}

.loader {
  width: 28px;
  height: 28px;
  border: 3px solid rgba(15, 23, 42, 0.12);
  border-top: 3px solid var(--primary-800);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.analysis-stream-box {
  width: 100%;
  margin-top: 14px;
  padding: 12px;
  border-radius: var(--radius-md);
  background: var(--surface-soft);
  border: 1px solid var(--border);
  text-align: left;
}

.analysis-stream-box h4 {
  margin: 0 0 8px;
  font-size: var(--fs-md);
  font-weight: 800;
}

.risk-pill {
  text-transform: uppercase;
  border-radius: 999px;
  padding: 6px 10px;
  font-weight: 850;
  height: fit-content;
  font-size: var(--fs-xs);
}

.risk-pill.low {
  background: var(--success-soft);
  color: #166534;
}

.risk-pill.moderate {
  background: var(--warning-soft);
  color: #92400e;
}

.risk-pill.high {
  background: var(--danger-soft);
  color: #991b1b;
}

/* ==========================================================================
   Responsive
   ========================================================================== */

@media (max-width: 1280px) {
  .hra-result-layout {
    grid-template-columns: 1fr;
  }

  .hra-result-layout > .hra-body-viewer {
    height: 620px;
    min-height: 620px;
  }

  .d3-organ-intel-panel {
    min-height: 620px;
  }
}

@media (max-width: 1180px) {
  .desktop-sidebar-panel,
  .desktop-sidebar-backdrop {
    display: none;
  }

  .dashboard-shell,
  .dashboard-hero-grid,
  .brain-map-layout,
  .relation-graph-layout,
  .reviews-layout,
  .settings-grid,
  .research-layout-row,
  .research-grid,
  .impact-validate-layout,
  .bio-library-layout,
  .molecular-studio-layout,
  .bio-studio-hero {
    grid-template-columns: 1fr;
  }

  .dashboard-sidebar {
    order: 2;
  }

  .dashboard-main {
    order: 1;
  }

  .patients-grid,
  .metric-grid,
  .sessions-grid,
  .page-stats-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .research-right,
  .patient-context-pro,
  .sticky-panel {
    position: static;
  }

  .research-warning {
    min-width: 0;
  }

  .brain-canvas-card {
    min-height: 560px;
  }

  .brain-canvas-wrap,
  .relation-graph-canvas-wrap,
  .relation-graph-svg {
    min-height: 500px;
    height: 500px;
  }
}

@media (max-width: 900px) {
  .app-layout-shell,
  .research-page,
  .admin-page {
    padding: 12px;
  }

  .topbar,
  .page-header,
  .section-head,
  .admin-page-header,
  .research-hero,
  .research-card-head,
  .organ-impact-table-head,
  .voice-chat-header,
  .molecular-studio-header,
  .compound-designer-head,
  .avatar-test-queue-strip {
    flex-direction: column;
    align-items: stretch;
  }

  .topbar-left,
  .topbar-right {
    width: 100%;
    flex-wrap: wrap;
  }

  .topbar-right .btn,
  .theme-toggle {
    flex: 1 1;
    min-width: 120px;
  }

  .research-hero h1,
  .admin-page-header h1 {
    font-size: 23px;
  }

  .summary-grid,
  .compact-summary-grid,
  .analysis-grid,
  .question-grid,
  .two-col,
  .session-top-grid,
  .impact-pro-grid,
  .impact-grid,
  .admin-stats-grid,
  .admin-filter-card,
  .admin-details-grid,
  .admin-form-grid,
  .biometric-row {
    grid-template-columns: 1fr;
  }

  .admin-form-grid textarea,
  .impact-field.wide,
  .impact-grid textarea {
    grid-column: span 1;
  }

  .patients-grid,
  .metric-grid,
  .sessions-grid,
  .page-stats-grid,
  .chemical-grid,
  .bio-card-grid,
  .history-grid-pro,
  .queue-grid {
    grid-template-columns: 1fr;
  }

  .chemical-search-bar,
  .assist-search-row {
    grid-template-columns: 1fr;
  }

  .bio-library-search,
  .voice-input-row,
  .impact-action-row,
  .selected-drugs-actions {
    flex-direction: column;
    display: flex;
  }

  .voice-input-row textarea {
    width: 100%;
  }

  .voice-bubble {
    max-width: 92%;
  }

  .session-tabs,
  .research-tabs,
  .bio-library-tabs,
  .bio-workbench-mode-tabs,
  .impact-step-tabs {
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
  }

  .session-tab,
  .research-tabs button,
  .bio-library-tabs button,
  .bio-workbench-mode-tabs button,
  .impact-step-tabs button {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .hra-body-viewer {
    min-height: 520px;
  }

  .hra-result-layout > .hra-body-viewer {
    height: 520px;
    min-height: 520px;
  }

  .studio-graph-shell {
    min-height: 520px;
  }

  .studio-molecule-svg {
    min-height: 460px;
  }

  .studio-picker-backdrop {
    align-items: flex-start;
    padding: 10px;
  }

  .studio-picker-modal {
    max-height: 94vh;
    border-radius: var(--radius-lg);
  }

  .studio-picker-bond-row {
    flex-wrap: wrap;
  }
}

@media (max-width: 600px) {
  body {
    font-size: var(--fs-md);
  }

  .topbar {
    padding: 10px 12px;
  }

  .topbar-title {
    font-size: 20px;
  }

  .brand-mark {
    width: 38px;
    height: 38px;
  }

  .user-chip {
    width: 100%;
    justify-content: flex-start;
  }

  .card,
  .research-card,
  .admin-modal,
  .wizard-modal,
  .pdf-modal {
    padding: 12px;
    border-radius: var(--radius-md);
  }

  .research-page,
  .admin-page,
  .app-layout-shell {
    padding: 10px;
  }

  .research-hero {
    padding: 14px;
    border-radius: var(--radius-lg);
  }

  .research-mini-stats span {
    font-size: 10px;
  }

  .admin-table,
  .organ-impact-table,
  .impact-table {
    min-width: 760px;
  }

  .admin-table th,
  .organ-impact-table th,
  .impact-table th,
  .admin-table td,
  .organ-impact-table td,
  .impact-table td {
    padding: 6px 7px;
    font-size: 10px;
  }

  .brain-canvas-wrap,
  .relation-graph-canvas-wrap,
  .relation-graph-svg {
    min-height: 420px;
    height: 420px;
  }

  .hra-body-viewer,
  .hra-result-layout > .hra-body-viewer {
    height: 460px;
    min-height: 460px;
  }

  .voice-chat-window {
    height: 320px;
  }
}


/* ==========================================================================
   STRUCTURE REPAIR PATCH v3
   --------------------------------------------------------------------------
   This section intentionally sits at the bottom so it wins over earlier rules.
   It restores page grids/cards that were lost during the theme rewrite while
   keeping the compact enterprise font/button/table look.
   ========================================================================== */

/* ---------- Page width safety ---------- */
.app-layout-shell,
.dashboard-shell,
.session-page,
.research-page,
.admin-page,
.page-content,
.dashboard-page,
.brain-page-shell {
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
}

.app-layout-main,
.dashboard-main,
.full-width-main,
.research-left,
.research-right {
  width: 100%;
  min-width: 0;
}

/* ---------- Topbar structure ---------- */
.topbar {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  width: 100%;
  gap: 16px;
}

.topbar-left {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  flex: 1 1 auto;
  min-width: 0;
}

.topbar-right {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  flex: 0 0 auto;
  min-width: max-content;
}

.topbar-title,
.topbar-subtitle {
  white-space: nowrap;
}

.theme-toggle {
  width: auto !important;
}

/* ---------- Generic page header ---------- */
.page-header {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 18px;
  margin: 18px 0 18px;
}

.page-header-copy {
  min-width: 0;
  flex: 1 1 auto;
}

.page-title {
  margin: 0 0 8px;
  font-size: clamp(28px, 2.4vw, 38px);
  line-height: 1.08;
  font-weight: 900;
  letter-spacing: -0.045em;
  color: var(--heading);
}

.page-subtitle {
  margin: 0;
  max-width: 860px;
  color: var(--text-soft);
}

.page-header-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex: 0 0 auto;
}

/* ---------- Restore dashboard layout ---------- */
.dashboard-shell {
  display: grid !important;
  grid-template-columns: 300px minmax(0, 1fr) !important;
  gap: 18px;
  align-items: start;
  padding-top: 18px;
}

.dashboard-shell.single-page-shell,
.single-page-shell {
  grid-template-columns: minmax(0, 1fr) !important;
}

.dashboard-sidebar {
  display: flex !important;
  flex-direction: column;
  gap: 16px;
}

.dashboard-main {
  display: block;
}

.dashboard-hero-grid {
  display: grid !important;
  grid-template-columns: minmax(0, 1.4fr) minmax(320px, 0.7fr);
  gap: 16px;
  align-items: stretch;
  margin-bottom: 18px;
}

.hero-panel,
.ai-side-panel {
  min-width: 0;
}

.ai-side-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.stats-stack,
.sidebar-actions,
.status-stack {
  display: grid;
  gap: 10px;
}

/* ---------- Restore stats/metric grids ---------- */
.page-stats-grid,
.metric-grid,
.metric-grid-compact {
  display: grid !important;
  gap: 14px !important;
  align-items: stretch;
}

.page-stats-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
  margin: 18px 0 18px;
}

.metric-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

.metric-grid-compact {
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

.metric-card,
.stat-tile,
.status-item,
.summary-item {
  min-height: 92px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding: 18px;
  border-radius: 12px;
}

.metric-card-primary,
.metric-card-blue,
.metric-card-pink,
.metric-card-green {
  background: var(--primary-800) !important;
  color: white;
}

/* ---------- Restore filter/toolbars ---------- */
.page-toolbar {
  display: flex !important;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 16px;
  flex-wrap: wrap;
}

.page-toolbar-filters {
  display: grid !important;
  grid-template-columns: minmax(260px, 1fr) 220px 220px 170px;
  gap: 10px;
  flex: 1 1 auto;
}

.page-toolbar-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.section-tools {
  display: flex !important;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.filter-input,
.filter-select {
  width: auto;
  min-width: 180px;
}

/* ---------- Restore patient cards ---------- */
.patients-grid,
.patients-page-grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
  gap: 16px !important;
  width: 100%;
  align-items: stretch;
}

.patient-card,
.session-card {
  width: 100%;
  height: 100%;
  text-align: left;
  border: 1px solid var(--border);
  background: var(--surface);
  border-radius: 14px;
  box-shadow: var(--shadow-sm);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.patient-card {
  display: flex !important;
  flex-direction: column;
  gap: 14px;
  min-height: 172px;
  padding: 16px;
}

.patient-card:hover,
.session-card:hover {
  border-color: var(--primary-600);
  box-shadow: var(--shadow-md);
  transform: translateY(-1px);
}

.patient-card.selected {
  border-color: var(--primary-800);
  box-shadow: 0 0 0 3px var(--primary-soft), var(--shadow-sm);
}

.patient-card-top {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 14px;
}

.patient-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.patient-meta h3 {
  margin: 0 0 6px;
  font-size: 18px;
  line-height: 1.2;
  font-weight: 850;
  color: var(--heading);
  word-break: break-word;
}

.patient-meta p,
.session-text {
  margin: 0;
  color: var(--text-soft);
  font-size: 13px;
}

.patient-avatar {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  color: var(--primary-900);
  background: var(--primary-soft);
  flex: 0 0 auto;
}

.patient-card-bottom {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 10px;
  margin-top: auto;
  flex-wrap: wrap;
}

.patient-card-bottom .btn,
.patient-card .btn {
  min-height: 34px;
  padding: 7px 11px;
}

.patient-actions,
.patient-card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
}

/* Some patient page cards may render without wrapper class; normalize children */
.patients-grid > *,
.patients-page-grid > * {
  min-width: 0;
}

/* ---------- Restore sessions cards ---------- */
.sessions-grid,
.sessions-page-grid {
  display: grid !important;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)) !important;
  gap: 16px !important;
  width: 100%;
  align-items: stretch;
}

.session-card-button {
  display: block;
  width: 100%;
  padding: 0;
  background: transparent;
  text-align: left;
}

.session-card {
  display: flex;
  flex-direction: column;
  min-height: 170px;
  padding: 16px;
}

.session-card-head,
.session-card-head-top {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
  gap: 12px;
}

.session-card h3,
.session-card h2 {
  margin: 0 0 6px;
  font-size: 18px;
  color: var(--heading);
}

.session-badge-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: flex-end;
  flex: 0 0 auto;
}

.session-summary-clamp {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-top: 10px;
  color: var(--text-soft);
}

/* ---------- Dashboard patient/session filter areas ---------- */
.sessions-head,
.patients-head {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-end !important;
  justify-content: space-between !important;
  gap: 14px;
  margin: 20px 0 12px;
}

.sessions-head > div:first-child,
.patients-head > div:first-child {
  flex: 1 1 auto;
}

/* ---------- Settings page structure ---------- */
.settings-grid {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 12px;
}

.settings-section-card {
  padding: 18px;
}

.settings-actions,
.settings-actions-stack {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ---------- Relation graph structure ---------- */
.relation-graph-layout {
  display: grid !important;
  grid-template-columns: minmax(0, 1.9fr) minmax(360px, 0.9fr) !important;
  gap: 16px;
  align-items: stretch;
}

.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card {
  padding: 18px;
}

.relation-graph-side {
  display: grid;
  gap: 16px;
}

.relation-graph-canvas-wrap {
  height: 620px;
  min-height: 620px;
}

.relation-graph-svg {
  height: 620px;
}

/* ---------- Brain page structure ---------- */
.brain-page-shell {
  display: grid;
  gap: 16px;
}

.brain-map-layout {
  display: grid !important;
  grid-template-columns: minmax(0, 1.55fr) minmax(360px, 0.55fr) !important;
  gap: 16px;
  align-items: stretch;
}

.brain-canvas-card,
.brain-side-card,
.brain-selected-panel {
  padding: 18px;
}

.brain-canvas-card {
  min-height: 760px;
}

.brain-canvas-wrap {
  height: 660px;
  min-height: 660px;
}

.brain-side-stack {
  display: grid;
  gap: 16px;
}

/* ---------- Research page structure ---------- */
.research-page {
  padding: 18px 24px 32px;
}

.research-hero {
  display: flex !important;
  flex-direction: row !important;
  align-items: stretch !important;
  justify-content: space-between !important;
}

.research-grid,
.research-layout-row {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 350px !important;
  gap: 16px !important;
  align-items: start;
}

.research-left {
  display: flex !important;
  flex-direction: column;
  gap: 16px;
}

.research-card.wide,
.result-card {
  grid-column: 1 / -1;
}

.research-tabs {
  display: flex !important;
  flex-wrap: wrap !important;
}

.bio-studio-hero {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 210px !important;
}

.molecular-studio-layout {
  display: grid !important;
  grid-template-columns: 280px minmax(0, 1fr) 240px !important;
  gap: 12px;
  align-items: stretch;
}

.avatar-test-queue-strip {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
}

.studio-graph-shell {
  min-height: 640px;
}

.studio-molecule-svg {
  min-height: 560px;
}

/* ---------- Admin layout structure ---------- */
.admin-page {
  padding: 18px 24px 32px;
}

.admin-page-header {
  display: flex !important;
  flex-direction: row !important;
  align-items: flex-start !important;
  justify-content: space-between !important;
}

.admin-stats-grid {
  display: grid !important;
  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
}

.admin-filter-card {
  display: grid !important;
  grid-template-columns: minmax(260px, 1fr) 220px 220px 130px !important;
  align-items: center;
}

.admin-table-card {
  width: 100%;
}

.admin-table {
  min-width: 1120px;
}

.admin-actions {
  display: flex !important;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
}

/* ---------- Cards that should not become weird full-width text blocks ---------- */
.empty-card,
.empty-state-box,
.analysis-block,
.analysis-summary-card,
.analysis-doctor-note {
  width: 100%;
}

/* ---------- Session page specific ---------- */
.session-page {
  display: grid;
  gap: 16px;
  padding: 18px 24px 32px;
}

.session-top-grid {
  display: grid !important;
  grid-template-columns: minmax(0, 1.2fr) minmax(360px, 0.8fr) !important;
  gap: 16px;
}

.session-tabs {
  display: flex !important;
  flex-wrap: wrap !important;
}

.summary-grid,
.compact-summary-grid,
.analysis-grid {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 12px;
}

/* ---------- Form layout restore ---------- */
.question-grid,
.two-col {
  display: grid !important;
  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  gap: 12px;
}

.full-span {
  grid-column: 1 / -1;
}

/* ---------- Tables keep proper width but don't explode page ---------- */
.admin-table-card,
.organ-impact-table-wrap,
.impact-table-wrap {
  max-width: 100%;
  overflow-x: auto;
}

/* ---------- Improve visual density without breaking layout ---------- */
.card,
.research-card,
.sidebar-card,
.settings-section-card,
.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card,
.brain-canvas-card,
.brain-side-card,
.brain-selected-panel,
.admin-stat-card,
.admin-table-card {
  border-radius: 14px;
}

/* ---------- Responsive: tablet ---------- */
@media (max-width: 1280px) {
  .dashboard-hero-grid,
  .relation-graph-layout,
  .brain-map-layout,
  .research-grid,
  .research-layout-row,
  .molecular-studio-layout {
    grid-template-columns: 1fr !important;
  }

  .research-right,
  .patient-context-pro,
  .sticky-panel {
    position: static !important;
  }

  .relation-graph-canvas-wrap,
  .relation-graph-svg,
  .brain-canvas-wrap {
    height: 560px;
    min-height: 560px;
  }
}

/* ---------- Responsive: small laptop / tablet ---------- */
@media (max-width: 1024px) {
  .dashboard-shell {
    grid-template-columns: 1fr !important;
  }

  .dashboard-sidebar {
    order: 2;
  }

  .dashboard-main {
    order: 1;
  }

  .page-stats-grid,
  .metric-grid,
  .metric-grid-compact,
  .admin-stats-grid,
  .settings-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .page-toolbar-filters,
  .admin-filter-card {
    grid-template-columns: 1fr 1fr !important;
  }

  .research-hero,
  .admin-page-header,
  .page-header {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .page-header-actions {
    justify-content: flex-start;
  }

  .bio-studio-hero {
    grid-template-columns: 1fr !important;
  }
}

/* ---------- Responsive: mobile ---------- */
@media (max-width: 760px) {
  .topbar {
    align-items: flex-start !important;
    gap: 12px;
  }

  .topbar-left {
    width: 100%;
  }

  .topbar-right {
    width: 100%;
    justify-content: flex-start !important;
    flex-wrap: wrap;
    min-width: 0;
  }

  .topbar-title,
  .topbar-subtitle {
    white-space: normal;
  }

  .theme-toggle,
  .topbar-right .btn,
  .user-chip {
    width: 100% !important;
  }

  .app-layout-shell,
  .research-page,
  .admin-page,
  .session-page {
    padding: 12px !important;
  }

  .page-stats-grid,
  .metric-grid,
  .metric-grid-compact,
  .admin-stats-grid,
  .settings-grid,
  .summary-grid,
  .compact-summary-grid,
  .analysis-grid,
  .question-grid,
  .two-col,
  .session-top-grid,
  .page-toolbar-filters,
  .admin-filter-card {
    grid-template-columns: 1fr !important;
  }

  .patients-grid,
  .patients-page-grid,
  .sessions-grid,
  .sessions-page-grid {
    grid-template-columns: 1fr !important;
  }

  .sessions-head,
  .patients-head,
  .patient-card-top,
  .patient-card-bottom,
  .session-card-head,
  .session-card-head-top,
  .avatar-test-queue-strip {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .patient-actions,
  .patient-card-actions,
  .session-badge-stack {
    align-items: flex-start;
    margin-left: 0;
  }

  .research-tabs,
  .session-tabs,
  .bio-library-tabs,
  .bio-workbench-mode-tabs,
  .impact-step-tabs {
    overflow-x: auto;
    flex-wrap: nowrap !important;
    padding-bottom: 4px;
  }

  .research-tabs button,
  .session-tab,
  .bio-library-tabs button,
  .bio-workbench-mode-tabs button,
  .impact-step-tabs button {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .relation-graph-canvas-wrap,
  .relation-graph-svg,
  .brain-canvas-wrap {
    height: 430px;
    min-height: 430px;
  }

  .brain-canvas-card {
    min-height: 500px;
  }

  .admin-table {
    min-width: 980px;
  }
}



/* ==========================================================================
   VERSION 4 — TYPOGRAPHY + SIMULATION THEME POLISH
   --------------------------------------------------------------------------
   Keep this block at the very bottom. It refines:
   - More professional enterprise font scale
   - Cleaner clinical/research typography
   - Theme-matched simulation/HRA/D3/brain dark panels
   - Less "AI generated" rounded/purple look
   - Better modal and button proportions
   ========================================================================== */

:root {
  /* v4 brand tuning: mature teal + clinical slate */
  --primary: #0f6b63;
  --primary-500: #147d74;
  --primary-600: #0f766e;
  --primary-700: #0d625b;
  --primary-800: #0b5a52;
  --primary-900: #083f3a;
  --primary-soft: rgba(15, 107, 99, 0.10);
  --primary-softer: rgba(15, 107, 99, 0.055);

  --ink-950: #07111f;
  --ink-900: #101827;
  --ink-800: #182335;
  --ink-700: #26354a;

  --bg: #eef3f6;
  --bg-page: #f5f8fa;
  --surface: #ffffff;
  --surface-strong: #ffffff;
  --surface-soft: #f7fafc;
  --surface-muted: #edf3f7;
  --surface-dark: #0b1320;
  --surface-dark-2: #111d2d;

  --heading: #111827;
  --text: #253245;
  --text-soft: #66758b;
  --text-muted: #8a98aa;

  --border: #d7e0ea;
  --border-strong: #b9c7d6;

  --shadow-xs: 0 1px 2px rgba(16, 24, 39, 0.05);
  --shadow-sm: 0 6px 18px rgba(16, 24, 39, 0.06);
  --shadow-md: 0 14px 34px rgba(16, 24, 39, 0.10);
  --shadow-lg: 0 24px 60px rgba(16, 24, 39, 0.16);

  --radius-xs: 5px;
  --radius-sm: 7px;
  --radius-md: 10px;
  --radius-lg: 13px;
  --radius-xl: 16px;
  --radius-2xl: 20px;

  --font-main:
    ui-sans-serif,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    "Noto Sans",
    "Noto Sans Devanagari",
    sans-serif;

  --font-display:
    ui-sans-serif,
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    "Helvetica Neue",
    Arial,
    sans-serif;

  --font-mono:
    ui-monospace,
    SFMono-Regular,
    Menlo,
    Monaco,
    Consolas,
    "Liberation Mono",
    monospace;

  --fs-xs: 10.5px;
  --fs-sm: 11.5px;
  --fs-md: 12.5px;
  --fs-base: 13px;
  --fs-lg: 15px;
  --fs-xl: 18px;
  --fs-2xl: 24px;
  --fs-3xl: 31px;

  --table-header-bg: var(--primary-800);
  --table-header-text: #f8fffd;
  --table-row-hover: #eef8f6;
  --grid-line: rgba(98, 116, 142, 0.14);

  /* simulation-specific variables */
  --sim-bg: #07101d;
  --sim-bg-2: #0b1628;
  --sim-panel: #0f1b2d;
  --sim-panel-soft: #152238;
  --sim-line: rgba(148, 163, 184, 0.16);
  --sim-primary: #2dd4bf;
  --sim-primary-soft: rgba(45, 212, 191, 0.16);
  --sim-text: #eaf2ff;
  --sim-muted: #9caec3;

  --risk-low: #16a34a;
  --risk-moderate: #d97706;
  --risk-high: #dc2626;
  --risk-critical: #991b1b;
}

/* ---------- Base text rendering ---------- */
html {
  text-rendering: geometricPrecision;
}

body {
  font-family: var(--font-main) !important;
  font-size: var(--fs-base);
  letter-spacing: -0.006em;
  color: var(--text);
  background:
    radial-gradient(circle at 4% 0%, rgba(15, 107, 99, 0.075), transparent 24%),
    radial-gradient(circle at 90% 8%, rgba(8, 63, 58, 0.04), transparent 26%),
    linear-gradient(180deg, #fbfdfe 0%, var(--bg-page) 100%) !important;
}

h1, h2, h3, h4, h5, h6,
.topbar-title,
.page-title,
.research-hero h1,
.bio-studio-hero h2,
.compound-designer-head h2,
.molecular-studio-header h2 {
  font-family: var(--font-display) !important;
  color: var(--heading);
  letter-spacing: -0.04em;
  text-wrap: balance;
}

h1 {
  font-size: clamp(28px, 2.8vw, 38px);
  line-height: 1.04;
  font-weight: 850;
}

h2 {
  font-size: clamp(20px, 2vw, 26px);
  line-height: 1.12;
  font-weight: 820;
}

h3 {
  font-size: 17px;
  line-height: 1.18;
  font-weight: 780;
}

p,
small,
.subtitle,
.muted,
.page-subtitle,
.topbar-subtitle {
  letter-spacing: -0.01em;
}

p {
  line-height: 1.55;
}

strong {
  font-weight: 760;
}

pre,
code,
.medgen-stream-box pre,
.medgen-final-box pre,
.analysis-stream-box pre {
  font-family: var(--font-mono) !important;
}

/* ---------- More sober density ---------- */
.app-layout-shell {
  padding: 10px 24px 28px !important;
}

.topbar {
  min-height: 62px !important;
  padding: 8px 12px !important;
}

.brand-mark {
  width: 36px !important;
  height: 36px !important;
  border-radius: 9px !important;
  font-size: 11px !important;
  letter-spacing: 0.02em;
  box-shadow: 0 7px 18px rgba(15, 107, 99, 0.18);
}

.topbar-title {
  font-size: 20px !important;
  font-weight: 850 !important;
  line-height: 1.05;
}

.topbar-subtitle {
  font-size: 11px !important;
  margin-top: 2px !important;
  color: var(--text-soft);
}

.hamburger-btn {
  width: 34px !important;
  height: 34px !important;
  border-radius: 8px !important;
  box-shadow: var(--shadow-xs);
}

.user-chip {
  min-height: 36px !important;
  padding: 5px 10px !important;
  border-radius: 18px !important;
}

.user-avatar {
  width: 26px !important;
  height: 26px !important;
  font-size: 10px !important;
}

.user-name {
  font-size: 11.5px !important;
  font-weight: 760 !important;
}

.user-role {
  font-size: 9.5px !important;
}

/* ---------- Buttons: less toy-like, more console/admin ---------- */
.btn,
button,
.research-primary,
.impact-next-btn,
.impact-secondary-btn,
.ask-llm-btn,
.admin-primary-btn,
.admin-filter-card button,
.admin-pagination button,
.admin-modal-actions button,
.admin-actions button,
.chemical-search-bar button,
.chemical-pagination button,
.bio-library-search button,
.selected-drugs-open-btn,
.face-actions button,
.avatar-test-actions button,
.molecular-studio-actions button,
.studio-preview-actions button,
.studio-library-open-btn {
  border-radius: 7px !important;
  min-height: 34px;
  padding: 8px 13px;
  font-size: 12px;
  font-weight: 720;
  letter-spacing: -0.01em;
  box-shadow: none;
}

.btn-primary,
.research-primary,
.impact-next-btn,
.admin-primary-btn,
.chemical-search-bar button,
.chemical-pagination button,
.bio-library-search button,
.avatar-test-actions button.primary,
.molecular-studio-actions button.primary,
.studio-preview-actions button.primary {
  background: var(--primary-800) !important;
  color: white !important;
}

.btn-primary:hover,
.research-primary:hover,
.impact-next-btn:hover,
.admin-primary-btn:hover {
  background: var(--primary-900) !important;
}

.btn-secondary,
.impact-secondary-btn,
.btn-ghost,
.theme-toggle {
  background: #f8fafc !important;
  border: 1px solid var(--border) !important;
  color: var(--heading) !important;
}

.ask-llm-btn,
.admin-filter-card button,
.chemical-search-bar button.dark,
.btn-dark {
  background: var(--ink-900) !important;
  color: white !important;
}

/* ---------- Inputs ---------- */
input,
select,
textarea,
.research-input,
.filter-input,
.filter-select {
  min-height: 36px !important;
  border-radius: 7px !important;
  padding: 8px 11px !important;
  font-size: 12.5px !important;
  font-weight: 520 !important;
  color: var(--text);
  background: #ffffff !important;
}

textarea {
  line-height: 1.5;
}

label,
.research-label,
.impact-field label,
.dose-row label,
.form-field label,
.field-block label {
  font-size: 11px !important;
  font-weight: 760 !important;
  letter-spacing: 0.015em;
  color: #44546a !important;
}

/* ---------- Cards and surfaces ---------- */
.card,
.research-card,
.sidebar-card,
.settings-section-card,
.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card,
.brain-canvas-card,
.brain-side-card,
.brain-selected-panel,
.admin-stat-card,
.admin-table-card,
.voice-chat-card,
.patient-card,
.session-card,
.wizard-modal,
.admin-modal,
.pdf-modal,
.selected-drugs-card,
.studio-picker-modal {
  border-radius: 12px !important;
  border-color: var(--border) !important;
  box-shadow: var(--shadow-sm) !important;
}

.research-card,
.settings-section-card,
.admin-stat-card,
.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card,
.brain-canvas-card,
.brain-side-card,
.brain-selected-panel {
  background: rgba(255, 255, 255, 0.92) !important;
}

/* ---------- Hero/header polish ---------- */
.research-hero {
  padding: 18px 22px !important;
  border-radius: 14px !important;
}

.research-hero h1 {
  font-size: clamp(24px, 2.2vw, 32px) !important;
  font-weight: 850 !important;
}

.research-hero p {
  font-size: 13px !important;
  color: var(--text-soft);
}

.research-warning {
  background: linear-gradient(135deg, var(--ink-900), #07101d) !important;
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 12px !important;
}

.research-kicker,
.eyebrow,
.bio-studio-kicker,
.compound-designer-head p,
.molecular-studio-header p,
.studio-picker-head p {
  color: var(--primary-800) !important;
  font-size: 10px !important;
  font-weight: 820 !important;
  letter-spacing: 0.14em !important;
}

/* ---------- Pills/tabs: theme matching ---------- */
.research-mini-stats span,
.pill,
.saved-pill,
.selected-patient-chip,
.bmi-chip {
  background: var(--primary-softer) !important;
  border-color: rgba(15, 107, 99, 0.14) !important;
  color: var(--primary-900) !important;
  font-size: 10px !important;
  font-weight: 780 !important;
}

.research-tabs button,
.session-tab,
.wizard-step,
.bio-library-tabs button,
.bio-workbench-mode-tabs button,
.impact-step-tabs button,
.chemical-az-filter button {
  border-radius: 7px !important;
  background: #eef3f7 !important;
  border-color: #dbe4ee !important;
  color: #213047 !important;
  font-size: 11.5px !important;
  font-weight: 760 !important;
  min-height: 32px !important;
}

.research-tabs button.active,
.session-tab.active,
.wizard-step.active,
.bio-library-tabs button.active,
.bio-workbench-mode-tabs button.active,
.impact-step-tabs button.active,
.chemical-az-filter button.active {
  background: var(--primary-800) !important;
  border-color: var(--primary-800) !important;
  color: #ffffff !important;
}

/* ---------- Table polish ---------- */
.admin-table th,
.organ-impact-table th,
.impact-table th {
  height: 32px !important;
  background: linear-gradient(180deg, var(--primary-800), var(--primary-900)) !important;
  font-size: 10.5px !important;
  font-weight: 760 !important;
  letter-spacing: 0.015em !important;
}

.admin-table td,
.organ-impact-table td,
.impact-table td {
  font-size: 11.5px !important;
  color: #27364b !important;
  padding: 8px 9px !important;
}

.admin-table tbody tr:hover td,
.organ-impact-table tbody tr:hover td,
.impact-table tbody tr:hover td {
  background: #eef8f6 !important;
}

/* ---------- Dark panels must match theme, not purple ---------- */
.dark-card,
.ai-side-panel,
.medgen-side-panel,
.studio-medgen-console,
.avatar-test-queue-strip,
.research-warning {
  background:
    radial-gradient(circle at 18% 0%, rgba(20, 184, 166, 0.10), transparent 30%),
    linear-gradient(145deg, var(--surface-dark), var(--surface-dark-2)) !important;
  border: 1px solid rgba(148, 163, 184, 0.16) !important;
  color: var(--sim-text) !important;
}

.dark-card h1,
.dark-card h2,
.dark-card h3,
.dark-card h4,
.dark-card strong,
.ai-side-panel h1,
.ai-side-panel h2,
.ai-side-panel h3,
.ai-side-panel strong,
.medgen-side-panel h3,
.studio-medgen-console strong {
  /* color: var(--sim-text) !important; */
  color: #020617 !important;
}

.dark-card p,
.dark-card small,
.ai-side-panel p,
.ai-side-panel small,
.medgen-side-panel p,
.studio-medgen-console span {
  color: var(--sim-muted) !important;
}

/* ---------- Simulation / HRA / D3 color alignment ---------- */
.hra-body-viewer,
.brain-canvas-wrap,
.relation-graph-canvas-wrap,
.avatar-3d-container,
.studio-graph-shell {
  border: 1px solid rgba(15, 107, 99, 0.24) !important;
  background:
    linear-gradient(90deg, rgba(148, 163, 184, 0.105) 1px, transparent 1px),
    linear-gradient(rgba(148, 163, 184, 0.105) 1px, transparent 1px),
    radial-gradient(circle at 50% 42%, rgba(20, 184, 166, 0.14), transparent 34%),
    radial-gradient(circle at 80% 12%, rgba(15, 107, 99, 0.12), transparent 26%),
    linear-gradient(180deg, var(--sim-bg-2), var(--sim-bg)) !important;
  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%, 100% 100% !important;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.025),
    0 18px 46px rgba(7, 16, 29, 0.18) !important;
}

.hra-result-layout .d3-organ-intel-panel,
.d3-organ-intel-panel,
.brain-canvas-card {
  background:
    radial-gradient(circle at 18% 0%, rgba(20, 184, 166, 0.10), transparent 32%),
    linear-gradient(145deg, #0b1320, #0e1a2b) !important;
  color: var(--sim-text) !important;
  border: 1px solid rgba(148, 163, 184, 0.18) !important;
}

.d3-organ-intel-panel h2,
.d3-organ-intel-panel h3,
.d3-organ-intel-panel strong,
.brain-canvas-card h2,
.brain-canvas-card h3 {
  color: var(--sim-text) !important;
}

.d3-organ-intel-panel p,
.d3-organ-intel-panel span,
.d3-organ-intel-panel small,
.brain-canvas-card p,
.brain-canvas-card small {
  color: var(--sim-muted);
}

.score-meter div,
.organ-bar div {
  background: linear-gradient(90deg, var(--primary-700), var(--sim-primary)) !important;
}

.brain-view-toggle button.active,
.d3-organ-intel-panel button.active,
.hra-result-layout button.active {
  background: var(--primary-800) !important;
  border-color: var(--primary-700) !important;
}

.hra-face-mask-label,
.hra-viewer-chip {
  border-color: rgba(45, 212, 191, 0.35) !important;
  background: rgba(7, 16, 29, 0.82) !important;
  color: #ccfbf1 !important;
}

/* Generic SVG graph colors where CSS can reach */
.relation-graph-svg text,
.d3-organ-intel-panel svg text,
.studio-molecule-svg text {
  font-family: var(--font-main) !important;
  letter-spacing: -0.01em;
}

.d3-organ-intel-panel svg .node rect,
.d3-organ-intel-panel svg rect {
  rx: 6px;
}

.d3-organ-intel-panel svg line,
.d3-organ-intel-panel svg path,
.relation-graph-svg path,
.relation-graph-svg line {
  stroke-opacity: 0.72;
}

/* ---------- Simulation history cards ---------- */
.history-grid-pro,
.queue-grid,
.chemical-grid,
.bio-card-grid {
  gap: 12px !important;
}

.history-tile,
.queue-card,
.chemical-card,
.bio-select-card,
.bio-library-item {
  border-radius: 8px !important;
  border-color: #dbe4ee !important;
  background: #ffffff !important;
  box-shadow: none !important;
}

.history-tile.active,
.queue-card.active,
.chemical-card.selected,
.bio-select-card.selected,
.bio-library-item.active {
  border-color: var(--primary-700) !important;
  box-shadow: 0 0 0 2px rgba(15, 107, 99, 0.08) !important;
  background: #f4fbfa !important;
}

.history-tile strong,
.queue-card strong,
.chemical-card-top strong,
.bio-select-card strong,
.bio-library-item strong {
  font-size: 12.5px !important;
  font-weight: 780 !important;
  letter-spacing: -0.02em;
}

/* ---------- Modal / wizard polish ---------- */
.admin-modal-backdrop,
.modal-backdrop,
.pdf-backdrop,
.selected-drugs-modal,
.studio-picker-backdrop {
  background: rgba(17, 24, 39, 0.46) !important;
  backdrop-filter: blur(7px) saturate(0.9) !important;
}

.wizard-modal,
.admin-modal,
.selected-drugs-card,
.studio-picker-modal {
  border-radius: 14px !important;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.26) !important;
}

.wizard-modal h2,
.admin-modal h2 {
  font-size: 22px !important;
  font-weight: 820 !important;
}

.wizard-step {
  min-width: 126px !important;
  border-radius: 8px !important;
}

.wizard-step.active {
  background: var(--primary-800) !important;
}

.wizard-step span {
  width: 22px !important;
  height: 22px !important;
  font-size: 10px;
}

/* ---------- Risk colors ---------- */
.risk-pill.low,
.organ-risk-chip.low,
.pill.low {
  background: rgba(22, 163, 74, 0.12) !important;
  color: #166534 !important;
}

.risk-pill.moderate,
.organ-risk-chip.moderate,
.pill.moderate {
  background: rgba(217, 119, 6, 0.13) !important;
  color: #92400e !important;
}

.risk-pill.high,
.organ-risk-chip.high,
.pill.high {
  background: rgba(220, 38, 38, 0.12) !important;
  color: #991b1b !important;
}

.risk-pill.critical,
.organ-risk-chip.critical,
.pill.critical {
  background: rgba(153, 27, 27, 0.13) !important;
  color: #7f1d1d !important;
}

/* ---------- Patient/session card typography ---------- */
.patient-meta h3,
.session-card h3,
.session-card h2 {
  font-size: 17px !important;
  font-weight: 820 !important;
}

.patient-meta p,
.session-text,
.session-summary-clamp {
  font-size: 12.5px !important;
  color: var(--text-soft);
}

.patient-avatar {
  border-radius: 10px !important;
}

/* ---------- Research studio controls ---------- */
.studio-node-context-menu {
  background: rgba(11, 19, 32, 0.98) !important;
  border-color: rgba(45, 212, 191, 0.18) !important;
}

.studio-node-context-head {
  background: rgba(15, 107, 99, 0.22) !important;
  border-color: rgba(45, 212, 191, 0.22) !important;
}

.studio-node-context-menu button {
  background: rgba(148, 163, 184, 0.12) !important;
}

.studio-node-context-menu button:hover {
  background: rgba(45, 212, 191, 0.16) !important;
}

/* ---------- Dark mode v4 ---------- */
html[data-theme="dark"] {
  --bg: #07101d;
  --bg-page: #0b1320;
  --surface: #111d2d;
  --surface-strong: #111d2d;
  --surface-soft: #152238;
  --surface-muted: #1b2a40;
  --heading: #f3f7fb;
  --text: #d7e2ee;
  --text-soft: #9caec3;
  --text-muted: #7d8da3;
  --border: rgba(148, 163, 184, 0.20);
  --border-strong: rgba(203, 213, 225, 0.28);
}

html[data-theme="dark"] body {
  background:
    radial-gradient(circle at 8% 0%, rgba(20, 184, 166, 0.13), transparent 26%),
    linear-gradient(180deg, #07101d 0%, #0b1320 100%) !important;
}

html[data-theme="dark"] input,
html[data-theme="dark"] select,
html[data-theme="dark"] textarea {
  background: #0f1b2d !important;
  color: var(--text) !important;
  border-color: rgba(148, 163, 184, 0.22) !important;
}

html[data-theme="dark"] .btn-secondary,
html[data-theme="dark"] .impact-secondary-btn,
html[data-theme="dark"] .btn-ghost,
html[data-theme="dark"] .theme-toggle {
  background: #111d2d !important;
  color: #eaf2ff !important;
}

/* ---------- Responsive fine tuning ---------- */
@media (max-width: 760px) {
  .app-layout-shell {
    padding: 10px 12px 22px !important;
  }

  .topbar {
    padding: 8px 6px !important;
  }

  .topbar-title {
    font-size: 18px !important;
  }

  h1 {
    font-size: 25px !important;
  }

  h2 {
    font-size: 20px !important;
  }
}

/* ==========================================================================
   MEDGEN LOGO THEME + RESPONSIVE STABILITY PATCH v5
   --------------------------------------------------------------------------
   Place this block at the bottom of App.css, or use the generated complete
   App.medgen-logo-v5.css file as a full replacement.

   Brand direction based on the supplied MedGen logo:
   - Deep black/navy clinical background
   - Electric medical blue highlights
   - Clean white typography
   - Less green, less generic dashboard feel
   - Strong mobile/tablet responsiveness using existing class names
   ========================================================================== */

:root {
  /* MedGen logo palette */
  --mg-black: #03060c;
  --mg-navy-950: #06101f;
  --mg-navy-900: #081525;
  --mg-navy-850: #0b1b30;
  --mg-navy-800: #10243a;
  --mg-blue: #1f7bc1;
  --mg-blue-500: #2d8fe0;
  --mg-blue-400: #21a7f3;
  --mg-cyan: #18b7ff;
  --mg-white: #ffffff;
  --mg-slate: #6f8198;
  --mg-soft-blue: rgba(31, 123, 193, 0.12);
  --mg-glow: 0 0 0 1px rgba(24, 183, 255, 0.18), 0 18px 44px rgba(31, 123, 193, 0.14);

  /* Override previous green clinical palette */
  --primary: var(--mg-blue);
  --primary-500: var(--mg-blue-500);
  --primary-600: var(--mg-blue-500);
  --primary-700: #1769a9;
  --primary-800: #125a94;
  --primary-900: #0b3d66;
  --primary-soft: rgba(31, 123, 193, 0.12);
  --primary-softer: rgba(31, 123, 193, 0.065);

  --bg: #eef4fa;
  --bg-page: #f6f9fc;
  --surface: #ffffff;
  --surface-strong: #ffffff;
  --surface-soft: #f7fbff;
  --surface-muted: #edf4fb;
  --surface-dark: var(--mg-navy-950);
  --surface-dark-2: var(--mg-navy-900);

  --heading: #07111f;
  --text: #243449;
  --text-soft: #687a91;
  --text-muted: #91a0b2;
  --text-inverse: #ffffff;

  --border: #d6e1ee;
  --border-strong: #b5c6da;
  --grid-line: rgba(31, 123, 193, 0.11);

  --shadow-xs: 0 1px 2px rgba(7, 17, 31, 0.05);
  --shadow-sm: 0 8px 22px rgba(7, 17, 31, 0.07);
  --shadow-md: 0 16px 42px rgba(7, 17, 31, 0.11);
  --shadow-lg: 0 28px 70px rgba(7, 17, 31, 0.18);

  --table-header-bg: #0b3d66;
  --table-header-text: #ffffff;
  --table-row-hover: #eef7ff;

  --sim-bg: #03060c;
  --sim-bg-2: #06101f;
  --sim-panel: #081525;
  --sim-panel-soft: #10243a;
  --sim-line: rgba(24, 183, 255, 0.18);
  --sim-primary: var(--mg-cyan);
  --sim-primary-soft: rgba(24, 183, 255, 0.16);
  --sim-text: #f4f9ff;
  --sim-muted: #9bb0c8;
}

html[data-theme="dark"] {
  --primary: var(--mg-blue-500);
  --primary-500: var(--mg-blue-500);
  --primary-600: var(--mg-cyan);
  --primary-700: var(--mg-blue);
  --primary-800: #1769a9;
  --primary-900: #0b3d66;
  --primary-soft: rgba(24, 183, 255, 0.15);
  --primary-softer: rgba(24, 183, 255, 0.08);

  --bg: #03060c;
  --bg-page: #050b14;
  --surface: #081525;
  --surface-strong: #0b1b30;
  --surface-soft: #10243a;
  --surface-muted: #132b45;
  --surface-dark: #03060c;
  --surface-dark-2: #06101f;

  --heading: #f6fbff;
  --text: #dbe8f6;
  --text-soft: #9db0c7;
  --text-muted: #75869d;
  --border: rgba(141, 174, 211, 0.22);
  --border-strong: rgba(194, 218, 244, 0.30);
  --table-row-bg: #081525;
  --table-row-alt: #06101f;
  --table-row-hover: rgba(24, 183, 255, 0.10);
  --table-cell-border: rgba(141, 174, 211, 0.20);
}

body {
  background:
    radial-gradient(circle at 6% 0%, rgba(24, 183, 255, 0.11), transparent 25%),
    radial-gradient(circle at 88% 2%, rgba(31, 123, 193, 0.07), transparent 28%),
    linear-gradient(180deg, #fbfdff 0%, var(--bg-page) 100%) !important;
}

html[data-theme="dark"] body {
  background:
    radial-gradient(circle at 8% 0%, rgba(24, 183, 255, 0.18), transparent 26%),
    radial-gradient(circle at 92% 8%, rgba(31, 123, 193, 0.15), transparent 28%),
    linear-gradient(180deg, #03060c 0%, #050b14 100%) !important;
}

/* ---------- Brand/topbar ---------- */
.brand-mark {
  background:
    radial-gradient(circle at 82% 18%, rgba(255,255,255,0.96) 0 13%, transparent 14%),
    conic-gradient(from 210deg, transparent 0deg, transparent 40deg, rgba(24,183,255,0.95) 105deg, transparent 165deg),
    linear-gradient(135deg, var(--mg-blue), #0b4e84) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 26px rgba(31,123,193,0.28), 0 0 22px rgba(24,183,255,0.16) !important;
}

.topbar-title,
.page-title,
.research-hero h1,
.bio-studio-hero h2,
.compound-designer-head h2,
.molecular-studio-header h2 {
  letter-spacing: -0.055em !important;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 200;
  background: rgba(246, 249, 252, 0.76) !important;
  backdrop-filter: blur(16px) saturate(1.1);
  border-bottom: 1px solid rgba(214, 225, 238, 0.65);
}

html[data-theme="dark"] .topbar {
  background: rgba(3, 6, 12, 0.72) !important;
  border-bottom-color: rgba(141, 174, 211, 0.16);
}

/* ---------- Buttons and active UI ---------- */
.btn-primary,
.research-primary,
.impact-next-btn,
.admin-primary-btn,
.chemical-search-bar button,
.chemical-pagination button,
.bio-library-search button,
.avatar-test-actions button.primary,
.molecular-studio-actions button.primary,
.studio-preview-actions button.primary,
.research-tabs button.active,
.session-tab.active,
.wizard-step.active,
.bio-library-tabs button.active,
.bio-workbench-mode-tabs button.active,
.impact-step-tabs button.active,
.chemical-az-filter button.active,
.app-nav-link.active {
  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d) !important;
  border-color: rgba(24, 183, 255, 0.24) !important;
  color: #ffffff !important;
  box-shadow: 0 8px 22px rgba(31, 123, 193, 0.18) !important;
}

.btn-primary:hover,
.research-primary:hover,
.impact-next-btn:hover,
.admin-primary-btn:hover {
  background: linear-gradient(135deg, var(--mg-blue-500), var(--mg-blue)) !important;
  transform: translateY(-1px);
}

.ask-llm-btn,
.admin-filter-card button,
.btn-dark {
  background: linear-gradient(135deg, #06101f, #10243a) !important;
  color: #ffffff !important;
  border: 1px solid rgba(24, 183, 255, 0.22) !important;
}

input:focus,
select:focus,
textarea:focus,
.research-input:focus,
.filter-input:focus,
.filter-select:focus {
  border-color: var(--mg-blue-500) !important;
  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.13) !important;
}

/* ---------- Cards and panels ---------- */
.card,
.research-card,
.sidebar-card,
.settings-section-card,
.relation-graph-canvas-card,
.relation-detail-card,
.relation-legend-card,
.brain-side-card,
.brain-selected-panel,
.admin-stat-card,
.admin-table-card,
.voice-chat-card,
.patient-card,
.session-card,
.wizard-modal,
.admin-modal,
.pdf-modal,
.selected-drugs-card,
.studio-picker-modal,
.auth-card,
.brain-page-meta-card,
.brain-source-card {
  border-color: rgba(185, 204, 224, 0.85) !important;
  box-shadow: 0 10px 30px rgba(7, 17, 31, 0.065) !important;
}

html[data-theme="dark"] .card,
html[data-theme="dark"] .research-card,
html[data-theme="dark"] .sidebar-card,
html[data-theme="dark"] .settings-section-card,
html[data-theme="dark"] .relation-detail-card,
html[data-theme="dark"] .relation-legend-card,
html[data-theme="dark"] .brain-side-card,
html[data-theme="dark"] .brain-selected-panel,
html[data-theme="dark"] .admin-stat-card,
html[data-theme="dark"] .admin-table-card,
html[data-theme="dark"] .voice-chat-card,
html[data-theme="dark"] .patient-card,
html[data-theme="dark"] .session-card {
  background: rgba(8, 21, 37, 0.92) !important;
  border-color: rgba(141, 174, 211, 0.20) !important;
}

.patient-card:hover,
.session-card:hover,
.chemical-card:hover,
.bio-select-card:hover,
.history-tile:hover,
.bio-library-item:hover {
  border-color: rgba(31, 123, 193, 0.55) !important;
  box-shadow: var(--mg-glow) !important;
}

.patient-card.selected,
.chemical-card.selected,
.bio-select-card.selected,
.history-tile.active,
.bio-library-item.active {
  border-color: var(--mg-blue) !important;
  background: #f3f9ff !important;
  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.13), var(--shadow-sm) !important;
}

html[data-theme="dark"] .patient-card.selected,
html[data-theme="dark"] .chemical-card.selected,
html[data-theme="dark"] .bio-select-card.selected,
html[data-theme="dark"] .history-tile.active,
html[data-theme="dark"] .bio-library-item.active {
  background: rgba(31, 123, 193, 0.12) !important;
}

.patient-avatar,
.user-avatar,
.research-mini-stats span,
.pill,
.saved-pill,
.selected-patient-chip,
.bmi-chip {
  background: rgba(31, 123, 193, 0.10) !important;
  color: #0b4e84 !important;
  border-color: rgba(31, 123, 193, 0.18) !important;
}

html[data-theme="dark"] .patient-avatar,
html[data-theme="dark"] .user-avatar,
html[data-theme="dark"] .research-mini-stats span,
html[data-theme="dark"] .pill,
html[data-theme="dark"] .saved-pill,
html[data-theme="dark"] .selected-patient-chip,
html[data-theme="dark"] .bmi-chip {
  background: rgba(24, 183, 255, 0.13) !important;
  color: #bfeaff !important;
  border-color: rgba(24, 183, 255, 0.22) !important;
}

/* ---------- Dark simulation panels: fix black text issue ---------- */
.dark-card,
.ai-side-panel,
.medgen-side-panel,
.studio-medgen-console,
.avatar-test-queue-strip,
.research-warning,
.brain-canvas-card,
.hra-result-layout .d3-organ-intel-panel,
.d3-organ-intel-panel {
  background:
    radial-gradient(circle at 18% 0%, rgba(24, 183, 255, 0.16), transparent 30%),
    radial-gradient(circle at 92% 8%, rgba(31, 123, 193, 0.14), transparent 34%),
    linear-gradient(145deg, #03060c, #081525 58%, #06101f) !important;
  border: 1px solid rgba(24, 183, 255, 0.18) !important;
  color: var(--sim-text) !important;
}

.dark-card h1,
.dark-card h2,
.dark-card h3,
.dark-card h4,
.dark-card strong,
.dark-card span,
.ai-side-panel h1,
.ai-side-panel h2,
.ai-side-panel h3,
.ai-side-panel strong,
.ai-side-panel span,
.medgen-side-panel h3,
.medgen-side-panel strong,
.studio-medgen-console strong,
.brain-canvas-card h1,
.brain-canvas-card h2,
.brain-canvas-card h3,
.brain-canvas-card h4,
.brain-canvas-card strong,
.d3-organ-intel-panel h1,
.d3-organ-intel-panel h2,
.d3-organ-intel-panel h3,
.d3-organ-intel-panel h4,
.d3-organ-intel-panel strong {
  color: #f6fbff !important;
}

.dark-card p,
.dark-card small,
.ai-side-panel p,
.ai-side-panel small,
.medgen-side-panel p,
.medgen-side-panel small,
.studio-medgen-console span,
.brain-canvas-card p,
.brain-canvas-card small,
.d3-organ-intel-panel p,
.d3-organ-intel-panel small {
  color: var(--sim-muted) !important;
}

/* ---------- Brain / graph / canvas style matching logo ---------- */
.hra-body-viewer,
.brain-canvas-wrap,
.relation-graph-canvas-wrap,
.avatar-3d-container,
.studio-graph-shell {
  border: 1px solid rgba(24, 183, 255, 0.22) !important;
  background:
    linear-gradient(90deg, rgba(24, 183, 255, 0.055) 1px, transparent 1px),
    linear-gradient(rgba(24, 183, 255, 0.055) 1px, transparent 1px),
    radial-gradient(circle at 48% 42%, rgba(24, 183, 255, 0.18), transparent 34%),
    radial-gradient(circle at 80% 14%, rgba(31, 123, 193, 0.18), transparent 28%),
    linear-gradient(180deg, #06101f, #03060c) !important;
  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%, 100% 100% !important;
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.025),
    0 20px 54px rgba(3, 6, 12, 0.26) !important;
}

.brain-view-toggle button.active,
.d3-organ-intel-panel button.active,
.hra-result-layout button.active {
  background: linear-gradient(135deg, var(--mg-blue), var(--mg-cyan)) !important;
  border-color: rgba(24, 183, 255, 0.45) !important;
  color: #ffffff !important;
}

.score-meter div,
.organ-bar div,
.organ-score-track i,
.voice-progress i {
  background: linear-gradient(90deg, var(--mg-blue), var(--mg-cyan)) !important;
}

.medgen-stream-box,
.medgen-final-box,
.analysis-stream-box {
  background: rgba(3, 6, 12, 0.38) !important;
  border-color: rgba(24, 183, 255, 0.18) !important;
  color: #dceeff !important;
}

/* ---------- Logo aware hero surfaces ---------- */
.research-hero,
.impact-workbench-header,
.bio-studio-hero {
  background:
    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 30%),
    linear-gradient(180deg, #ffffff, #f8fbff) !important;
}

html[data-theme="dark"] .research-hero,
html[data-theme="dark"] .impact-workbench-header,
html[data-theme="dark"] .bio-studio-hero {
  background:
    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.16), transparent 30%),
    linear-gradient(145deg, #081525, #06101f) !important;
}

/* ---------- Layout / responsiveness cleanup ---------- */
.app-layout-shell,
.research-page,
.admin-page,
.session-page,
.brain-page-shell,
.dashboard-shell,
.main-content,
.app-layout-main {
  max-width: 100vw !important;
  overflow-x: hidden !important;
}

img,
video,
canvas,
svg,
iframe {
  max-width: 100%;
}

.page-toolbar,
.sessions-head,
.patients-head,
.topbar,
.topbar-left,
.topbar-right,
.patient-card-top,
.patient-card-bottom,
.session-card-head,
.session-card-head-top,
.research-card-head,
.admin-page-header,
.page-header {
  min-width: 0;
}

.filter-input,
.filter-select,
.page-toolbar-filters input,
.page-toolbar-filters select,
.page-toolbar-actions .btn,
.section-tools .btn {
  width: 100%;
}

/* ---------- Large screens ---------- */
@media (min-width: 1440px) {
  .app-layout-shell {
    padding-left: 28px !important;
    padding-right: 28px !important;
  }

  .patients-grid,
  .patients-page-grid {
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)) !important;
  }

  .sessions-grid,
  .sessions-page-grid {
    grid-template-columns: repeat(auto-fill, minmax(390px, 1fr)) !important;
  }
}

/* ---------- Tablet / small laptop ---------- */
@media (max-width: 1180px) {
  .topbar {
    position: sticky;
  }

  .dashboard-shell,
  .dashboard-hero-grid,
  .brain-map-layout,
  .relation-graph-layout,
  .reviews-layout,
  .settings-grid,
  .research-layout-row,
  .research-grid,
  .impact-validate-layout,
  .bio-library-layout,
  .molecular-studio-layout,
  .bio-studio-hero,
  .session-top-grid,
  .hra-result-layout {
    grid-template-columns: 1fr !important;
  }

  .research-right,
  .patient-context-pro,
  .sticky-panel {
    position: static !important;
  }

  .page-toolbar {
    align-items: stretch !important;
  }

  .page-toolbar-filters {
    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
  }

  .page-toolbar-actions,
  .section-tools {
    justify-content: flex-start !important;
  }
}

/* ---------- Tablet ---------- */
@media (max-width: 900px) {
  .app-layout-shell,
  .research-page,
  .admin-page,
  .session-page {
    padding: 12px !important;
  }

  .topbar {
    padding: 10px 12px !important;
    gap: 10px !important;
    flex-wrap: wrap;
  }

  .topbar-left {
    flex-grow: 1;
    flex-shrink: 1;
    flex-basis: calc(100% - 48px);
  }

  .topbar-right {
    width: 100%;
    justify-content: flex-start !important;
    flex-wrap: wrap;
  }

  .theme-toggle,
  .topbar-right .btn,
  .topbar-right button,
  .user-chip {
    flex: 1 1 150px;
    width: auto !important;
  }

  .page-header,
  .research-hero,
  .admin-page-header,
  .sessions-head,
  .patients-head,
  .organ-impact-table-head,
  .voice-chat-header,
  .molecular-studio-header,
  .compound-designer-head,
  .avatar-test-queue-strip {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .page-header-actions,
  .page-toolbar-actions,
  .section-tools,
  .patient-actions,
  .patient-card-actions {
    justify-content: flex-start !important;
  }

  .page-stats-grid,
  .metric-grid,
  .metric-grid-compact,
  .admin-stats-grid,
  .summary-grid,
  .compact-summary-grid,
  .analysis-grid,
  .question-grid,
  .two-col,
  .impact-pro-grid,
  .impact-grid,
  .admin-details-grid,
  .admin-form-grid,
  .settings-grid {
    grid-template-columns: 1fr !important;
  }

  .page-toolbar-filters,
  .admin-filter-card,
  .chemical-search-bar,
  .assist-search-row {
    grid-template-columns: 1fr !important;
  }

  .patients-grid,
  .patients-page-grid,
  .sessions-grid,
  .sessions-page-grid,
  .chemical-grid,
  .bio-card-grid,
  .history-grid-pro,
  .queue-grid,
  .studio-picker-grid {
    grid-template-columns: 1fr !important;
  }

  .research-tabs,
  .session-tabs,
  .bio-library-tabs,
  .bio-workbench-mode-tabs,
  .impact-step-tabs,
  .chemical-az-filter {
    display: flex !important;
    overflow-x: auto;
    flex-wrap: nowrap !important;
    padding-bottom: 6px;
    scroll-snap-type: x proximity;
    -webkit-overflow-scrolling: touch;
  }

  .research-tabs button,
  .session-tab,
  .bio-library-tabs button,
  .bio-workbench-mode-tabs button,
  .impact-step-tabs button,
  .chemical-az-filter button {
    flex: 0 0 auto;
    white-space: nowrap;
    scroll-snap-align: start;
  }

  .brain-canvas-wrap,
  .relation-graph-canvas-wrap,
  .relation-graph-svg,
  .studio-graph-shell,
  .studio-molecule-svg {
    height: 460px !important;
    min-height: 460px !important;
  }

  .brain-canvas-card {
    min-height: 540px !important;
  }

  .hra-body-viewer,
  .hra-result-layout > .hra-body-viewer,
  .d3-organ-intel-panel {
    height: 540px !important;
    min-height: 540px !important;
  }
}

/* ---------- Mobile ---------- */
@media (max-width: 600px) {
  body {
    font-size: 12.5px !important;
  }

  .app-layout-shell,
  .research-page,
  .admin-page,
  .session-page {
    padding: 10px !important;
  }

  .topbar {
    padding: 8px 10px !important;
    border-radius: 0;
  }

  .topbar-left {
    gap: 8px !important;
    width: 100%;
  }

  .topbar-title {
    font-size: 17px !important;
    white-space: normal !important;
  }

  .topbar-subtitle {
    display: none;
  }

  .brand-mark {
    width: 34px !important;
    height: 34px !important;
    flex: 0 0 auto;
  }

  .hamburger-btn {
    width: 34px !important;
    height: 34px !important;
    flex: 0 0 auto;
  }

  .theme-toggle,
  .topbar-right .btn,
  .topbar-right button,
  .user-chip {
    flex: 1 1 100% !important;
    width: 100% !important;
  }

  .page-title,
  h1 {
    font-size: 25px !important;
    line-height: 1.08 !important;
  }

  .page-subtitle,
  .research-hero p {
    font-size: 12px !important;
  }

  .page-header,
  .research-hero {
    margin-top: 10px !important;
    margin-bottom: 12px !important;
  }

  .metric-card,
  .stat-tile,
  .status-item,
  .summary-item {
    min-height: 82px !important;
    padding: 14px !important;
  }

  .patient-card,
  .session-card,
  .research-card,
  .sidebar-card,
  .settings-section-card,
  .brain-side-card,
  .brain-selected-panel,
  .brain-canvas-card,
  .admin-stat-card,
  .admin-table-card,
  .card {
    border-radius: 11px !important;
    padding: 12px !important;
  }

  .patient-card-top,
  .patient-card-bottom,
  .session-card-head,
  .session-card-head-top {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .patient-avatar {
    width: 38px !important;
    height: 38px !important;
  }

  .patient-meta h3,
  .session-card h3,
  .session-card h2 {
    font-size: 15.5px !important;
  }

  .patient-actions,
  .patient-card-actions,
  .session-badge-stack,
  .admin-actions,
  .admin-modal-actions,
  .impact-action-row,
  .voice-input-row,
  .bio-library-search,
  .selected-drugs-actions {
    width: 100%;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    margin-left: 0 !important;
  }

  .patient-actions .btn,
  .patient-card-actions .btn,
  .admin-actions button,
  .impact-action-row button,
  .voice-input-row button {
    width: 100%;
  }

  .mobile-drawer {
    width: min(330px, 88vw) !important;
  }

  .brain-canvas-wrap,
  .relation-graph-canvas-wrap,
  .relation-graph-svg,
  .studio-graph-shell,
  .studio-molecule-svg {
    height: 390px !important;
    min-height: 390px !important;
  }

  .brain-canvas-card {
    min-height: 470px !important;
  }

  .hra-body-viewer,
  .hra-result-layout > .hra-body-viewer,
  .d3-organ-intel-panel {
    height: 460px !important;
    min-height: 460px !important;
  }

  .voice-chat-window {
    height: 300px !important;
  }

  .admin-table,
  .organ-impact-table,
  .impact-table {
    min-width: 760px !important;
  }

  .studio-picker-modal,
  .wizard-modal,
  .admin-modal,
  .pdf-modal {
    width: calc(100vw - 20px) !important;
    max-height: 92vh !important;
  }
}

@media (max-width: 380px) {
  .page-title,
  h1 {
    font-size: 22px !important;
  }

  .topbar-title {
    font-size: 16px !important;
  }

  .btn,
  button,
  input,
  select,
  textarea {
    font-size: 12px !important;
  }
}

/* ==========================================================================
   MEDGEN DARK TOPBAR PATCH
   ========================================================================== */

.medgen-topbar {
  min-height: 68px !important;
  padding: 10px 18px !important;
  background:
    radial-gradient(circle at 8% 0%, rgba(24, 183, 255, 0.16), transparent 28%),
    linear-gradient(135deg, #03060c 0%, #06101f 48%, #0b1b30 100%) !important;
  border-bottom: 1px solid rgba(24, 183, 255, 0.18) !important;
  box-shadow: 0 14px 38px rgba(3, 6, 12, 0.22) !important;
}

.medgen-topbar .topbar-left,
.medgen-topbar .topbar-right {
  display: flex !important;
  align-items: center !important;
}

.medgen-topbar .topbar-title {
  color: #ffffff !important;
  font-weight: 900 !important;
  letter-spacing: -0.03em !important;
}

.medgen-topbar .topbar-subtitle {
  color: rgba(210, 230, 248, 0.72) !important;
}

.brand-mark-logo {
  padding: 0 !important;
  background: rgba(255, 255, 255, 0.08) !important;
  border: 1px solid rgba(24, 183, 255, 0.22) !important;
  overflow: hidden !important;
  box-shadow: 0 10px 26px rgba(24, 183, 255, 0.16) !important;
}

.brand-mark-logo img {
  width: 100% !important;
  height: 100% !important;
  display: block !important;
  object-fit: contain !important;
  border-radius: inherit !important;
}

/* Buttons on dark topbar */
.medgen-topbar .theme-toggle,
.medgen-topbar .btn-secondary {
  background: rgba(255, 255, 255, 0.96) !important;
  color: #07111f !important;
  border: 1px solid rgba(194, 218, 244, 0.55) !important;
  box-shadow: 0 8px 22px rgba(3, 6, 12, 0.16) !important;
}

.medgen-topbar .user-chip {
  background: rgba(255, 255, 255, 0.97) !important;
  border: 1px solid rgba(194, 218, 244, 0.55) !important;
  box-shadow: 0 8px 22px rgba(3, 6, 12, 0.16) !important;
}

.medgen-topbar .user-name {
  color: #07111f !important;
}

.medgen-topbar .user-role {
  color: #66778e !important;
}

.medgen-topbar .user-avatar {
  background: rgba(31, 123, 193, 0.12) !important;
  color: #0b4e84 !important;
}

/* Hamburger on dark topbar */
.medgen-topbar .hamburger-btn {
  background: rgba(255, 255, 255, 0.96) !important;
  border: 1px solid rgba(194, 218, 244, 0.55) !important;
}

.medgen-topbar .hamburger-btn span {
  background: #07111f !important;
}

@media (max-width: 760px) {
  .medgen-topbar {
    padding: 10px 12px !important;
  }

  .medgen-topbar .topbar-subtitle {
    display: none !important;
  }
}


/* ==========================================================================
   SESSION PAGE TEXT VISIBILITY PATCH
   Fixes white text inside light cards placed inside dark session panels
   ========================================================================== */

.session-page,
.session-workspace,
.session-detail-page,
.session-shell {
  --session-text: #07111f;
  --session-muted: #66778e;
  --session-border: #cddcec;
  --session-light-card: #f7fbff;
  --session-white: #ffffff;
  --session-dark: #06101f;
}

/* Main light cards on session page */
.session-page .card,
.session-page .panel,
.session-page .info-card,
.session-page .detail-card,
.session-page .patient-card,
.session-page .clinical-card,
.session-workspace .card,
.session-workspace .panel,
.session-workspace .info-card,
.session-workspace .detail-card,
.session-workspace .patient-card,
.session-workspace .clinical-card {
  color: var(--session-text) !important;
}

/* Labels inside patient/session detail cards */
.session-page label,
.session-page .label,
.session-page .field-label,
.session-page .meta-label,
.session-page .card-label,
.session-page .detail-label,
.session-workspace label,
.session-workspace .label,
.session-workspace .field-label,
.session-workspace .meta-label,
.session-workspace .card-label,
.session-workspace .detail-label {
  color: var(--session-muted) !important;
}

/* Values inside patient/session detail cards */
.session-page .value,
.session-page .field-value,
.session-page .meta-value,
.session-page .card-value,
.session-page .detail-value,
.session-workspace .value,
.session-workspace .field-value,
.session-workspace .meta-value,
.session-workspace .card-value,
.session-workspace .detail-value {
  color: var(--session-text) !important;
}

/* The white boxes inside the dark Session Status panel */
.session-page .session-status-card,
.session-page .status-card,
.session-page .status-box,
.session-page .status-item,
.session-page .summary-box,
.session-page .analysis-box,
.session-page .review-box,
.session-workspace .session-status-card,
.session-workspace .status-card,
.session-workspace .status-box,
.session-workspace .status-item,
.session-workspace .summary-box,
.session-workspace .analysis-box,
.session-workspace .review-box {
  background: var(--session-light-card) !important;
  color: var(--session-text) !important;
  border: 1px solid var(--session-border) !important;
}

/* Any text inside those white status boxes */
.session-page .session-status-card *,
.session-page .status-card *,
.session-page .status-box *,
.session-page .status-item *,
.session-page .summary-box *,
.session-page .analysis-box *,
.session-page .review-box *,
.session-workspace .session-status-card *,
.session-workspace .status-card *,
.session-workspace .status-box *,
.session-workspace .status-item *,
.session-workspace .summary-box *,
.session-workspace .analysis-box *,
.session-workspace .review-box * {
  color: inherit !important;
}

/* Keep headings inside dark panels white */
.session-page .dark-panel > h1,
.session-page .dark-panel > h2,
.session-page .dark-panel > h3,
.session-page .dark-panel > h4,
.session-page .session-status-panel > h1,
.session-page .session-status-panel > h2,
.session-page .session-status-panel > h3,
.session-page .session-status-panel > h4,
.session-workspace .dark-panel > h1,
.session-workspace .dark-panel > h2,
.session-workspace .dark-panel > h3,
.session-workspace .dark-panel > h4,
.session-workspace .session-status-panel > h1,
.session-workspace .session-status-panel > h2,
.session-workspace .session-status-panel > h3,
.session-workspace .session-status-panel > h4 {
  color: #ffffff !important;
}

/* Paragraphs directly on dark panels should stay readable */
.session-page .dark-panel > p,
.session-page .session-status-panel > p,
.session-workspace .dark-panel > p,
.session-workspace .session-status-panel > p {
  color: #a9bdd3 !important;
}

.app-sidebar-title-small {
  margin-top: 18px;
  font-size: 11px;
  opacity: 0.72;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}`, "",{"version":3,"sources":["webpack://./src/App.css"],"names":[],"mappings":"AAAA;;;;;;;;;+EAS+E;;AAE/E,oGAAoG;;AAEpG;EACE,kEAAkE;EAClE,kBAAkB;EAClB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,wCAAwC;;EAExC,eAAe;EACf,aAAa;EACb,kBAAkB;EAClB,sBAAsB;EACtB,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,uBAAuB;;EAEvB,SAAS;EACT,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;EACrB,uBAAuB;;EAEvB,sBAAsB;EACtB,iBAAiB;EACjB,wBAAwB;EACxB,sCAAsC;EACtC,6CAA6C;EAC7C,8CAA8C;EAC9C,+CAA+C;EAC/C,+CAA+C;;EAE/C,WAAW;EACX,kBAAkB;EAClB,uBAAuB;EACvB,kBAAkB;EAClB,uBAAuB;EACvB,iBAAiB;EACjB,sBAAsB;EACtB,eAAe;EACf,oBAAoB;;EAEpB,WAAW;EACX,iBAAiB;EACjB,qBAAqB;EACrB,gBAAgB;EAChB,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;EACjB,iBAAiB;EACjB,kBAAkB;;EAElB,eAAe;EACf,0EAA0E;EAC1E,aAAa;EACb,aAAa;EACb,aAAa;EACb,eAAe;EACf,aAAa;EACb,aAAa;EACb,cAAc;EACd,cAAc;;EAEd,WAAW;EACX,qCAAqC;EACrC,4BAA4B;EAC5B,uBAAuB;EACvB,wBAAwB;EACxB,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA,uCAAuC;AACvC;EACE,kBAAkB;EAClB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,wCAAwC;;EAExC,aAAa;EACb,kBAAkB;EAClB,sBAAsB;EACtB,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,uBAAuB;;EAEvB,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;EACrB,mCAAmC;EACnC,0CAA0C;EAC1C,uBAAuB;EACvB,wBAAwB;EACxB,0BAA0B;EAC1B,8CAA8C;AAChD;;AAEA;;+EAE+E;;AAE/E;;;EAGE,sBAAsB;EACtB,YAAY;AACd;;AAEA;;;EAGE,SAAS;EACT,gBAAgB;EAChB,WAAW;AACb;;AAEA;;EAEE,eAAe;EACf,kBAAkB;AACpB;;AAEA;EACE,6BAA6B;EAC7B,yBAAyB;EACzB,iBAAiB;EACjB,kBAAkB;EAClB;;4DAE0D;EAC1D,mCAAmC;EACnC,kCAAkC;AACpC;;AAEA;EACE;;4DAE0D;AAC5D;;AAEA;;;;EAIE,oBAAoB;AACtB;;AAEA;EACE,SAAS;EACT,eAAe;AACjB;;AAEA;;;;EAIE,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,cAAc;AAChB;;AAEA;;;;;;;EAOE,aAAa;AACf;;AAEA;;;;;;EAME,qBAAqB;EACrB,wBAAwB;AAC1B;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,qBAAqB;AACvB;;AAEA;EACE,uBAAuB;AACzB;;AAEA;;+EAE+E;;AAE/E;;;;;;EAME,WAAW;EACX,gBAAgB;EAChB,iBAAiB;EACjB,+BAA+B;EAC/B,+BAA+B;EAC/B,iCAAiC;EACjC,kBAAkB;EAClB,uBAAuB;EACvB,gBAAgB;EAChB,aAAa;EACb,iFAAiF;AACnF;;AAEA;EACE,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;;EAEE,wBAAwB;AAC1B;;AAEA;;;;;;EAME,gCAAgC;EAChC,yCAAyC;AAC3C;;AAEA;;+EAE+E;;AAE/E;;;;;;;;;;;;;;;;;;;EAmBE,gBAAgB;EAChB,iBAAiB;EACjB,+BAA+B;EAC/B,uBAAuB;EACvB,gBAAgB;EAChB,iBAAiB;EACjB,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,QAAQ;EACR,yHAAyH;AAC3H;;AAEA;;;;;EAKE,2BAA2B;AAC7B;;AAEA;;;;;;;;;;EAUE,0BAA0B;EAC1B,8BAA8B;EAC9B,4BAA4B;AAC9B;;AAEA;;;;EAIE,8BAA8B;AAChC;;AAEA;;;EAGE,qBAAqB;EACrB,gCAAgC;EAChC,+BAA+B;AACjC;;AAEA;;EAEE,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;EAClB,uBAAuB;EACvB,+BAA+B;AACjC;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;;+EAE+E;;AAE/E;EACE,iBAAiB;AACnB;;AAEA;;;;;;EAME,WAAW;EACX,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,SAAS;EACT,uBAAuB;AACzB;;AAEA;EACE,WAAW;EACX,2BAA2B;EAC3B,gCAAgC;EAChC,SAAS;EACT,kBAAkB;EAClB,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,kBAAkB;EAClB,YAAY;EACZ,uBAAuB;AACzB;;AAEA;;EAEE,aAAa;EACb,mBAAmB;EACnB,SAAS;AACX;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,8BAA8B;EAC9B,gBAAgB;EAChB,uBAAuB;EACvB,4BAA4B;AAC9B;;AAEA;EACE,SAAS;EACT,eAAe;EACf,iBAAiB;EACjB,gBAAgB;EAChB,qBAAqB;AACvB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,cAAc;EACd,WAAW;EACX,WAAW;EACX,oBAAoB;EACpB,uBAAuB;EACvB,oDAAoD;AACtD;;AAEA;EACE,wCAAwC;AAC1C;;AAEA;EACE,UAAU;AACZ;;AAEA;EACE,0CAA0C;AAC5C;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,gBAAgB;EAChB,iBAAiB;EACjB,oBAAoB;EACpB,+BAA+B;EAC/B,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA;;EAEE,aAAa;EACb,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,yBAAyB;EACzB,+BAA+B;EAC/B,uBAAuB;AACzB;;AAEA;EACE,uBAAuB;EACvB,gBAAgB;EAChB,qBAAqB;AACvB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA;EACE,gBAAgB;AAClB;;AAEA,sBAAsB;AACtB;;EAEE,eAAe;EACf,QAAQ;EACR,kCAAkC;EAClC,UAAU;EACV,oBAAoB;EACpB,8BAA8B;EAC9B,YAAY;AACd;;AAEA;;EAEE,UAAU;EACV,oBAAoB;AACtB;;AAEA;EACE,eAAe;EACf,SAAS;EACT,UAAU;EACV,YAAY;EACZ,YAAY;EACZ,4BAA4B;EAC5B,gCAAgC;AAClC;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,eAAe;EACf,MAAM;EACN,OAAO;EACP,uBAAuB;EACvB,aAAa;EACb,aAAa;EACb,0BAA0B;EAC1B,qCAAqC;EACrC,4BAA4B;EAC5B,4BAA4B;EAC5B,gCAAgC;EAChC,aAAa;EACb,gBAAgB;AAClB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA;;EAEE,mBAAmB;EACnB,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;EAChB,yBAAyB;EACzB,sBAAsB;AACxB;;AAEA;EACE,aAAa;EACb,QAAQ;AACV;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,gBAAgB;EAChB,iBAAiB;EACjB,+BAA+B;EAC/B,qBAAqB;EACrB,kBAAkB;EAClB,uBAAuB;EACvB,6BAA6B;EAC7B,uBAAuB;EACvB,gBAAgB;EAChB,mBAAmB;EACnB,gBAAgB;AAClB;;AAEA;EACE,+BAA+B;EAC/B,2BAA2B;AAC7B;;AAEA;EACE,YAAY;EACZ,8BAA8B;AAChC;;AAEA;;+EAE+E;;AAE/E;;;;;;;;;;;;;;;;;;;;EAoBE,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;;EAEE,aAAa;AACf;;AAEA;EACE,cAAc;EACd,+BAA+B;EAC/B,uCAAuC;AACzC;;AAEA;;;;;;EAME,cAAc;AAChB;;AAEA;;;;EAIE,gCAAgC;AAClC;;AAEA;;EAEE,uBAAuB;AACzB;;AAEA;;EAEE,oBAAoB;EACpB,mBAAmB;EACnB,QAAQ;EACR,kBAAkB;EAClB,uBAAuB;EACvB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;EACzB,yBAAyB;AAC3B;;AAEA;;;;;;;;;;;;EAYE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,SAAS;AACX;;AAEA;EACE,mBAAmB;AACrB;;AAEA;;EAEE,eAAe;EACf,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,SAAS;EACT,iBAAiB;EACjB,aAAa;EACb,gCAAgC;EAChC,SAAS;AACX;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;;;;;;;;;;EAUE,YAAY;AACd;;AAEA;;;;;;;;EAQE,aAAa;EACb,SAAS;AACX;;AAEA;;;;;;;EAOE,aAAa;EACb,SAAS;AACX;;AAEA;EACE,gDAAgD;AAClD;;AAEA;EACE,gDAAgD;AAClD;;AAEA;;;;;;EAME,gDAAgD;AAClD;;AAEA;;;;;;;;EAQE,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;;;;EAIE,cAAc;EACd,kBAAkB;EAClB,uBAAuB;EACvB,uBAAuB;EACvB,yBAAyB;EACzB,sBAAsB;AACxB;;AAEA;;;;EAIE,uBAAuB;AACzB;;AAEA;;;;EAIE,YAAY;EACZ,8BAA8B;EAC9B,yBAAyB;AAC3B;;AAEA;;;;EAIE,uBAAuB;AACzB;;AAEA;;+EAE+E;;AAE/E;EACE,aAAa;EACb,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,kBAAkB;EAClB,YAAY;EACZ,UAAU;EACV,YAAY;EACZ,aAAa;EACb,kBAAkB;EAClB,yEAAyE;EACzE,oBAAoB;AACtB;;AAEA;EACE,SAAS;EACT,wBAAwB;EACxB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,mBAAmB;EACnB,YAAY;EACZ,aAAa;EACb,sBAAsB;EACtB,uBAAuB;AACzB;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,cAAc;EACd,eAAe;EACf,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,gBAAgB;EAChB,eAAe;AACjB;;AAEA;;;;;EAKE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,gBAAgB;EAChB,oBAAoB;EACpB,uBAAuB;EACvB,gBAAgB;EAChB,+BAA+B;EAC/B,yBAAyB;EACzB,0CAA0C;AAC5C;;AAEA;;;EAGE,+BAA+B;EAC/B,cAAc;EACd,qBAAqB;AACvB;;AAEA;EACE,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,8BAA8B;EAC9B,cAAc;AAChB;;AAEA;EACE,gCAAgC;EAChC,uBAAuB;EACvB,2BAA2B;AAC7B;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,mBAAmB;EACnB,eAAe;AACjB;;AAEA;;;;;;;EAOE,gBAAgB;EAChB,iBAAiB;EACjB,+BAA+B;EAC/B,+BAA+B;EAC/B,gCAAgC;EAChC,kBAAkB;EAClB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;;;;;;;EAOE,YAAY;EACZ,8BAA8B;EAC9B,gCAAgC;EAChC,gBAAgB;AAClB;;AAEA;;EAEE,aAAa;EACb,2CAA2C;EAC3C,SAAS;EACT,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA;;;EAGE,gBAAgB;EAChB,SAAS;AACX;;AAEA;EACE,aAAa;AACf;;AAEA;;EAEE,mBAAmB;AACrB;;AAEA;EACE,+BAA+B;EAC/B,yBAAyB;EACzB,0CAA0C;EAC1C,mBAAmB;EACnB,oBAAoB;EACpB,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,0BAA0B;AAC5B;;AAEA;;+EAE+E;;AAE/E;EACE,yBAAyB;AAC3B;;AAEA;;;EAGE,WAAW;EACX,gBAAgB;EAChB,+BAA+B;EAC/B,0CAA0C;EAC1C,0BAA0B;AAC5B;;AAEA;;;EAGE,WAAW;EACX,gBAAgB;EAChB,yBAAyB;EACzB,mBAAmB;EACnB,0BAA0B;AAC5B;;AAEA;;;EAGE,YAAY;EACZ,gBAAgB;EAChB,gBAAgB;EAChB,kCAAkC;EAClC,+BAA+B;EAC/B,iDAAiD;EACjD,2CAA2C;EAC3C,eAAe;EACf,gBAAgB;EAChB,iBAAiB;EACjB,oBAAoB;EACpB,sBAAsB;AACxB;;AAEA;;;EAGE,gBAAgB;EAChB,gBAAgB;EAChB,mBAAmB;EACnB,gDAAgD;EAChD,iDAAiD;EACjD,kBAAkB;EAClB,uBAAuB;EACvB,iBAAiB;EACjB,+BAA+B;AACjC;;AAEA;;;EAGE,gCAAgC;AAClC;;AAEA;;;EAGE,kCAAkC;AACpC;;AAEA;;EAEE,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,uBAAuB;AACzB;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,0BAA0B;EAC1B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,uBAAuB;EACvB,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,qBAAqB;EACrB,uBAAuB;AACzB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;AACV;;AAEA;EACE,UAAU;EACV,YAAY;EACZ,oBAAoB;EACpB,cAAc;AAChB;;AAEA;;EAEE,cAAc;AAChB;;AAEA;EACE,qBAAqB;EACrB,uBAAuB;AACzB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,8BAA8B;EAC9B,eAAe;EACf,gBAAgB;EAChB,yBAAyB;AAC3B;;AAEA;EACE,aAAa;EACb,8CAA8C;EAC9C,QAAQ;EACR,mBAAmB;AACrB;;AAEA;;;EAGE,gBAAgB;EAChB,oBAAoB;EACpB,mBAAmB;AACrB;;AAEA;EACE,WAAW;AACb;;AAEA;;;EAGE,cAAc;EACd,YAAY;EACZ,oBAAoB;AACtB;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE,uBAAuB;EACvB,qBAAqB;AACvB;;AAEA;;EAEE,cAAc;EACd,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,gBAAgB;EAChB,kBAAkB;EAClB,0BAA0B;AAC5B;;AAEA;;+EAE+E;;AAE/E;EACE,aAAa;EACb,kBAAkB;AACpB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,wBAAwB;EACxB,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,oDAAoD;EACpD,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,cAAc;EACd,uBAAuB;EACvB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;AAC1B;;AAEA;EACE,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;EACb,aAAa;EACb,4CAA4C;EAC5C,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,8BAA8B;EAC9B,cAAc;EACd,+BAA+B;EAC/B,kBAAkB;EAClB,mBAAmB;AACrB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,uBAAuB;EACvB,gBAAgB;EAChB,gBAAgB;EAChB,oBAAoB;EACpB,uBAAuB;EACvB,gBAAgB;EAChB,gCAAgC;EAChC,kBAAkB;AACpB;;AAEA;EACE,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,8BAA8B;EAC9B,cAAc;AAChB;;AAEA;EACE,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;;EAEE,gCAAgC;EAChC,qBAAqB;AACvB;;AAEA;;EAEE,0BAA0B;EAC1B,YAAY;AACd;;AAEA;;EAEE,yBAAyB;EACzB,YAAY;AACd;;AAEA;;EAEE,mBAAmB;EACnB,YAAY;AACd;;AAEA;;EAEE,uBAAuB;EACvB,YAAY;AACd;;AAEA;;EAEE,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,aAAa;EACb,kBAAkB;EAClB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,mBAAmB;EACnB,SAAS;EACT,gBAAgB;AAClB;;AAEA;;;;;EAKE,eAAe;EACf,QAAQ;EACR,kCAAkC;EAClC,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,aAAa;EACb,aAAa;EACb,0BAA0B;AAC5B;;AAEA;;;;;EAKE,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,YAAY;EACZ,gBAAgB;EAChB,gBAAgB;EAChB,aAAa;AACf;;AAEA;EACE,uBAAuB;AACzB;;AAEA;;;EAGE,aAAa;EACb,8BAA8B;EAC9B,uBAAuB;EACvB,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,uBAAuB;AACzB;;AAEA;;;EAGE,WAAW;EACX,YAAY;EACZ,+BAA+B;EAC/B,gCAAgC;EAChC,qBAAqB;EACrB,gBAAgB;AAClB;;AAEA;;EAEE,aAAa;EACb,oDAAoD;EACpD,SAAS;AACX;;AAEA;EACE,gBAAgB;EAChB,mBAAmB;AACrB;;AAEA;EACE,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;AACf;;AAEA;EACE,cAAc;EACd,uBAAuB;EACvB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,sBAAsB;AACxB;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,QAAQ;EACR,gBAAgB;AAClB;;AAEA;;+EAE+E;;AAE/E;EACE,aAAa;EACb,oCAAoC;EACpC,QAAQ;EACR,mBAAmB;EACnB,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,mBAAmB;EACnB,kBAAkB;EAClB,iBAAiB;EACjB,+BAA+B;EAC/B,+BAA+B;EAC/B,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;;;;EAIE,aAAa;EACb,4DAA4D;EAC5D,SAAS;AACX;;AAEA;;;;;;;EAOE,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,eAAe;EACf,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;;;;EAIE,gCAAgC;EAChC,4BAA4B;AAC9B;;AAEA;;;;EAIE,gCAAgC;EAChC,kCAAkC;EAClC,yCAAyC;AAC3C;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,QAAQ;AACV;;AAEA;;;;;EAKE,cAAc;EACd,qBAAqB;EACrB,uBAAuB;EACvB,iBAAiB;EACjB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;;EAEE,cAAc;EACd,eAAe;EACf,gBAAgB;EAChB,oBAAoB;EACpB,gCAAgC;EAChC,uBAAuB;EACvB,eAAe;EACf,gBAAgB;EAChB,cAAc;EACd,mBAAmB;EACnB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;;;;;;;;EAQE,cAAc;EACd,eAAe;EACf,uBAAuB;EACvB,uBAAuB;EACvB,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;EACE,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,uBAAuB;EACvB,SAAS;AACX;;AAEA;EACE,eAAe;EACf,kBAAkB;EAClB,gBAAgB;EAChB,kBAAkB;EAClB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;EACR,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,UAAU;AACZ;;AAEA;EACE,aAAa;EACb,SAAS;AACX;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,mBAAmB;AACrB;;AAEA;EACE,SAAO;AACT;;AAEA;;;;;EAKE,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;AACf;;AAEA;;EAEE,cAAc;EACd,qBAAqB;EACrB,kBAAkB;AACpB;;AAEA;;EAEE,SAAS;EACT,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,2CAA2C;EAC3C,SAAS;EACT,kBAAkB;AACpB;;AAEA;EACE,iBAAiB;EACjB,gBAAgB;EAChB,aAAa;EACb,QAAQ;EACR,kBAAkB;AACpB;;AAEA;;+EAE+E;;AAE/E;EACE,iBAAiB;AACnB;;AAEA;EACE,0BAA0B;EAC1B,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;EACb,mBAAmB;EACnB,4BAA4B;AAC9B;;AAEA;EACE,SAAS;EACT,uBAAuB;AACzB;;AAEA;EACE,uBAAuB;EACvB,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;EAEE,aAAa;EACb,oDAAoD;EACpD,SAAS;AACX;;AAEA;;;EAGE,cAAc;EACd,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;;EAEE,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,yBAAyB;EACzB,QAAQ;EACR,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,2CAA2C;EAC3C,SAAS;EACT,kBAAkB;AACpB;;AAEA;;EAEE,mBAAmB;EACnB,YAAY;EACZ,+BAA+B;EAC/B,aAAa;EACb,iBAAiB;EACjB,4BAA4B;AAC9B;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,YAAY;AACd;;AAEA;EACE,cAAc;EACd,iBAAiB;AACnB;;AAEA;;EAEE,gBAAgB;EAChB,qCAAqC;EACrC,2CAA2C;EAC3C,+BAA+B;EAC/B,aAAa;EACb,cAAc;EACd,iBAAiB;EACjB,gBAAgB;EAChB,uBAAuB;EACvB,iBAAiB;AACnB;;AAEA;;;EAGE,qBAAqB;EACrB,SAAS;EACT,6EAA6E;EAC7E,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;AACrB;;AAEA;;+EAE+E;;AAE/E;;;;EAIE,WAAW;EACX,+BAA+B;EAC/B,gBAAgB;EAChB,+BAA+B;EAC/B,kBAAkB;EAClB;;;;WAIS;EACT,2DAA2D;AAC7D;;AAEA;EACE,YAAY;EACZ,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,iEAAiE;EACjE,SAAS;EACT,oBAAoB;AACtB;;AAEA;EACE,YAAY;EACZ,iBAAiB;AACnB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,kBAAkB;EAClB,+BAA+B;EAC/B,kCAAkC;EAClC,cAAc;EACd,gBAAgB;EAChB,2CAA2C;AAC7C;;AAEA;;EAEE,gBAAgB;EAChB,oBAAoB;EACpB,0CAA0C;EAC1C,gCAAgC;EAChC,cAAc;EACd,eAAe;EACf,gBAAgB;EAChB,sBAAsB;EACtB,kBAAkB;EAClB,yBAAyB;EACzB,2BAA2B;EAC3B,oBAAoB;AACtB;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,UAAU;EACV,UAAU;AACZ;;AAEA;;;EAGE,aAAa;EACb,8DAA8D;EAC9D,SAAS;AACX;;AAEA;EACE,aAAa;EACb,iBAAiB;AACnB;;AAEA;;;;;EAKE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,eAAe;AACjB;;AAEA;;EAEE,iBAAiB;AACnB;;AAEA;EACE,WAAW;EACX,aAAa;EACb,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,0BAA0B;EAC1B,kBAAkB;EAClB,oBAAoB;EACpB,iBAAiB;EACjB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,8BAA8B;EAC9B,YAAY;EACZ,gCAAgC;AAClC;;AAEA;;+EAE+E;;AAE/E;EACE,SAAS;AACX;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,2CAA2C;EAC3C,SAAS;EACT,oBAAoB;EACpB,mBAAmB;EACnB,aAAa;EACb,+BAA+B;EAC/B,0BAA0B;EAC1B,+BAA+B;AACjC;;AAEA;;;;EAIE,eAAe;EACf,yBAAyB;EACzB,uBAAuB;EACvB,gBAAgB;EAChB,sBAAsB;EACtB,yBAAyB;AAC3B;;AAEA;;;;EAIE,SAAS;EACT,qBAAqB;EACrB,iCAAiC;EACjC,gBAAgB;AAClB;;AAEA;;;;EAIE,cAAc;EACd,eAAe;EACf,uBAAuB;EACvB,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,qBAAqB;EACrB,aAAa;EACb,+BAA+B;EAC/B,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,wBAAwB;EACxB,cAAc;EACd,cAAc;AAChB;;AAEA;EACE,mBAAmB;EACnB,cAAc;EACd,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,eAAe;EACf,QAAQ;EACR,mBAAmB;EACnB,YAAY;EACZ,+BAA+B;EAC/B,gCAAgC;EAChC,+BAA+B;AACjC;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;EACnB,mBAAmB;EACnB,aAAa;EACb,+BAA+B;EAC/B,mBAAmB;EACnB,YAAY;AACd;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,yBAAyB;AAC3B;;AAEA;EACE,cAAc;EACd,eAAe;EACf,cAAc;EACd,uBAAuB;AACzB;;AAEA;;;EAGE,aAAa;EACb,eAAe;EACf,QAAQ;AACV;;AAEA;EACE,mBAAmB;EACnB,kBAAkB;EAClB,+BAA+B;EAC/B,mBAAmB;EACnB,yBAAyB;EACzB,cAAc;EACd,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;;;;;EAKE,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,4BAA4B;AAC9B;;AAEA;;EAEE,aAAa;EACb,gBAAgB;AAClB;;AAEA;;EAEE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,uBAAuB;EACvB,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,iDAAiD;EACjD,SAAS;EACT,oBAAoB;AACtB;;AAEA;;;EAGE,aAAa;EACb,+BAA+B;AACjC;;AAEA;EACE,cAAc;EACd,kBAAkB;EAClB,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,0CAA0C;AAC5C;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,gBAAgB;AAClB;;AAEA;;EAEE,gBAAgB;EAChB,+BAA+B;EAC/B,gBAAgB;EAChB,mBAAmB;EACnB,YAAY;EACZ,gBAAgB;AAClB;;AAEA;EACE,YAAY;EACZ,kBAAkB;AACpB;;AAEA;EACE,YAAY;EACZ,iBAAiB;EACjB,aAAa;EACb,+BAA+B;EAC/B;;;;WAIS;EACT,2DAA2D;EAC3D,qDAAqD;AACvD;;AAEA;EACE,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;EACnB,mBAAmB;EACnB,YAAY;AACd;;AAEA;;;EAGE,cAAc;AAChB;;AAEA;;EAEE,cAAc;EACd,uBAAuB;AACzB;;AAEA;EACE,WAAW;EACX,iBAAiB;EACjB,+BAA+B;EAC/B,gBAAgB;EAChB,cAAc;EACd,uBAAuB;AACzB;;AAEA;EACE,YAAY;AACd;;AAEA;EACE,iBAAiB;EACjB,cAAc;EACd,aAAa;EACb,gDAAgD;EAChD,QAAQ;EACR,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B,0BAA0B;EAC1B,eAAe;EACf,gBAAgB;EAChB,YAAY;AACd;;AAEA;EACE,gCAAgC;EAChC,kCAAkC;AACpC;;AAEA;EACE,cAAc;EACd,qBAAqB;EACrB,yBAAyB;AAC3B;;AAEA;EACE,cAAc;EACd,uBAAuB;EACvB,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,gBAAgB;EAChB,gBAAgB;EAChB,aAAa;EACb,0CAA0C;AAC5C;;AAEA;;EAEE,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,cAAc;EACd,uBAAuB;AACzB;;AAEA;EACE,eAAe;EACf,aAAa;EACb,YAAY;EACZ,6BAA6B;EAC7B,YAAY;EACZ,+BAA+B;EAC/B,kCAAkC;EAClC,2CAA2C;EAC3C,4BAA4B;EAC5B,cAAc;AAChB;;AAEA;EACE,YAAY;EACZ,+BAA+B;EAC/B,oCAAoC;EACpC,0CAA0C;EAC1C,kBAAkB;AACpB;;AAEA;;EAEE,cAAc;AAChB;;AAEA;EACE,YAAY;EACZ,uBAAuB;AACzB;;AAEA;;EAEE,cAAc;EACd,eAAe;AACjB;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,+BAA+B;EAC/B,eAAe;EACf,gBAAgB;EAChB,qCAAqC;EACrC,cAAc;EACd,uBAAuB;EACvB,gBAAgB;EAChB,gBAAgB;AAClB;;AAEA;EACE,oCAAoC;AACtC;;AAEA;EACE,mCAAmC;EACnC,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,gBAAgB;EAChB,+CAA+C;AACjD;;AAEA;EACE,wBAAwB;EACxB,gBAAgB;EAChB,gBAAgB;EAChB,aAAa;EACb,sBAAsB;AACxB;;AAEA;EACE,SAAS;EACT,kBAAkB;EAClB,sCAAsC;AACxC;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,oBAAoB;EACpB,eAAe;AACjB;;AAEA;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,iBAAiB;EACjB,gCAAgC;EAChC,kBAAkB;EAClB,gBAAgB;EAChB,0BAA0B;AAC5B;;AAEA;EACE,YAAY;EACZ,8BAA8B;AAChC;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,SAAS;EACT,mBAAmB;EACnB,aAAa;EACb,+BAA+B;EAC/B,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,SAAS;EACT,+BAA+B;EAC/B,gBAAgB;EAChB,iBAAiB;EACjB,cAAc;EACd,gBAAgB;AAClB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,cAAc;EACd,oBAAoB;EACpB,aAAa;EACb,4DAA4D;EAC5D,SAAS;AACX;;AAEA;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;EACb,gBAAgB;EAChB,0BAA0B;EAC1B,eAAe;AACjB;;AAEA;EACE,gCAAgC;EAChC,kCAAkC;AACpC;;AAEA;;;EAGE,cAAc;AAChB;;AAEA;EACE,qBAAqB;EACrB,uBAAuB;AACzB;;AAEA;EACE,kBAAkB;EAClB,eAAe;EACf,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,uBAAuB;EACvB,eAAe;EACf,iBAAiB;EACjB,uBAAuB;AACzB;;AAEA;;+EAE+E;;AAE/E;;EAEE,iBAAiB;EACjB,aAAa;EACb,mBAAmB;EACnB,aAAa;AACf;;AAEA;;EAEE,0BAA0B;EAC1B,+BAA+B;EAC/B,4BAA4B;AAC9B;;AAEA;EACE,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,uBAAuB;EACvB,aAAa;EACb,+BAA+B;AACjC;;AAEA;EACE,wBAAwB;EACxB,kBAAkB;AACpB;;AAEA;EACE,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,SAAS;EACT,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,QAAQ;AACV;;AAEA;;EAEE,uBAAuB;EACvB,gBAAgB;EAChB,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,oBAAoB;EACpB,SAAS;EACT,gBAAgB;AAClB;;AAEA;EACE,gBAAgB;EAChB,yBAAyB;EACzB,eAAe;EACf,SAAS;AACX;;AAEA;EACE,qCAAqC;EACrC,aAAa;AACf;;AAEA;;EAEE,aAAa;EACb,QAAQ;EACR,eAAe;AACjB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,oBAAoB;EACpB,mBAAmB;EACnB,WAAW;EACX,YAAY;EACZ,oBAAoB;EACpB,gCAAgC;EAChC,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,SAAS;EACT,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,qCAAqC;AACvC;;AAEA;EACE,UAAU;EACV,gBAAgB;AAClB;;AAEA;EACE,uBAAuB;AACzB;;AAEA;;EAEE,aAAa;EACb,QAAQ;EACR,eAAe;AACjB;;AAEA;EACE,mBAAmB;EACnB,cAAc;AAChB;;AAEA;EACE,oBAAoB;EACpB,sCAAsC;AACxC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,gBAAgB;EAChB,SAAS;EACT,UAAU;EACV,aAAa;EACb,8BAA8B;EAC9B,SAAS;EACT,uBAAuB;EACvB,mCAAmC;EACnC,qCAAqC;EACrC,2BAA2B;AAC7B;;AAEA;EACE,kCAAkC;AACpC;;AAEA;EACE,sCAAsC;EACtC,aAAa;AACf;;AAEA;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,gBAAgB;EAChB,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;AACjC;;AAEA;;EAEE,WAAW;EACX,gBAAgB;AAClB;;AAEA;EACE,SAAS;AACX;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA;EACE,aAAa;AACf;;AAEA;EACE,aAAa;EACb,uBAAuB;EACvB,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,uBAAuB;EACvB,qBAAqB;AACvB;;AAEA;EACE,+BAA+B;EAC/B,+BAA+B;EAC/B,iBAAiB;EACjB,+BAA+B;EAC/B,kBAAkB;EAClB,gBAAgB;AAClB;;AAEA;EACE,mBAAmB;AACrB;;AAEA;EACE,cAAc;EACd,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;EAChB,kBAAkB;AACpB;;AAEA;EACE,WAAW;EACX,gCAAgC;EAChC,oBAAoB;EACpB,gBAAgB;AAClB;;AAEA;EACE,cAAc;EACd,YAAY;EACZ,8BAA8B;EAC9B,oBAAoB;AACtB;;AAEA;EACE,aAAa;EACb,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;EAC/B,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,mBAAmB;AACrB;;AAEA;EACE,2BAA2B;AAC7B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,cAAc;EACd,kBAAkB;EAClB,+BAA+B;EAC/B,uBAAuB;EACvB,iBAAiB;EACjB,qBAAqB;AACvB;;AAEA;EACE,0BAA0B;EAC1B,kBAAkB;EAClB,+BAA+B;AACjC;;AAEA;EACE,8BAA8B;EAC9B,YAAY;AACd;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,qBAAqB;AACvB;;AAEA;EACE,SAAO;EACP,YAAY;EACZ,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,eAAe;EACf,YAAY;EACZ,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;EAC/B,eAAe;EACf,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,eAAe;EACf,8BAA8B;EAC9B,cAAc;EACd,qBAAqB;AACvB;;AAEA;;+EAE+E;;AAE/E;EACE,gBAAgB;EAChB,+BAA+B;EAC/B,+BAA+B;EAC/B,aAAa;EACb,+BAA+B;AACjC;;AAEA;EACE,uBAAuB;AACzB;;AAEA;EACE,uBAAuB;EACvB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,cAAc;AAChB;;AAEA;;EAEE,WAAW;EACX,+BAA+B;EAC/B,mBAAmB;EACnB,iBAAiB;EACjB,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,mBAAmB;AACrB;;AAEA;EACE,SAAO;EACP,YAAY;EACZ,mBAAmB;AACrB;;AAEA;EACE,aAAa;EACb,QAAQ;EACR,kBAAkB;EAClB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,eAAe;EACf,4BAA4B;EAC5B,cAAc;EACd,YAAY;EACZ,+BAA+B;EAC/B,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,aAAa;AACf;;AAEA;EACE,kBAAkB;EAClB,YAAY;EACZ,aAAa;EACb,kBAAkB;AACpB;;AAEA;;;;;EAKE,kBAAkB;EAClB,qDAAqD;EACrD,yBAAyB;EACzB,mDAAmD;AACrD;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;EAClB,MAAM;EACN,WAAW;AACb;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,SAAS;EACT,WAAW;EACX,mBAAmB;AACrB;;AAEA;EACE,YAAY;EACZ,aAAa;EACb,UAAU;EACV,UAAU;EACV,kCAAkC;AACpC;;AAEA;EACE,WAAW;EACX,aAAa;EACb,UAAU;EACV,mBAAmB;AACrB;;AAEA;EACE,SAAS;EACT,uBAAuB;AACzB;;AAEA;EACE,UAAU;EACV,wBAAwB;AAC1B;;AAEA;EACE,WAAW;EACX,aAAa;EACb,UAAU;EACV,mBAAmB;AACrB;;AAEA;EACE,UAAU;AACZ;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,kBAAkB;EAClB,WAAW;EACX,YAAY;EACZ,gCAAgC;EAChC,kBAAkB;EAClB,uBAAuB;EACvB,WAAW;EACX,aAAa;EACb,mBAAmB;EACnB,YAAY;EACZ,cAAc;EACd,kBAAkB;EAClB,gBAAgB;EAChB,eAAe;AACjB;;AAEA;EACE,kBAAkB;EAClB,SAAS;EACT,qBAAqB;EACrB,0BAA0B;EAC1B,+BAA+B;EAC/B,gBAAgB;EAChB,oBAAoB;EACpB,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,SAAS;EACT,gBAAgB;EAChB,uBAAuB;EACvB,eAAe;AACjB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,uBAAuB;EACvB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,kBAAkB;AACpB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;;+EAE+E;;AAE/E;EACE,mBAAmB;AACrB;;AAEA;EACE,kBAAkB;AACpB;;AAEA;EACE,0BAA0B;AAC5B;;AAEA;EACE,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,mBAAmB;EACnB,aAAa;EACb,SAAS;AACX;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,wCAAwC;EACxC,wCAAwC;EACxC,kBAAkB;EAClB,kCAAkC;AACpC;;AAEA;EACE,KAAK,yBAAyB,EAAE;AAClC;;AAEA;EACE,WAAW;EACX,gBAAgB;EAChB,aAAa;EACb,+BAA+B;EAC/B,+BAA+B;EAC/B,+BAA+B;EAC/B,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,yBAAyB;EACzB,oBAAoB;EACpB,iBAAiB;EACjB,gBAAgB;EAChB,mBAAmB;EACnB,uBAAuB;AACzB;;AAEA;EACE,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,8BAA8B;EAC9B,cAAc;AAChB;;AAEA;;+EAE+E;;AAE/E;EACE;IACE,0BAA0B;EAC5B;;EAEA;IACE,aAAa;IACb,iBAAiB;EACnB;;EAEA;IACE,iBAAiB;EACnB;AACF;;AAEA;EACE;;IAEE,aAAa;EACf;;EAEA;;;;;;;;;;;;IAYE,0BAA0B;EAC5B;;EAEA;IACE,QAAQ;EACV;;EAEA;IACE,QAAQ;EACV;;EAEA;;;;IAIE,gDAAgD;EAClD;;EAEA;;;IAGE,gBAAgB;EAClB;;EAEA;IACE,YAAY;EACd;;EAEA;IACE,iBAAiB;EACnB;;EAEA;;;IAGE,iBAAiB;IACjB,aAAa;EACf;AACF;;AAEA;EACE;;;IAGE,aAAa;EACf;;EAEA;;;;;;;;;;;IAWE,sBAAsB;IACtB,oBAAoB;EACtB;;EAEA;;IAEE,WAAW;IACX,eAAe;EACjB;;EAEA;;IAEE,SAAO;IACP,gBAAgB;EAClB;;EAEA;;IAEE,eAAe;EACjB;;EAEA;;;;;;;;;;;;;IAaE,0BAA0B;EAC5B;;EAEA;;;IAGE,mBAAmB;EACrB;;EAEA;;;;;;;;IAQE,0BAA0B;EAC5B;;EAEA;;IAEE,0BAA0B;EAC5B;;EAEA;;;;IAIE,sBAAsB;IACtB,aAAa;EACf;;EAEA;IACE,WAAW;EACb;;EAEA;IACE,cAAc;EAChB;;EAEA;;;;;IAKE,gBAAgB;IAChB,iBAAiB;IACjB,mBAAmB;EACrB;;EAEA;;;;;IAKE,cAAc;IACd,mBAAmB;EACrB;;EAEA;IACE,iBAAiB;EACnB;;EAEA;IACE,aAAa;IACb,iBAAiB;EACnB;;EAEA;IACE,iBAAiB;EACnB;;EAEA;IACE,iBAAiB;EACnB;;EAEA;IACE,uBAAuB;IACvB,aAAa;EACf;;EAEA;IACE,gBAAgB;IAChB,+BAA+B;EACjC;;EAEA;IACE,eAAe;EACjB;AACF;;AAEA;EACE;IACE,uBAAuB;EACzB;;EAEA;IACE,kBAAkB;EACpB;;EAEA;IACE,eAAe;EACjB;;EAEA;IACE,WAAW;IACX,YAAY;EACd;;EAEA;IACE,WAAW;IACX,2BAA2B;EAC7B;;EAEA;;;;;IAKE,aAAa;IACb,+BAA+B;EACjC;;EAEA;;;IAGE,aAAa;EACf;;EAEA;IACE,aAAa;IACb,+BAA+B;EACjC;;EAEA;IACE,eAAe;EACjB;;EAEA;;;IAGE,gBAAgB;EAClB;;EAEA;;;;;;IAME,gBAAgB;IAChB,eAAe;EACjB;;EAEA;;;IAGE,iBAAiB;IACjB,aAAa;EACf;;EAEA;;IAEE,aAAa;IACb,iBAAiB;EACnB;;EAEA;IACE,aAAa;EACf;AACF;;;AAGA;;;;;;+EAM+E;;AAE/E,4CAA4C;AAC5C;;;;;;;;EAQE,WAAW;EACX,eAAe;EACf,kBAAkB;AACpB;;AAEA;;;;;EAKE,WAAW;EACX,YAAY;AACd;;AAEA,2CAA2C;AAC3C;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,8BAA8B;EAC9B,yCAAyC;EACzC,WAAW;EACX,SAAS;AACX;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,sCAAsC;EACtC,cAAc;EACd,YAAY;AACd;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,oCAAoC;EACpC,cAAc;EACd,sBAAsB;AACxB;;AAEA;;EAEE,mBAAmB;AACrB;;AAEA;EACE,sBAAsB;AACxB;;AAEA,8CAA8C;AAC9C;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,yCAAyC;EACzC,SAAS;EACT,mBAAmB;AACrB;;AAEA;EACE,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,mCAAmC;EACnC,iBAAiB;EACjB,gBAAgB;EAChB,wBAAwB;EACxB,qBAAqB;AACvB;;AAEA;EACE,SAAS;EACT,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,yBAAyB;EACzB,SAAS;EACT,cAAc;AAChB;;AAEA,mDAAmD;AACnD;EACE,wBAAwB;EACxB,sDAAsD;EACtD,SAAS;EACT,kBAAkB;EAClB,iBAAiB;AACnB;;AAEA;;EAEE,gDAAgD;AAClD;;AAEA;EACE,wBAAwB;EACxB,sBAAsB;EACtB,SAAS;AACX;;AAEA;EACE,cAAc;AAChB;;AAEA;EACE,wBAAwB;EACxB,4DAA4D;EAC5D,SAAS;EACT,oBAAoB;EACpB,mBAAmB;AACrB;;AAEA;;EAEE,YAAY;AACd;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,8BAA8B;AAChC;;AAEA;;;EAGE,aAAa;EACb,SAAS;AACX;;AAEA,qDAAqD;AACrD;;;EAGE,wBAAwB;EACxB,oBAAoB;EACpB,oBAAoB;AACtB;;AAEA;EACE,2DAA2D;EAC3D,mBAAmB;AACrB;;AAEA;EACE,2DAA2D;AAC7D;;AAEA;EACE,2DAA2D;AAC7D;;AAEA;;;;EAIE,gBAAgB;EAChB,aAAa;EACb,sBAAsB;EACtB,uBAAuB;EACvB,uBAAuB;EACvB,aAAa;EACb,mBAAmB;AACrB;;AAEA;;;;EAIE,yCAAyC;EACzC,YAAY;AACd;;AAEA,kDAAkD;AAClD;EACE,wBAAwB;EACxB,mBAAmB;EACnB,8BAA8B;EAC9B,SAAS;EACT,mBAAmB;EACnB,eAAe;AACjB;;AAEA;EACE,wBAAwB;EACxB,2DAA2D;EAC3D,SAAS;EACT,cAAc;AAChB;;AAEA;EACE,aAAa;EACb,mBAAmB;EACnB,yBAAyB;EACzB,SAAS;AACX;;AAEA;EACE,wBAAwB;EACxB,mBAAmB;EACnB,yBAAyB;EACzB,SAAS;EACT,eAAe;AACjB;;AAEA;;EAEE,WAAW;EACX,gBAAgB;AAClB;;AAEA,gDAAgD;AAChD;;EAEE,wBAAwB;EACxB,uEAAuE;EACvE,oBAAoB;EACpB,WAAW;EACX,oBAAoB;AACtB;;AAEA;;EAEE,WAAW;EACX,YAAY;EACZ,gBAAgB;EAChB,+BAA+B;EAC/B,0BAA0B;EAC1B,mBAAmB;EACnB,4BAA4B;EAC5B,gFAAgF;AAClF;;AAEA;EACE,wBAAwB;EACxB,sBAAsB;EACtB,SAAS;EACT,iBAAiB;EACjB,aAAa;AACf;;AAEA;;EAEE,gCAAgC;EAChC,4BAA4B;EAC5B,2BAA2B;AAC7B;;AAEA;EACE,gCAAgC;EAChC,2DAA2D;AAC7D;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,yCAAyC;EACzC,SAAS;AACX;;AAEA;EACE,YAAY;EACZ,cAAc;AAChB;;AAEA;EACE,eAAe;EACf,eAAe;EACf,gBAAgB;EAChB,gBAAgB;EAChB,qBAAqB;EACrB,sBAAsB;AACxB;;AAEA;;EAEE,SAAS;EACT,uBAAuB;EACvB,eAAe;AACjB;;AAEA;EACE,WAAW;EACX,YAAY;EACZ,mBAAmB;EACnB,yBAAyB;EACzB,+BAA+B;EAC/B,cAAc;AAChB;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,8BAA8B;EAC9B,yCAAyC;EACzC,SAAS;EACT,gBAAgB;EAChB,eAAe;AACjB;;AAEA;;EAEE,gBAAgB;EAChB,iBAAiB;AACnB;;AAEA;;EAEE,aAAa;EACb,mBAAmB;EACnB,QAAQ;EACR,iBAAiB;EACjB,eAAe;AACjB;;AAEA,iFAAiF;AACjF;;EAEE,YAAY;AACd;;AAEA,iDAAiD;AACjD;;EAEE,wBAAwB;EACxB,uEAAuE;EACvE,oBAAoB;EACpB,WAAW;EACX,oBAAoB;AACtB;;AAEA;EACE,cAAc;EACd,WAAW;EACX,UAAU;EACV,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,iBAAiB;EACjB,aAAa;AACf;;AAEA;;EAEE,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,yCAAyC;EACzC,SAAS;AACX;;AAEA;;EAEE,eAAe;EACf,eAAe;EACf,qBAAqB;AACvB;;AAEA;EACE,aAAa;EACb,sBAAsB;EACtB,QAAQ;EACR,qBAAqB;EACrB,cAAc;AAChB;;AAEA;EACE,oBAAoB;EACpB,qBAAqB;EACrB,4BAA4B;EAC5B,gBAAgB;EAChB,gBAAgB;EAChB,uBAAuB;AACzB;;AAEA,iEAAiE;AACjE;;EAEE,wBAAwB;EACxB,8BAA8B;EAC9B,gCAAgC;EAChC,yCAAyC;EACzC,SAAS;EACT,mBAAmB;AACrB;;AAEA;;EAEE,cAAc;AAChB;;AAEA,kDAAkD;AAClD;EACE,wBAAwB;EACxB,2DAA2D;EAC3D,SAAS;AACX;;AAEA;EACE,aAAa;AACf;;AAEA;;EAEE,aAAa;EACb,sBAAsB;EACtB,SAAS;AACX;;AAEA,mDAAmD;AACnD;EACE,wBAAwB;EACxB,uEAAuE;EACvE,SAAS;EACT,oBAAoB;AACtB;;AAEA;;;EAGE,aAAa;AACf;;AAEA;EACE,aAAa;EACb,SAAS;AACX;;AAEA;EACE,aAAa;EACb,iBAAiB;AACnB;;AAEA;EACE,aAAa;AACf;;AAEA,+CAA+C;AAC/C;EACE,aAAa;EACb,SAAS;AACX;;AAEA;EACE,wBAAwB;EACxB,yEAAyE;EACzE,SAAS;EACT,oBAAoB;AACtB;;AAEA;;;EAGE,aAAa;AACf;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,iBAAiB;AACnB;;AAEA;EACE,aAAa;EACb,SAAS;AACX;;AAEA,kDAAkD;AAClD;EACE,uBAAuB;AACzB;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,+BAA+B;EAC/B,yCAAyC;AAC3C;;AAEA;;EAEE,wBAAwB;EACxB,sDAAsD;EACtD,oBAAoB;EACpB,kBAAkB;AACpB;;AAEA;EACE,wBAAwB;EACxB,sBAAsB;EACtB,SAAS;AACX;;AAEA;;EAEE,mBAAmB;AACrB;;AAEA;EACE,wBAAwB;EACxB,0BAA0B;AAC5B;;AAEA;EACE,wBAAwB;EACxB,sDAAsD;AACxD;;AAEA;EACE,wBAAwB;EACxB,4DAA4D;EAC5D,SAAS;EACT,oBAAoB;AACtB;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,8BAA8B;EAC9B,yCAAyC;AAC3C;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,iBAAiB;AACnB;;AAEA,iDAAiD;AACjD;EACE,uBAAuB;AACzB;;AAEA;EACE,wBAAwB;EACxB,8BAA8B;EAC9B,kCAAkC;EAClC,yCAAyC;AAC3C;;AAEA;EACE,wBAAwB;EACxB,2DAA2D;AAC7D;;AAEA;EACE,wBAAwB;EACxB,sEAAsE;EACtE,mBAAmB;AACrB;;AAEA;EACE,WAAW;AACb;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,wBAAwB;EACxB,mBAAmB;EACnB,2BAA2B;EAC3B,QAAQ;EACR,eAAe;AACjB;;AAEA,oFAAoF;AACpF;;;;;EAKE,WAAW;AACb;;AAEA,gDAAgD;AAChD;EACE,aAAa;EACb,SAAS;EACT,uBAAuB;AACzB;;AAEA;EACE,wBAAwB;EACxB,uEAAuE;EACvE,SAAS;AACX;;AAEA;EACE,wBAAwB;EACxB,0BAA0B;AAC5B;;AAEA;;;EAGE,wBAAwB;EACxB,2DAA2D;EAC3D,SAAS;AACX;;AAEA,8CAA8C;AAC9C;;EAEE,wBAAwB;EACxB,2DAA2D;EAC3D,SAAS;AACX;;AAEA;EACE,mBAAmB;AACrB;;AAEA,0EAA0E;AAC1E;;;EAGE,eAAe;EACf,gBAAgB;AAClB;;AAEA,yEAAyE;AACzE;;;;;;;;;;;;EAYE,mBAAmB;AACrB;;AAEA,6CAA6C;AAC7C;EACE;;;;;;IAME,qCAAqC;EACvC;;EAEA;;;IAGE,2BAA2B;EAC7B;;EAEA;;;IAGE,aAAa;IACb,iBAAiB;EACnB;AACF;;AAEA,4DAA4D;AAC5D;EACE;IACE,qCAAqC;EACvC;;EAEA;IACE,QAAQ;EACV;;EAEA;IACE,QAAQ;EACV;;EAEA;;;;;IAKE,2DAA2D;EAC7D;;EAEA;;IAEE,yCAAyC;EAC3C;;EAEA;;;IAGE,iCAAiC;IACjC,+BAA+B;EACjC;;EAEA;IACE,2BAA2B;EAC7B;;EAEA;IACE,qCAAqC;EACvC;AACF;;AAEA,6CAA6C;AAC7C;EACE;IACE,kCAAkC;IAClC,SAAS;EACX;;EAEA;IACE,WAAW;EACb;;EAEA;IACE,WAAW;IACX,sCAAsC;IACtC,eAAe;IACf,YAAY;EACd;;EAEA;;IAEE,mBAAmB;EACrB;;EAEA;;;IAGE,sBAAsB;EACxB;;EAEA;;;;IAIE,wBAAwB;EAC1B;;EAEA;;;;;;;;;;;;;IAaE,qCAAqC;EACvC;;EAEA;;;;IAIE,qCAAqC;EACvC;;EAEA;;;;;;;IAOE,iCAAiC;IACjC,+BAA+B;EACjC;;EAEA;;;IAGE,uBAAuB;IACvB,cAAc;EAChB;;EAEA;;;;;IAKE,gBAAgB;IAChB,4BAA4B;IAC5B,mBAAmB;EACrB;;EAEA;;;;;IAKE,cAAc;IACd,mBAAmB;EACrB;;EAEA;;;IAGE,aAAa;IACb,iBAAiB;EACnB;;EAEA;IACE,iBAAiB;EACnB;;EAEA;IACE,gBAAgB;EAClB;AACF;;;;AAIA;;;;;;;;;+EAS+E;;AAE/E;EACE,kDAAkD;EAClD,kBAAkB;EAClB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,uCAAuC;EACvC,0CAA0C;;EAE1C,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB;EAClB,kBAAkB;;EAElB,aAAa;EACb,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,uBAAuB;EACvB,yBAAyB;;EAEzB,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;;EAErB,iBAAiB;EACjB,wBAAwB;;EAExB,6CAA6C;EAC7C,8CAA8C;EAC9C,+CAA+C;EAC/C,+CAA+C;;EAE/C,gBAAgB;EAChB,gBAAgB;EAChB,iBAAiB;EACjB,iBAAiB;EACjB,iBAAiB;EACjB,kBAAkB;;EAElB;;;;;;;;;;cAUY;;EAEZ;;;;;;;;cAQY;;EAEZ;;;;;;;aAOW;;EAEX,eAAe;EACf,eAAe;EACf,eAAe;EACf,eAAe;EACf,aAAa;EACb,aAAa;EACb,cAAc;EACd,cAAc;;EAEd,qCAAqC;EACrC,4BAA4B;EAC5B,0BAA0B;EAC1B,qCAAqC;;EAErC,kCAAkC;EAClC,iBAAiB;EACjB,mBAAmB;EACnB,oBAAoB;EACpB,yBAAyB;EACzB,qCAAqC;EACrC,sBAAsB;EACtB,4CAA4C;EAC5C,mBAAmB;EACnB,oBAAoB;;EAEpB,mBAAmB;EACnB,wBAAwB;EACxB,oBAAoB;EACpB,wBAAwB;AAC1B;;AAEA,8CAA8C;AAC9C;EACE,kCAAkC;AACpC;;AAEA;EACE,wCAAwC;EACxC,yBAAyB;EACzB,wBAAwB;EACxB,kBAAkB;EAClB;;;uEAGqE;AACvE;;AAEA;;;;;;;EAOE,2CAA2C;EAC3C,qBAAqB;EACrB,uBAAuB;EACvB,kBAAkB;AACpB;;AAEA;EACE,mCAAmC;EACnC,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,iCAAiC;EACjC,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;EACE,eAAe;EACf,iBAAiB;EACjB,gBAAgB;AAClB;;AAEA;;;;;;EAME,uBAAuB;AACzB;;AAEA;EACE,iBAAiB;AACnB;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;;;;EAKE,wCAAwC;AAC1C;;AAEA,6CAA6C;AAC7C;EACE,kCAAkC;AACpC;;AAEA;EACE,2BAA2B;EAC3B,4BAA4B;AAC9B;;AAEA;EACE,sBAAsB;EACtB,uBAAuB;EACvB,6BAA6B;EAC7B,0BAA0B;EAC1B,sBAAsB;EACtB,8CAA8C;AAChD;;AAEA;EACE,0BAA0B;EAC1B,2BAA2B;EAC3B,iBAAiB;AACnB;;AAEA;EACE,0BAA0B;EAC1B,0BAA0B;EAC1B,uBAAuB;AACzB;;AAEA;EACE,sBAAsB;EACtB,uBAAuB;EACvB,6BAA6B;EAC7B,4BAA4B;AAC9B;;AAEA;EACE,2BAA2B;EAC3B,4BAA4B;EAC5B,8BAA8B;AAChC;;AAEA;EACE,sBAAsB;EACtB,uBAAuB;EACvB,0BAA0B;AAC5B;;AAEA;EACE,4BAA4B;EAC5B,2BAA2B;AAC7B;;AAEA;EACE,2BAA2B;AAC7B;;AAEA,qEAAqE;AACrE;;;;;;;;;;;;;;;;;;;;EAoBE,6BAA6B;EAC7B,gBAAgB;EAChB,iBAAiB;EACjB,eAAe;EACf,gBAAgB;EAChB,uBAAuB;EACvB,gBAAgB;AAClB;;AAEA;;;;;;;;;;EAUE,yCAAyC;EACzC,uBAAuB;AACzB;;AAEA;;;;EAIE,yCAAyC;AAC3C;;AAEA;;;;EAIE,8BAA8B;EAC9B,0CAA0C;EAC1C,gCAAgC;AAClC;;AAEA;;;;EAIE,qCAAqC;EACrC,uBAAuB;AACzB;;AAEA,iCAAiC;AACjC;;;;;;EAME,2BAA2B;EAC3B,6BAA6B;EAC7B,4BAA4B;EAC5B,4BAA4B;EAC5B,2BAA2B;EAC3B,kBAAkB;EAClB,8BAA8B;AAChC;;AAEA;EACE,gBAAgB;AAClB;;AAEA;;;;;;EAME,0BAA0B;EAC1B,2BAA2B;EAC3B,uBAAuB;EACvB,yBAAyB;AAC3B;;AAEA,6CAA6C;AAC7C;;;;;;;;;;;;;;;;;;;;EAoBE,8BAA8B;EAC9B,sCAAsC;EACtC,uCAAuC;AACzC;;AAEA;;;;;;;;;EASE,gDAAgD;AAClD;;AAEA,6CAA6C;AAC7C;EACE,6BAA6B;EAC7B,8BAA8B;AAChC;;AAEA;EACE,8CAA8C;EAC9C,2BAA2B;AAC7B;;AAEA;EACE,0BAA0B;EAC1B,uBAAuB;AACzB;;AAEA;EACE,uEAAuE;EACvE,2CAA2C;EAC3C,8BAA8B;AAChC;;AAEA;;;;;;EAME,oCAAoC;EACpC,0BAA0B;EAC1B,2BAA2B;EAC3B,iCAAiC;AACnC;;AAEA,qDAAqD;AACrD;;;;;EAKE,4CAA4C;EAC5C,gDAAgD;EAChD,oCAAoC;EACpC,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA;;;;;;;EAOE,6BAA6B;EAC7B,8BAA8B;EAC9B,gCAAgC;EAChC,yBAAyB;EACzB,4BAA4B;EAC5B,2BAA2B;EAC3B,2BAA2B;AAC7B;;AAEA;;;;;;;EAOE,yCAAyC;EACzC,2CAA2C;EAC3C,yBAAyB;AAC3B;;AAEA,uCAAuC;AACvC;;;EAGE,uBAAuB;EACvB,sFAAsF;EACtF,4BAA4B;EAC5B,2BAA2B;EAC3B,kCAAkC;AACpC;;AAEA;;;EAGE,4BAA4B;EAC5B,yBAAyB;EACzB,2BAA2B;AAC7B;;AAEA;;;EAGE,8BAA8B;AAChC;;AAEA,mEAAmE;AACnE;;;;;;EAME;;kFAEgF;EAChF,sDAAsD;EACtD,iCAAiC;AACnC;;AAEA;;;;;;;;;;;EAWE,uCAAuC;EACvC,yBAAyB;AAC3B;;AAEA;;;;;;EAME,kCAAkC;AACpC;;AAEA,gEAAgE;AAChE;;;;;EAKE,oDAAoD;EACpD;;;;;sEAKoE;EACpE,iFAAiF;EACjF;;gDAE8C;AAChD;;AAEA;;;EAGE;;wDAEsD;EACtD,iCAAiC;EACjC,sDAAsD;AACxD;;AAEA;;;;;EAKE,iCAAiC;AACnC;;AAEA;;;;;EAKE,uBAAuB;AACzB;;AAEA;;EAEE,qFAAqF;AACvF;;AAEA;;;EAGE,yCAAyC;EACzC,2CAA2C;AAC7C;;AAEA;;EAEE,iDAAiD;EACjD,4CAA4C;EAC5C,yBAAyB;AAC3B;;AAEA,iDAAiD;AACjD;;;EAGE,wCAAwC;EACxC,uBAAuB;AACzB;;AAEA;;EAEE,OAAO;AACT;;AAEA;;;;EAIE,oBAAoB;AACtB;;AAEA,mDAAmD;AACnD;;;;EAIE,oBAAoB;AACtB;;AAEA;;;;;EAKE,6BAA6B;EAC7B,gCAAgC;EAChC,8BAA8B;EAC9B,2BAA2B;AAC7B;;AAEA;;;;;EAKE,2CAA2C;EAC3C,wDAAwD;EACxD,8BAA8B;AAChC;;AAEA;;;;;EAKE,4BAA4B;EAC5B,2BAA2B;EAC3B,uBAAuB;AACzB;;AAEA,gDAAgD;AAChD;;;;;EAKE,6CAA6C;EAC7C,mDAAmD;AACrD;;AAEA;;;;EAIE,8BAA8B;EAC9B,yDAAyD;AAC3D;;AAEA;;EAEE,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA;EACE,2BAA2B;EAC3B,6BAA6B;AAC/B;;AAEA;EACE,yCAAyC;AAC3C;;AAEA;EACE,sBAAsB;EACtB,uBAAuB;EACvB,eAAe;AACjB;;AAEA,sCAAsC;AACtC;;;EAGE,8CAA8C;EAC9C,yBAAyB;AAC3B;;AAEA;;;EAGE,8CAA8C;EAC9C,yBAAyB;AAC3B;;AAEA;;;EAGE,8CAA8C;EAC9C,yBAAyB;AAC3B;;AAEA;;;EAGE,8CAA8C;EAC9C,yBAAyB;AAC3B;;AAEA,0DAA0D;AAC1D;;;EAGE,0BAA0B;EAC1B,2BAA2B;AAC7B;;AAEA;;;EAGE,4BAA4B;EAC5B,uBAAuB;AACzB;;AAEA;EACE,8BAA8B;AAChC;;AAEA,mDAAmD;AACnD;EACE,6CAA6C;EAC7C,iDAAiD;AACnD;;AAEA;EACE,8CAA8C;EAC9C,iDAAiD;AACnD;;AAEA;EACE,gDAAgD;AAClD;;AAEA;EACE,+CAA+C;AACjD;;AAEA,uCAAuC;AACvC;EACE,aAAa;EACb,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;EACrB,mCAAmC;EACnC,0CAA0C;AAC5C;;AAEA;EACE;;gEAE8D;AAChE;;AAEA;;;EAGE,8BAA8B;EAC9B,6BAA6B;EAC7B,kDAAkD;AACpD;;AAEA;;;;EAIE,8BAA8B;EAC9B,yBAAyB;AAC3B;;AAEA,iDAAiD;AACjD;EACE;IACE,kCAAkC;EACpC;;EAEA;IACE,2BAA2B;EAC7B;;EAEA;IACE,0BAA0B;EAC5B;;EAEA;IACE,0BAA0B;EAC5B;;EAEA;IACE,0BAA0B;EAC5B;AACF;;AAEA;;;;;;;;;;;;+EAY+E;;AAE/E;EACE,wBAAwB;EACxB,mBAAmB;EACnB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,kBAAkB;EAClB,sBAAsB;EACtB,sBAAsB;EACtB,kBAAkB;EAClB,mBAAmB;EACnB,mBAAmB;EACnB,wCAAwC;EACxC,mFAAmF;;EAEnF,6CAA6C;EAC7C,yBAAyB;EACzB,iCAAiC;EACjC,iCAAiC;EACjC,sBAAsB;EACtB,sBAAsB;EACtB,sBAAsB;EACtB,wCAAwC;EACxC,2CAA2C;;EAE3C,aAAa;EACb,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,kCAAkC;EAClC,oCAAoC;;EAEpC,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;EACrB,uBAAuB;;EAEvB,iBAAiB;EACjB,wBAAwB;EACxB,qCAAqC;;EAErC,4CAA4C;EAC5C,6CAA6C;EAC7C,8CAA8C;EAC9C,8CAA8C;;EAE9C,0BAA0B;EAC1B,4BAA4B;EAC5B,0BAA0B;;EAE1B,iBAAiB;EACjB,mBAAmB;EACnB,oBAAoB;EACpB,yBAAyB;EACzB,oCAAoC;EACpC,6BAA6B;EAC7B,4CAA4C;EAC5C,mBAAmB;EACnB,oBAAoB;AACtB;;AAEA;EACE,6BAA6B;EAC7B,iCAAiC;EACjC,6BAA6B;EAC7B,6BAA6B;EAC7B,sBAAsB;EACtB,sBAAsB;EACtB,wCAAwC;EACxC,0CAA0C;;EAE1C,aAAa;EACb,kBAAkB;EAClB,kBAAkB;EAClB,yBAAyB;EACzB,uBAAuB;EACvB,wBAAwB;EACxB,uBAAuB;EACvB,yBAAyB;;EAEzB,kBAAkB;EAClB,eAAe;EACf,oBAAoB;EACpB,qBAAqB;EACrB,mCAAmC;EACnC,0CAA0C;EAC1C,uBAAuB;EACvB,wBAAwB;EACxB,2CAA2C;EAC3C,8CAA8C;AAChD;;AAEA;EACE;;;uEAGqE;AACvE;;AAEA;EACE;;;gEAG8D;AAChE;;AAEA,uCAAuC;AACvC;EACE;;;+DAG6D;EAC7D,yBAAyB;EACzB,wFAAwF;AAC1F;;AAEA;;;;;;EAME,mCAAmC;AACrC;;AAEA;EACE,gBAAgB;EAChB,MAAM;EACN,YAAY;EACZ,gDAAgD;EAChD,yCAAyC;EACzC,kDAAkD;AACpD;;AAEA;EACE,2CAA2C;EAC3C,8CAA8C;AAChD;;AAEA,gDAAgD;AAChD;;;;;;;;;;;;;;;;;;EAkBE,uEAAuE;EACvE,iDAAiD;EACjD,yBAAyB;EACzB,0DAA0D;AAC5D;;AAEA;;;;EAIE,kFAAkF;EAClF,2BAA2B;AAC7B;;AAEA;;;EAGE,gEAAgE;EAChE,yBAAyB;EACzB,qDAAqD;AACvD;;AAEA;;;;;;EAME,2CAA2C;EAC3C,yDAAyD;AAC3D;;AAEA,2CAA2C;AAC3C;;;;;;;;;;;;;;;;;;;;;;EAsBE,kDAAkD;EAClD,yDAAyD;AAC3D;;AAEA;;;;;;;;;;;;;EAaE,4CAA4C;EAC5C,kDAAkD;AACpD;;AAEA;;;;;;EAME,iDAAiD;EACjD,qCAAqC;AACvC;;AAEA;;;;;EAKE,uCAAuC;EACvC,8BAA8B;EAC9B,2EAA2E;AAC7E;;AAEA;;;;;EAKE,+CAA+C;AACjD;;AAEA;;;;;;;EAOE,+CAA+C;EAC/C,yBAAyB;EACzB,iDAAiD;AACnD;;AAEA;;;;;;;EAOE,+CAA+C;EAC/C,yBAAyB;EACzB,iDAAiD;AACnD;;AAEA,uEAAuE;AACvE;;;;;;;;;EASE;;;qEAGmE;EACnE,qDAAqD;EACrD,iCAAiC;AACnC;;AAEA;;;;;;;;;;;;;;;;;;;;;;;;EAwBE,yBAAyB;AAC3B;;AAEA;;;;;;;;;;;EAWE,kCAAkC;AACpC;;AAEA,qEAAqE;AACrE;;;;;EAKE,qDAAqD;EACrD;;;;;wDAKsD;EACtD,iFAAiF;EACjF;;+CAE6C;AAC/C;;AAEA;;;EAGE,8EAA8E;EAC9E,iDAAiD;EACjD,yBAAyB;AAC3B;;AAEA;;;;EAIE,6EAA6E;AAC/E;;AAEA;;;EAGE,2CAA2C;EAC3C,iDAAiD;EACjD,yBAAyB;AAC3B;;AAEA,mDAAmD;AACnD;;;EAGE;;wDAEsD;AACxD;;AAEA;;;EAGE;;wDAEsD;AACxD;;AAEA,0DAA0D;AAC1D;;;;;;;;EAQE,2BAA2B;EAC3B,6BAA6B;AAC/B;;AAEA;;;;;EAKE,eAAe;AACjB;;AAEA;;;;;;;;;;;;;EAaE,YAAY;AACd;;AAEA;;;;;;EAME,WAAW;AACb;;AAEA,wCAAwC;AACxC;EACE;IACE,6BAA6B;IAC7B,8BAA8B;EAChC;;EAEA;;IAEE,uEAAuE;EACzE;;EAEA;;IAEE,uEAAuE;EACzE;AACF;;AAEA,gDAAgD;AAChD;EACE;IACE,gBAAgB;EAClB;;EAEA;;;;;;;;;;;;;;IAcE,qCAAqC;EACvC;;EAEA;;;IAGE,2BAA2B;EAC7B;;EAEA;IACE,+BAA+B;EACjC;;EAEA;IACE,2DAA2D;EAC7D;;EAEA;;IAEE,sCAAsC;EACxC;AACF;;AAEA,iCAAiC;AACjC;EACE;;;;IAIE,wBAAwB;EAC1B;;EAEA;IACE,6BAA6B;IAC7B,oBAAoB;IACpB,eAAe;EACjB;;EAEA;IACE,YAA2B;IAA3B,cAA2B;IAA3B,6BAA2B;EAC7B;;EAEA;IACE,WAAW;IACX,sCAAsC;IACtC,eAAe;EACjB;;EAEA;;;;IAIE,eAAe;IACf,sBAAsB;EACxB;;EAEA;;;;;;;;;;IAUE,iCAAiC;IACjC,+BAA+B;EACjC;;EAEA;;;;;IAKE,sCAAsC;EACxC;;EAEA;;;;;;;;;;;;;;IAcE,qCAAqC;EACvC;;EAEA;;;;IAIE,qCAAqC;EACvC;;EAEA;;;;;;;;;IASE,qCAAqC;EACvC;;EAEA;;;;;;IAME,wBAAwB;IACxB,gBAAgB;IAChB,4BAA4B;IAC5B,mBAAmB;IACnB,6BAA6B;IAC7B,iCAAiC;EACnC;;EAEA;;;;;;IAME,cAAc;IACd,mBAAmB;IACnB,wBAAwB;EAC1B;;EAEA;;;;;IAKE,wBAAwB;IACxB,4BAA4B;EAC9B;;EAEA;IACE,4BAA4B;EAC9B;;EAEA;;;IAGE,wBAAwB;IACxB,4BAA4B;EAC9B;AACF;;AAEA,iCAAiC;AACjC;EACE;IACE,4BAA4B;EAC9B;;EAEA;;;;IAIE,wBAAwB;EAC1B;;EAEA;IACE,4BAA4B;IAC5B,gBAAgB;EAClB;;EAEA;IACE,mBAAmB;IACnB,WAAW;EACb;;EAEA;IACE,0BAA0B;IAC1B,8BAA8B;EAChC;;EAEA;IACE,aAAa;EACf;;EAEA;IACE,sBAAsB;IACtB,uBAAuB;IACvB,cAAc;EAChB;;EAEA;IACE,sBAAsB;IACtB,uBAAuB;IACvB,cAAc;EAChB;;EAEA;;;;IAIE,yBAAyB;IACzB,sBAAsB;EACxB;;EAEA;;IAEE,0BAA0B;IAC1B,4BAA4B;EAC9B;;EAEA;;IAEE,0BAA0B;EAC5B;;EAEA;;IAEE,2BAA2B;IAC3B,8BAA8B;EAChC;;EAEA;;;;IAIE,2BAA2B;IAC3B,wBAAwB;EAC1B;;EAEA;;;;;;;;;;;IAWE,8BAA8B;IAC9B,wBAAwB;EAC1B;;EAEA;;;;IAIE,iCAAiC;IACjC,+BAA+B;EACjC;;EAEA;IACE,sBAAsB;IACtB,uBAAuB;EACzB;;EAEA;;;IAGE,4BAA4B;EAC9B;;EAEA;;;;;;;;;IASE,WAAW;IACX,wBAAwB;IACxB,iCAAiC;IACjC,+BAA+B;IAC/B,yBAAyB;EAC3B;;EAEA;;;;;IAKE,WAAW;EACb;;EAEA;IACE,kCAAkC;EACpC;;EAEA;;;;;IAKE,wBAAwB;IACxB,4BAA4B;EAC9B;;EAEA;IACE,4BAA4B;EAC9B;;EAEA;;;IAGE,wBAAwB;IACxB,4BAA4B;EAC9B;;EAEA;IACE,wBAAwB;EAC1B;;EAEA;;;IAGE,2BAA2B;EAC7B;;EAEA;;;;IAIE,oCAAoC;IACpC,2BAA2B;EAC7B;AACF;;AAEA;EACE;;IAEE,0BAA0B;EAC5B;;EAEA;IACE,0BAA0B;EAC5B;;EAEA;;;;;IAKE,0BAA0B;EAC5B;AACF;;AAEA;;+EAE+E;;AAE/E;EACE,2BAA2B;EAC3B,6BAA6B;EAC7B;;6EAE2E;EAC3E,4DAA4D;EAC5D,uDAAuD;AACzD;;AAEA;;EAEE,wBAAwB;EACxB,8BAA8B;AAChC;;AAEA;EACE,yBAAyB;EACzB,2BAA2B;EAC3B,kCAAkC;AACpC;;AAEA;EACE,2CAA2C;AAC7C;;AAEA;EACE,qBAAqB;EACrB,gDAAgD;EAChD,qDAAqD;EACrD,2BAA2B;EAC3B,2DAA2D;AAC7D;;AAEA;EACE,sBAAsB;EACtB,uBAAuB;EACvB,yBAAyB;EACzB,8BAA8B;EAC9B,iCAAiC;AACnC;;AAEA,2BAA2B;AAC3B;;EAEE,gDAAgD;EAChD,yBAAyB;EACzB,sDAAsD;EACtD,sDAAsD;AACxD;;AAEA;EACE,gDAAgD;EAChD,sDAAsD;EACtD,sDAAsD;AACxD;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,yBAAyB;AAC3B;;AAEA;EACE,+CAA+C;EAC/C,yBAAyB;AAC3B;;AAEA,6BAA6B;AAC7B;EACE,gDAAgD;EAChD,sDAAsD;AACxD;;AAEA;EACE,8BAA8B;AAChC;;AAEA;EACE;IACE,6BAA6B;EAC/B;;EAEA;IACE,wBAAwB;EAC1B;AACF;;;AAGA;;;+EAG+E;;AAE/E;;;;EAIE,uBAAuB;EACvB,wBAAwB;EACxB,yBAAyB;EACzB,6BAA6B;EAC7B,wBAAwB;EACxB,uBAAuB;AACzB;;AAEA,qCAAqC;AACrC;;;;;;;;;;;;EAYE,qCAAqC;AACvC;;AAEA,+CAA+C;AAC/C;;;;;;;;;;;;EAYE,sCAAsC;AACxC;;AAEA,+CAA+C;AAC/C;;;;;;;;;;EAUE,qCAAqC;AACvC;;AAEA,yDAAyD;AACzD;;;;;;;;;;;;;;EAcE,gDAAgD;EAChD,qCAAqC;EACrC,kDAAkD;AACpD;;AAEA,6CAA6C;AAC7C;;;;;;;;;;;;;;EAcE,yBAAyB;AAC3B;;AAEA,2CAA2C;AAC3C;;;;;;;;;;;;;;;;EAgBE,yBAAyB;AAC3B;;AAEA,4DAA4D;AAC5D;;;;EAIE,yBAAyB;AAC3B;;AAEA;EACE,gBAAgB;EAChB,eAAe;EACf,aAAa;EACb,sBAAsB;EACtB,yBAAyB;AAC3B","sourcesContent":["/* ==========================================================================\n   MedGen / Medical Assistant — App.css\n   Enterprise Research Theme v2\n   --------------------------------------------------------------------------\n   Goal:\n   - One central theme using CSS variables.\n   - Compact, enterprise-table UI inspired by investigation/research dashboards.\n   - No hard-coded green dependency. Change --primary / --primary-dark once.\n   - Keeps existing class names used across your React app.\n   ========================================================================== */\n\n/* Fonts: system enterprise stack. No external dependency, sharper rendering on Mac/Windows/Linux. */\n\n:root {\n  /* Brand palette — change these 6 values to re-skin the full app */\n  --primary: #0f1876;\n  --primary-600: #0d9488;\n  --primary-700: #290f76;\n  --primary-800: #115e59;\n  --primary-900: #134e4a;\n  --primary-soft: rgba(15, 118, 110, 0.11);\n\n  /* Neutral UI */\n  --bg: #eef1f4;\n  --bg-page: #f4f6f8;\n  --bg-elevated: #ffffff;\n  --bg-soft: #f7f9fb;\n  --surface: #ffffff;\n  --surface-strong: #ffffff;\n  --surface-soft: #f8fafc;\n  --surface-muted: #eef2f6;\n  --surface-dark: #111827;\n\n  /* Text */\n  --heading: #0f172a;\n  --text: #1f2937;\n  --text-soft: #64748b;\n  --text-muted: #8492a6;\n  --text-inverse: #ffffff;\n\n  /* Borders / shadows */\n  --border: #d7dee8;\n  --border-strong: #b8c3d1;\n  --grid-line: rgba(100, 116, 139, 0.16);\n  --shadow-xs: 0 1px 2px rgba(15, 23, 42, 0.06);\n  --shadow-sm: 0 4px 12px rgba(15, 23, 42, 0.07);\n  --shadow-md: 0 10px 26px rgba(15, 23, 42, 0.10);\n  --shadow-lg: 0 20px 48px rgba(15, 23, 42, 0.16);\n\n  /* Status */\n  --success: #16a34a;\n  --success-soft: #dcfce7;\n  --warning: #d97706;\n  --warning-soft: #fef3c7;\n  --danger: #dc2626;\n  --danger-soft: #fee2e2;\n  --info: #2563eb;\n  --info-soft: #dbeafe;\n\n  /* Layout */\n  --max-width: 100%;\n  --topbar-height: 74px;\n  --radius-xs: 6px;\n  --radius-sm: 8px;\n  --radius-md: 10px;\n  --radius-lg: 14px;\n  --radius-xl: 18px;\n  --radius-2xl: 24px;\n\n  /* Typography */\n  --font-main: \"Inter\", \"Noto Sans Devanagari\", Arial, Helvetica, sans-serif;\n  --fs-xs: 11px;\n  --fs-sm: 12px;\n  --fs-md: 13px;\n  --fs-base: 14px;\n  --fs-lg: 16px;\n  --fs-xl: 20px;\n  --fs-2xl: 26px;\n  --fs-3xl: 32px;\n\n  /* Tables */\n  --table-header-bg: var(--primary-800);\n  --table-header-text: #ffffff;\n  --table-row-bg: #ffffff;\n  --table-row-alt: #f8fafc;\n  --table-row-hover: #eef7f6;\n  --table-cell-border: #cfd8e3;\n}\n\n/* Optional dark mode still supported */\nhtml[data-theme=\"dark\"] {\n  --primary: #14b8a6;\n  --primary-600: #2dd4bf;\n  --primary-700: #14b8a6;\n  --primary-800: #0f766e;\n  --primary-900: #134e4a;\n  --primary-soft: rgba(45, 212, 191, 0.13);\n\n  --bg: #0b1020;\n  --bg-page: #0f172a;\n  --bg-elevated: #111827;\n  --bg-soft: #111827;\n  --surface: #121a2c;\n  --surface-strong: #111827;\n  --surface-soft: #182235;\n  --surface-muted: #1f2937;\n  --surface-dark: #030712;\n\n  --heading: #f8fafc;\n  --text: #e5e7eb;\n  --text-soft: #a7b0c0;\n  --text-muted: #7d8aa1;\n  --border: rgba(148, 163, 184, 0.22);\n  --border-strong: rgba(203, 213, 225, 0.30);\n  --table-row-bg: #111827;\n  --table-row-alt: #0f172a;\n  --table-row-hover: #143b3a;\n  --table-cell-border: rgba(148, 163, 184, 0.24);\n}\n\n/* ==========================================================================\n   Base reset\n   ========================================================================== */\n\n*,\n*::before,\n*::after {\n  box-sizing: border-box;\n  min-width: 0;\n}\n\nhtml,\nbody,\n#root {\n  margin: 0;\n  min-height: 100%;\n  width: 100%;\n}\n\nhtml,\nbody {\n  max-width: 100%;\n  overflow-x: hidden;\n}\n\nbody {\n  font-family: var(--font-main);\n  font-size: var(--fs-base);\n  line-height: 1.45;\n  color: var(--text);\n  background:\n    radial-gradient(circle at 4% 0%, rgba(15, 118, 110, 0.08), transparent 26%),\n    linear-gradient(180deg, #f8fafc 0%, var(--bg-page) 100%);\n  -webkit-font-smoothing: antialiased;\n  -moz-osx-font-smoothing: grayscale;\n}\n\nhtml[data-theme=\"dark\"] body {\n  background:\n    radial-gradient(circle at 8% 0%, rgba(20, 184, 166, 0.12), transparent 28%),\n    linear-gradient(180deg, #111827 0%, var(--bg-page) 100%);\n}\n\nbutton,\ninput,\nselect,\ntextarea {\n  font-family: inherit;\n}\n\nbutton {\n  border: 0;\n  cursor: pointer;\n}\n\nbutton:disabled,\ninput:disabled,\nselect:disabled,\ntextarea:disabled {\n  opacity: 0.58;\n  cursor: not-allowed;\n}\n\na {\n  color: inherit;\n}\n\nh1,\nh2,\nh3,\nh4,\nh5,\nh6,\np {\n  margin-top: 0;\n}\n\nh1,\nh2,\nh3,\nh4,\nh5,\nh6 {\n  color: var(--heading);\n  letter-spacing: -0.025em;\n}\n\np {\n  color: var(--text-soft);\n}\n\nstrong {\n  color: var(--heading);\n}\n\nsmall {\n  color: var(--text-soft);\n}\n\n/* ==========================================================================\n   Global form controls\n   ========================================================================== */\n\ninput,\nselect,\ntextarea,\n.research-input,\n.filter-input,\n.filter-select {\n  width: 100%;\n  min-height: 38px;\n  padding: 9px 11px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  background: var(--surface-strong);\n  color: var(--text);\n  font-size: var(--fs-md);\n  font-weight: 500;\n  outline: none;\n  transition: border-color 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;\n}\n\ntextarea {\n  min-height: 92px;\n  resize: vertical;\n}\n\ninput::placeholder,\ntextarea::placeholder {\n  color: var(--text-muted);\n}\n\ninput:focus,\nselect:focus,\ntextarea:focus,\n.research-input:focus,\n.filter-input:focus,\n.filter-select:focus {\n  border-color: var(--primary-600);\n  box-shadow: 0 0 0 3px var(--primary-soft);\n}\n\n/* ==========================================================================\n   Buttons\n   ========================================================================== */\n\n.btn,\n.research-primary,\n.impact-next-btn,\n.impact-secondary-btn,\n.ask-llm-btn,\n.admin-primary-btn,\n.admin-filter-card button,\n.admin-pagination button,\n.admin-modal-actions button,\n.admin-actions button,\n.chemical-search-bar button,\n.chemical-pagination button,\n.bio-library-search button,\n.selected-drugs-open-btn,\n.face-actions button,\n.avatar-test-actions button,\n.molecular-studio-actions button,\n.studio-preview-actions button,\n.studio-library-open-btn {\n  min-height: 36px;\n  padding: 8px 13px;\n  border-radius: var(--radius-sm);\n  font-size: var(--fs-md);\n  font-weight: 700;\n  letter-spacing: 0;\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  gap: 7px;\n  transition: background 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.12s ease, box-shadow 0.16s ease;\n}\n\n.btn:hover,\n.research-primary:hover,\n.admin-primary-btn:hover,\n.chemical-search-bar button:hover,\n.bio-library-search button:hover {\n  transform: translateY(-1px);\n}\n\n.btn-primary,\n.research-primary,\n.impact-next-btn,\n.admin-primary-btn,\n.chemical-search-bar button,\n.chemical-pagination button,\n.bio-library-search button,\n.avatar-test-actions button.primary,\n.molecular-studio-actions button.primary,\n.studio-preview-actions button.primary {\n  color: var(--text-inverse);\n  background: var(--primary-800);\n  box-shadow: var(--shadow-xs);\n}\n\n.btn-primary:hover,\n.research-primary:hover,\n.impact-next-btn:hover,\n.admin-primary-btn:hover {\n  background: var(--primary-900);\n}\n\n.btn-secondary,\n.impact-secondary-btn,\n.chemical-search-bar button.secondary {\n  color: var(--heading);\n  background: var(--surface-muted);\n  border: 1px solid var(--border);\n}\n\n.btn-secondary:hover,\n.impact-secondary-btn:hover {\n  background: #e7edf4;\n}\n\n.btn-ghost {\n  color: var(--text);\n  background: transparent;\n  border: 1px solid var(--border);\n}\n\n.btn-block {\n  width: 100%;\n}\n\n.ask-llm-btn {\n  color: white;\n  background: #111827;\n}\n\n/* ==========================================================================\n   App shell / topbar / nav\n   ========================================================================== */\n\n.app-shell {\n  min-height: 100vh;\n}\n\n.app,\n.app-shell,\n.dashboard-shell,\n.session-page,\n.main-content,\n.app-layout-main {\n  width: 100%;\n  max-width: 100vw;\n}\n\n.app-layout-shell {\n  width: 100%;\n  margin: 0;\n  padding: 10px 18px 24px;\n}\n\n.topbar {\n  width: 100%;\n  max-width: var(--max-width);\n  min-height: var(--topbar-height);\n  margin: 0;\n  padding: 10px 18px;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 16px;\n  position: relative;\n  z-index: 130;\n  background: transparent;\n}\n\n.topbar-left,\n.topbar-right {\n  display: flex;\n  align-items: center;\n  gap: 12px;\n}\n\n.brand-mark {\n  width: 42px;\n  height: 42px;\n  border-radius: var(--radius-md);\n  display: grid;\n  place-items: center;\n  color: white;\n  background: var(--primary-800);\n  font-weight: 800;\n  font-size: var(--fs-sm);\n  box-shadow: var(--shadow-sm);\n}\n\n.topbar-title {\n  margin: 0;\n  font-size: 22px;\n  line-height: 1.05;\n  font-weight: 800;\n  color: var(--heading);\n}\n\n.topbar-subtitle {\n  margin: 3px 0 0;\n  color: var(--text-soft);\n  font-size: var(--fs-sm);\n}\n\n.hamburger-btn {\n  width: 36px;\n  height: 36px;\n  border-radius: var(--radius-sm);\n  border: 1px solid var(--border);\n  background: var(--surface);\n  display: grid;\n  place-items: center;\n  gap: 3px;\n  padding: 8px;\n  flex: 0 0 auto;\n}\n\n.hamburger-btn span {\n  display: block;\n  width: 17px;\n  height: 2px;\n  border-radius: 999px;\n  background: var(--text);\n  transition: transform 0.22s ease, opacity 0.22s ease;\n}\n\n.hamburger-btn.open span:nth-child(1) {\n  transform: translateY(5px) rotate(45deg);\n}\n\n.hamburger-btn.open span:nth-child(2) {\n  opacity: 0;\n}\n\n.hamburger-btn.open span:nth-child(3) {\n  transform: translateY(-5px) rotate(-45deg);\n}\n\n.user-chip {\n  display: flex;\n  align-items: center;\n  gap: 9px;\n  min-height: 40px;\n  padding: 6px 10px;\n  border-radius: 999px;\n  border: 1px solid var(--border);\n  background: var(--surface);\n  box-shadow: var(--shadow-xs);\n}\n\n.user-avatar,\n.patient-avatar {\n  display: grid;\n  place-items: center;\n  font-weight: 800;\n}\n\n.user-avatar {\n  width: 30px;\n  height: 30px;\n  border-radius: 50%;\n  color: var(--primary-900);\n  background: var(--primary-soft);\n  font-size: var(--fs-xs);\n}\n\n.user-name {\n  font-size: var(--fs-sm);\n  font-weight: 800;\n  color: var(--heading);\n}\n\n.user-role {\n  font-size: 10px;\n  color: var(--text-soft);\n  text-transform: capitalize;\n}\n\n.theme-toggle {\n  min-width: 128px;\n}\n\n/* Sidebar / drawers */\n.desktop-sidebar-backdrop,\n.mobile-drawer-backdrop {\n  position: fixed;\n  inset: 0;\n  background: rgba(15, 23, 42, 0.34);\n  opacity: 0;\n  pointer-events: none;\n  transition: opacity 0.18s ease;\n  z-index: 110;\n}\n\n.desktop-sidebar-backdrop.show,\n.mobile-drawer-backdrop.show {\n  opacity: 1;\n  pointer-events: auto;\n}\n\n.desktop-sidebar-panel {\n  position: fixed;\n  top: 78px;\n  left: 18px;\n  width: 250px;\n  z-index: 120;\n  transform: translateX(-115%);\n  transition: transform 0.22s ease;\n}\n\n.desktop-sidebar-panel.open {\n  transform: translateX(0);\n}\n\n.mobile-drawer {\n  position: fixed;\n  top: 0;\n  left: 0;\n  width: min(320px, 86vw);\n  height: 100vh;\n  padding: 16px;\n  background: var(--surface);\n  border-right: 1px solid var(--border);\n  box-shadow: var(--shadow-lg);\n  transform: translateX(-100%);\n  transition: transform 0.22s ease;\n  z-index: 1000;\n  overflow-y: auto;\n}\n\n.mobile-drawer.open {\n  transform: translateX(0);\n}\n\n.mobile-drawer-header {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  gap: 12px;\n  margin-bottom: 14px;\n}\n\n.app-sidebar {\n  width: 100%;\n}\n\n.app-sidebar-inner {\n  padding: 14px;\n  border-radius: var(--radius-lg);\n  border: 1px solid var(--border);\n  background: var(--surface);\n  box-shadow: var(--shadow-md);\n}\n\n.app-sidebar-title,\n.sidebar-section-title {\n  margin-bottom: 10px;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  font-weight: 800;\n  text-transform: uppercase;\n  letter-spacing: 0.12em;\n}\n\n.app-sidebar-nav {\n  display: grid;\n  gap: 6px;\n}\n\n.app-nav-link {\n  display: flex;\n  align-items: center;\n  min-height: 36px;\n  padding: 8px 10px;\n  border-radius: var(--radius-sm);\n  text-decoration: none;\n  color: var(--text);\n  background: transparent;\n  border: 1px solid transparent;\n  font-size: var(--fs-md);\n  font-weight: 700;\n  white-space: nowrap;\n  overflow: hidden;\n}\n\n.app-nav-link:hover {\n  background: var(--surface-soft);\n  border-color: var(--border);\n}\n\n.app-nav-link.active {\n  color: white;\n  background: var(--primary-800);\n}\n\n/* ==========================================================================\n   Generic cards / grids\n   ========================================================================== */\n\n.card,\n.auth-card,\n.wizard-modal,\n.pdf-modal,\n.sidebar-card,\n.brain-canvas-card,\n.brain-side-card,\n.brain-selected-panel,\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card,\n.brain-page-meta-card,\n.brain-source-card,\n.settings-section-card,\n.reviews-queue-card,\n.reviews-detail-card,\n.research-card,\n.admin-stat-card,\n.admin-table-card,\n.voice-chat-card {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  box-shadow: var(--shadow-sm);\n}\n\n.card,\n.sidebar-card {\n  padding: 16px;\n}\n\n.dark-card {\n  color: #eef2ff;\n  background: var(--surface-dark);\n  border-color: rgba(148, 163, 184, 0.24);\n}\n\n.dark-card h1,\n.dark-card h2,\n.dark-card h3,\n.dark-card h4,\n.dark-card strong,\n.dark-card span {\n  color: #f8fafc;\n}\n\n.dark-card p,\n.dark-card small,\n.dark-card .muted,\n.dark-muted {\n  color: rgba(226, 232, 240, 0.78);\n}\n\n.subtitle,\n.muted {\n  color: var(--text-soft);\n}\n\n.eyebrow,\n.research-kicker {\n  display: inline-flex;\n  align-items: center;\n  gap: 7px;\n  margin-bottom: 6px;\n  font-size: var(--fs-xs);\n  font-weight: 800;\n  letter-spacing: 0.12em;\n  text-transform: uppercase;\n  color: var(--primary-800);\n}\n\n.section-head,\n.page-header,\n.research-card-head,\n.patient-card-top,\n.patient-card-bottom,\n.session-card-head,\n.review-actions,\n.pdf-modal-header,\n.pdf-modal-actions,\n.wizard-header,\n.wizard-footer,\n.session-top-actions {\n  display: flex;\n  align-items: flex-start;\n  justify-content: space-between;\n  gap: 12px;\n}\n\n.section-head {\n  margin-bottom: 12px;\n}\n\n.section-head h2,\n.research-card h2 {\n  margin: 0 0 4px;\n  font-size: var(--fs-xl);\n  font-weight: 800;\n}\n\n.dashboard-shell {\n  width: 100%;\n  margin: 0;\n  padding: 18px 0 0;\n  display: grid;\n  grid-template-columns: 260px 1fr;\n  gap: 16px;\n}\n\n.single-page-shell {\n  grid-template-columns: 1fr;\n}\n\n.dashboard-sidebar,\n.dashboard-main,\n.full-width-main,\n.research-left,\n.research-right,\n.relation-graph-side,\n.relation-detail-stack,\n.settings-grid,\n.settings-actions-stack,\n.brain-side-stack {\n  min-width: 0;\n}\n\n.dashboard-sidebar,\n.stats-stack,\n.sidebar-actions,\n.status-stack,\n.relation-graph-side,\n.relation-detail-stack,\n.settings-actions-stack,\n.brain-side-stack {\n  display: grid;\n  gap: 12px;\n}\n\n.metric-grid,\n.summary-grid,\n.analysis-grid,\n.question-grid,\n.session-top-grid,\n.compact-summary-grid,\n.page-stats-grid {\n  display: grid;\n  gap: 12px;\n}\n\n.metric-grid {\n  grid-template-columns: repeat(4, minmax(0, 1fr));\n}\n\n.metric-grid-compact {\n  grid-template-columns: repeat(3, minmax(0, 1fr));\n}\n\n.summary-grid,\n.compact-summary-grid,\n.analysis-grid,\n.question-grid,\n.two-col,\n.session-top-grid {\n  grid-template-columns: repeat(2, minmax(0, 1fr));\n}\n\n.stat-tile,\n.status-item,\n.summary-item,\n.metric-card,\n.analysis-block,\n.analysis-summary-card,\n.analysis-doctor-note,\n.empty-state-box {\n  padding: 13px;\n  border: 1px solid var(--border);\n  background: var(--surface-soft);\n  border-radius: var(--radius-md);\n}\n\n.stat-tile span,\n.status-item span,\n.summary-item span,\n.metric-card span {\n  display: block;\n  margin-bottom: 5px;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  text-transform: uppercase;\n  letter-spacing: 0.06em;\n}\n\n.stat-tile strong,\n.status-item strong,\n.summary-item strong,\n.metric-card strong {\n  font-size: var(--fs-md);\n}\n\n.metric-card-primary,\n.metric-card-blue,\n.metric-card-pink,\n.metric-card-green {\n  color: white;\n  background: var(--primary-800);\n  border-color: transparent;\n}\n\n.metric-card-primary *,\n.metric-card-blue *,\n.metric-card-pink *,\n.metric-card-green * {\n  color: white !important;\n}\n\n/* ==========================================================================\n   Research page main structure\n   ========================================================================== */\n\n.research-page {\n  padding: 18px;\n  color: var(--text);\n}\n\n.research-hero {\n  display: flex;\n  justify-content: space-between;\n  gap: 18px;\n  align-items: stretch;\n  margin-bottom: 16px;\n  padding: 18px;\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-sm);\n}\n\n.research-hero-pro {\n  position: relative;\n  overflow: hidden;\n}\n\n.research-hero-pro::after {\n  content: \"\";\n  position: absolute;\n  right: 260px;\n  top: -90px;\n  width: 230px;\n  height: 230px;\n  border-radius: 50%;\n  background: radial-gradient(circle, var(--primary-soft), transparent 70%);\n  pointer-events: none;\n}\n\n.research-hero h1 {\n  margin: 0;\n  font-size: var(--fs-2xl);\n  line-height: 1.1;\n  font-weight: 850;\n}\n\n.research-hero p {\n  margin: 5px 0 0;\n  color: var(--text-soft);\n  font-size: var(--fs-md);\n}\n\n.research-warning {\n  min-width: 250px;\n  padding: 15px;\n  border-radius: var(--radius-md);\n  background: #111827;\n  color: white;\n  display: flex;\n  flex-direction: column;\n  justify-content: center;\n}\n\n.research-warning strong {\n  color: white;\n}\n\n.research-warning span {\n  color: #cbd5e1;\n  margin-top: 6px;\n  font-size: var(--fs-sm);\n}\n\n.research-mini-stats {\n  display: flex;\n  gap: 8px;\n  margin-top: 12px;\n  flex-wrap: wrap;\n}\n\n.research-mini-stats span,\n.pill,\n.saved-pill,\n.selected-patient-chip,\n.bmi-chip {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-height: 24px;\n  padding: 5px 8px;\n  border-radius: 999px;\n  font-size: var(--fs-xs);\n  font-weight: 800;\n  background: var(--primary-soft);\n  color: var(--primary-900);\n  border: 1px solid rgba(15, 118, 110, 0.16);\n}\n\n.pill.soft,\n.saved-pill,\n.selected-patient-chip {\n  background: var(--success-soft);\n  color: #166534;\n  border-color: #bbf7d0;\n}\n\n.pill.warning {\n  background: var(--warning-soft);\n  color: #92400e;\n}\n\n.pill.danger {\n  background: var(--danger-soft);\n  color: #991b1b;\n}\n\n.pill.neutral {\n  background: var(--surface-muted);\n  color: var(--text-soft);\n  border-color: var(--border);\n}\n\n.research-tabs {\n  display: flex;\n  gap: 8px;\n  margin-bottom: 16px;\n  flex-wrap: wrap;\n}\n\n.research-tabs button,\n.session-tab,\n.wizard-step,\n.bio-library-tabs button,\n.bio-workbench-mode-tabs button,\n.impact-step-tabs button,\n.chemical-az-filter button {\n  min-height: 34px;\n  padding: 7px 12px;\n  border-radius: var(--radius-sm);\n  border: 1px solid var(--border);\n  background: var(--surface-muted);\n  color: var(--text);\n  font-size: var(--fs-md);\n  font-weight: 750;\n}\n\n.research-tabs button.active,\n.session-tab.active,\n.wizard-step.active,\n.bio-library-tabs button.active,\n.bio-workbench-mode-tabs button.active,\n.impact-step-tabs button.active,\n.chemical-az-filter button.active {\n  color: white;\n  background: var(--primary-800);\n  border-color: var(--primary-800);\n  box-shadow: none;\n}\n\n.research-grid,\n.research-layout-row {\n  display: grid;\n  grid-template-columns: minmax(0, 1fr) 340px;\n  gap: 16px;\n  align-items: start;\n}\n\n.research-left {\n  display: flex;\n  flex-direction: column;\n  gap: 16px;\n}\n\n.research-right,\n.patient-context-pro,\n.sticky-panel {\n  position: sticky;\n  top: 14px;\n}\n\n.research-card {\n  padding: 16px;\n}\n\n.research-card.wide,\n.result-card {\n  grid-column: 1 / -1;\n}\n\n.research-card-head span {\n  background: var(--primary-soft);\n  color: var(--primary-900);\n  border: 1px solid rgba(15, 118, 110, 0.15);\n  height: fit-content;\n  border-radius: 999px;\n  padding: 6px 9px;\n  font-weight: 800;\n  font-size: var(--fs-xs);\n}\n\n.compact-builder {\n  min-height: 320px;\n}\n\n.simulation-canvas-card {\n  min-height: 520px;\n}\n\n.result-card-pro {\n  margin-top: 0;\n  background: var(--surface);\n}\n\n/* ==========================================================================\n   Tables — app-wide normalized look\n   ========================================================================== */\n\ntable {\n  border-collapse: collapse;\n}\n\n.admin-table-card,\n.organ-impact-table-wrap,\n.impact-table-wrap {\n  width: 100%;\n  overflow-x: auto;\n  border-radius: var(--radius-sm);\n  border: 1px solid var(--table-cell-border);\n  background: var(--surface);\n}\n\n.admin-table,\n.organ-impact-table,\n.impact-table {\n  width: 100%;\n  min-width: 900px;\n  border-collapse: collapse;\n  table-layout: fixed;\n  background: var(--surface);\n}\n\n.admin-table th,\n.organ-impact-table th,\n.impact-table th {\n  height: 34px;\n  padding: 7px 9px;\n  text-align: left;\n  background: var(--table-header-bg);\n  color: var(--table-header-text);\n  border-right: 1px solid rgba(255, 255, 255, 0.22);\n  border-bottom: 1px solid var(--primary-900);\n  font-size: 11px;\n  font-weight: 750;\n  letter-spacing: 0;\n  text-transform: none;\n  vertical-align: middle;\n}\n\n.admin-table td,\n.organ-impact-table td,\n.impact-table td {\n  min-height: 38px;\n  padding: 8px 9px;\n  vertical-align: top;\n  border-right: 1px solid var(--table-cell-border);\n  border-bottom: 1px solid var(--table-cell-border);\n  color: var(--text);\n  font-size: var(--fs-sm);\n  line-height: 1.35;\n  background: var(--table-row-bg);\n}\n\n.admin-table tbody tr:nth-child(even) td,\n.organ-impact-table tbody tr:nth-child(even) td,\n.impact-table tbody tr:nth-child(even) td {\n  background: var(--table-row-alt);\n}\n\n.admin-table tbody tr:hover td,\n.organ-impact-table tbody tr:hover td,\n.impact-table tbody tr:hover td {\n  background: var(--table-row-hover);\n}\n\n.admin-table td strong,\n.admin-table td small {\n  display: block;\n}\n\n.admin-table td small {\n  margin-top: 3px;\n  color: var(--text-soft);\n}\n\n.organ-impact-table-card {\n  margin-top: 16px;\n  padding: 14px;\n  border-radius: var(--radius-lg);\n  background: var(--surface);\n  border: 1px solid var(--border);\n  box-shadow: var(--shadow-sm);\n}\n\n.organ-impact-table-head {\n  display: flex;\n  justify-content: space-between;\n  align-items: flex-start;\n  gap: 12px;\n  margin-bottom: 10px;\n}\n\n.organ-impact-table-head h3 {\n  margin: 0;\n  color: var(--heading);\n  font-size: var(--fs-lg);\n}\n\n.organ-impact-table-head p {\n  margin: 3px 0 0;\n  color: var(--text-soft);\n  font-size: var(--fs-sm);\n}\n\n.organ-cell-name {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n}\n\n.organ-cell-name > span {\n  width: 8px;\n  height: 30px;\n  border-radius: 999px;\n  flex: 0 0 auto;\n}\n\n.organ-cell-name strong,\n.organ-cell-name small {\n  display: block;\n}\n\n.organ-cell-name strong {\n  color: var(--heading);\n  font-size: var(--fs-sm);\n}\n\n.organ-cell-name small {\n  margin-top: 2px;\n  color: var(--text-soft);\n  font-size: 10px;\n  font-weight: 700;\n}\n\n.organ-risk-chip {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-width: 70px;\n  padding: 4px 7px;\n  border-radius: 999px;\n  border: 1px solid currentColor;\n  font-size: 10px;\n  font-weight: 850;\n  text-transform: uppercase;\n}\n\n.organ-score-cell {\n  display: grid;\n  grid-template-columns: minmax(110px, 1fr) 42px;\n  gap: 8px;\n  align-items: center;\n}\n\n.organ-score-track,\n.organ-bar,\n.score-meter {\n  overflow: hidden;\n  border-radius: 999px;\n  background: #e2e8f0;\n}\n\n.organ-score-track {\n  height: 8px;\n}\n\n.organ-score-track i,\n.organ-bar div,\n.score-meter div {\n  display: block;\n  height: 100%;\n  border-radius: 999px;\n}\n\n.score-meter {\n  height: 10px;\n  margin-bottom: 14px;\n}\n\n.score-meter div {\n  background: var(--primary-800);\n}\n\n.organ-score-cell b {\n  font-size: var(--fs-xs);\n  color: var(--heading);\n}\n\n.organ-inline-meta,\n.organ-row-reason {\n  display: block;\n  color: var(--text-soft);\n  font-size: var(--fs-sm);\n  line-height: 1.4;\n}\n\n.organ-inline-meta {\n  font-weight: 750;\n  color: var(--text);\n  text-transform: capitalize;\n}\n\n/* ==========================================================================\n   Admin pages\n   ========================================================================== */\n\n.admin-page {\n  padding: 18px;\n  color: var(--text);\n}\n\n.admin-page-header {\n  display: flex;\n  justify-content: space-between;\n  gap: 16px;\n  align-items: flex-start;\n  margin-bottom: 16px;\n}\n\n.admin-page-header h1 {\n  margin: 0;\n  font-size: var(--fs-2xl);\n  font-weight: 850;\n}\n\n.admin-page-header p {\n  margin-top: 5px;\n  color: var(--text-soft);\n}\n\n.admin-stats-grid {\n  display: grid;\n  grid-template-columns: repeat(3, minmax(160px, 1fr));\n  gap: 12px;\n  margin-bottom: 14px;\n}\n\n.admin-stat-card {\n  padding: 14px;\n}\n\n.admin-stat-card span {\n  display: block;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  margin-bottom: 5px;\n}\n\n.admin-stat-card strong {\n  font-size: var(--fs-2xl);\n}\n\n.admin-filter-card {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  padding: 12px;\n  display: grid;\n  grid-template-columns: 1fr 160px 200px 100px;\n  gap: 10px;\n  margin-bottom: 14px;\n}\n\n.admin-filter-card button {\n  color: white;\n  background: #111827;\n}\n\n.admin-error {\n  background: var(--danger-soft);\n  color: #991b1b;\n  border-radius: var(--radius-sm);\n  padding: 10px 12px;\n  margin-bottom: 12px;\n}\n\n.admin-badge {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  min-height: 22px;\n  padding: 4px 8px;\n  border-radius: 999px;\n  font-size: var(--fs-xs);\n  font-weight: 800;\n  background: var(--surface-muted);\n  color: var(--text);\n}\n\n.admin-badge-green {\n  background: var(--success-soft);\n  color: #166534;\n}\n\n.admin-badge-yellow {\n  background: var(--warning-soft);\n  color: #92400e;\n}\n\n.admin-badge-red {\n  background: var(--danger-soft);\n  color: #991b1b;\n}\n\n.admin-badge-dark {\n  background: #111827;\n  color: white;\n}\n\n.admin-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 6px;\n}\n\n.admin-actions button,\n.admin-modal-actions button {\n  background: var(--surface-muted);\n  color: var(--heading);\n}\n\n.admin-actions .green,\n.admin-modal-actions .green {\n  background: var(--success);\n  color: white;\n}\n\n.admin-actions .red,\n.admin-modal-actions .red {\n  background: var(--danger);\n  color: white;\n}\n\n.admin-actions .orange,\n.admin-modal-actions .orange {\n  background: #f97316;\n  color: white;\n}\n\n.admin-actions .blue,\n.admin-modal-actions .blue {\n  background: var(--info);\n  color: white;\n}\n\n.admin-actions .dark,\n.admin-modal-actions .dark {\n  background: #111827;\n  color: white;\n}\n\n.admin-empty {\n  padding: 24px;\n  text-align: center;\n  color: var(--text-soft);\n}\n\n.admin-pagination {\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  gap: 10px;\n  margin-top: 14px;\n}\n\n.admin-modal-backdrop,\n.modal-backdrop,\n.pdf-backdrop,\n.selected-drugs-modal,\n.studio-picker-backdrop {\n  position: fixed;\n  inset: 0;\n  background: rgba(15, 23, 42, 0.52);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  z-index: 9998;\n  padding: 18px;\n  backdrop-filter: blur(8px);\n}\n\n.admin-modal,\n.wizard-modal,\n.pdf-modal,\n.selected-drugs-card,\n.studio-picker-modal {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-xl);\n  box-shadow: var(--shadow-lg);\n}\n\n.admin-modal {\n  width: 520px;\n  max-height: 90vh;\n  overflow-y: auto;\n  padding: 16px;\n}\n\n.admin-modal.large {\n  width: min(920px, 96vw);\n}\n\n.admin-modal-header,\n.selected-drugs-head,\n.studio-picker-head {\n  display: flex;\n  justify-content: space-between;\n  align-items: flex-start;\n  gap: 12px;\n  margin-bottom: 14px;\n}\n\n.admin-modal-header h2 {\n  margin: 0;\n  font-size: var(--fs-xl);\n}\n\n.admin-modal-header button,\n.selected-drugs-head button,\n.studio-picker-head > button {\n  width: 34px;\n  height: 34px;\n  border-radius: var(--radius-sm);\n  background: var(--surface-muted);\n  color: var(--heading);\n  font-weight: 800;\n}\n\n.admin-details-grid,\n.admin-form-grid {\n  display: grid;\n  grid-template-columns: repeat(2, minmax(220px, 1fr));\n  gap: 10px;\n}\n\n.admin-form-grid textarea {\n  min-height: 88px;\n  grid-column: span 2;\n}\n\n.admin-detail {\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  padding: 10px;\n}\n\n.admin-detail span {\n  display: block;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  margin-bottom: 4px;\n}\n\n.admin-detail strong {\n  word-break: break-word;\n}\n\n.admin-modal-actions {\n  display: flex;\n  justify-content: flex-end;\n  gap: 8px;\n  margin-top: 14px;\n}\n\n/* ==========================================================================\n   Chemical / bio library\n   ========================================================================== */\n\n.chemical-search-bar {\n  display: grid;\n  grid-template-columns: 1fr 88px 78px;\n  gap: 8px;\n  margin: 12px 0 10px;\n  align-items: center;\n}\n\n.chemical-search-bar input {\n  min-height: 38px;\n}\n\n.chemical-pagination-info {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  margin: 4px 0 10px;\n  padding: 8px 10px;\n  border-radius: var(--radius-sm);\n  background: var(--surface-soft);\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  font-weight: 800;\n}\n\n.chemical-grid,\n.bio-card-grid,\n.history-grid-pro,\n.queue-grid {\n  display: grid;\n  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));\n  gap: 10px;\n}\n\n.chemical-card,\n.bio-select-card,\n.history-tile,\n.queue-card,\n.library-item,\n.bio-library-item,\n.selected-drug-item {\n  padding: 12px;\n  border-radius: var(--radius-sm);\n  border: 1px solid var(--border);\n  background: var(--surface);\n  cursor: pointer;\n  overflow: hidden;\n  box-shadow: none;\n}\n\n.chemical-card {\n  min-height: 105px;\n}\n\n.chemical-card:hover,\n.bio-select-card:hover,\n.history-tile:hover,\n.bio-library-item:hover {\n  border-color: var(--primary-600);\n  box-shadow: var(--shadow-xs);\n}\n\n.chemical-card.selected,\n.bio-select-card.selected,\n.history-tile.active,\n.bio-library-item.active {\n  border-color: var(--primary-800);\n  background: var(--table-row-hover);\n  box-shadow: 0 0 0 2px var(--primary-soft);\n}\n\n.chemical-card-top {\n  display: flex;\n  align-items: flex-start;\n  justify-content: space-between;\n  gap: 8px;\n}\n\n.chemical-card-top strong,\n.bio-select-card strong,\n.bio-library-item strong,\n.history-tile strong,\n.queue-card strong {\n  display: block;\n  color: var(--heading);\n  font-size: var(--fs-md);\n  line-height: 1.25;\n  font-weight: 800;\n  overflow-wrap: anywhere;\n}\n\n.chemical-card-top span,\n.bio-library-item em {\n  flex-shrink: 0;\n  max-width: 92px;\n  padding: 4px 6px;\n  border-radius: 999px;\n  background: var(--surface-muted);\n  color: var(--text-soft);\n  font-size: 10px;\n  font-weight: 800;\n  line-height: 1;\n  white-space: normal;\n  overflow-wrap: anywhere;\n  text-align: center;\n}\n\n.chemical-card p,\n.bio-select-card p,\n.bio-select-card small,\n.bio-library-item span,\n.history-tile span,\n.queue-card span,\n.queue-card p,\n.queue-card small {\n  display: block;\n  margin: 7px 0 0;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  line-height: 1.35;\n  overflow-wrap: anywhere;\n}\n\n.chemical-pagination {\n  margin-top: 12px;\n  padding: 10px;\n  border-radius: var(--radius-sm);\n  background: var(--surface-soft);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  gap: 10px;\n}\n\n.chemical-pagination span {\n  min-width: 88px;\n  text-align: center;\n  font-weight: 800;\n  color: var(--text);\n  font-size: var(--fs-xs);\n}\n\n.chemical-az-filter {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 5px;\n  margin: 8px 0 10px;\n}\n\n.chemical-az-filter button {\n  width: 28px;\n  height: 28px;\n  padding: 0;\n}\n\n.bio-lab-library-page {\n  display: grid;\n  gap: 16px;\n}\n\n.bio-library-tabs {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n}\n\n.bio-library-search {\n  display: flex;\n  gap: 8px;\n  margin-bottom: 12px;\n}\n\n.bio-library-search input {\n  flex: 1;\n}\n\n.digital-avatar-reason-card,\n.llm-suggestion-box,\n.assist-result-box,\n.research-empty,\n.empty-studio-state {\n  border: 1px solid var(--border);\n  border-radius: var(--radius-md);\n  background: var(--surface-soft);\n  padding: 12px;\n}\n\n.digital-avatar-reason-card strong,\n.empty-studio-state strong {\n  display: block;\n  color: var(--heading);\n  margin-bottom: 5px;\n}\n\n.digital-avatar-reason-card p,\n.empty-studio-state span {\n  margin: 0;\n  color: var(--text-soft);\n  font-size: var(--fs-sm);\n}\n\n.bio-library-layout {\n  display: grid;\n  grid-template-columns: 320px minmax(0, 1fr);\n  gap: 14px;\n  align-items: start;\n}\n\n.bio-library-list {\n  max-height: 650px;\n  overflow-y: auto;\n  display: grid;\n  gap: 8px;\n  padding-right: 3px;\n}\n\n/* ==========================================================================\n   MedGen / impact panels\n   ========================================================================== */\n\n.impact-workbench {\n  max-width: 1180px;\n}\n\n.impact-workbench-header {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  padding: 16px;\n  margin-bottom: 12px;\n  box-shadow: var(--shadow-sm);\n}\n\n.impact-workbench-header h2 {\n  margin: 0;\n  font-size: var(--fs-xl);\n}\n\n.impact-workbench-header p {\n  color: var(--text-soft);\n  margin: 5px 0 0;\n}\n\n.impact-step-tabs {\n  display: flex;\n  gap: 8px;\n  margin-bottom: 12px;\n  flex-wrap: wrap;\n}\n\n.impact-step-card {\n  max-width: 980px;\n}\n\n.impact-pro-grid,\n.impact-grid {\n  display: grid;\n  grid-template-columns: repeat(2, minmax(240px, 1fr));\n  gap: 10px;\n}\n\n.impact-field label,\n.research-label,\n.dose-row label {\n  display: block;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  font-weight: 800;\n  margin-bottom: 5px;\n}\n\n.impact-field.wide,\n.impact-grid textarea {\n  grid-column: 1 / -1;\n}\n\n.impact-action-row {\n  display: flex;\n  justify-content: flex-end;\n  gap: 8px;\n  margin-top: 14px;\n}\n\n.impact-save-btn {\n  width: auto;\n  min-width: 190px;\n}\n\n.impact-validate-layout {\n  display: grid;\n  grid-template-columns: minmax(0, 1fr) 390px;\n  gap: 14px;\n  align-items: start;\n}\n\n.medgen-side-panel,\n.studio-medgen-console {\n  background: #0f172a;\n  color: white;\n  border-radius: var(--radius-lg);\n  padding: 14px;\n  min-height: 420px;\n  box-shadow: var(--shadow-sm);\n}\n\n.medgen-side-panel h3 {\n  margin: 0 0 6px;\n  font-size: var(--fs-lg);\n  color: white;\n}\n\n.medgen-side-panel p {\n  color: #cbd5e1;\n  line-height: 1.55;\n}\n\n.medgen-stream-box,\n.medgen-final-box {\n  margin-top: 12px;\n  background: rgba(255, 255, 255, 0.06);\n  border: 1px solid rgba(255, 255, 255, 0.12);\n  border-radius: var(--radius-sm);\n  padding: 10px;\n  color: #cbd5e1;\n  max-height: 360px;\n  overflow-y: auto;\n  font-size: var(--fs-sm);\n  line-height: 1.55;\n}\n\n.medgen-stream-box pre,\n.medgen-final-box pre,\n.analysis-stream-box pre {\n  white-space: pre-wrap;\n  margin: 0;\n  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;\n  font-size: var(--fs-sm);\n}\n\n.medgen-final-head {\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n  align-items: center;\n}\n\n/* ==========================================================================\n   HRA / body viewer / graph panels\n   ========================================================================== */\n\n.hra-body-viewer,\n.brain-canvas-wrap,\n.relation-graph-canvas-wrap,\n.avatar-3d-container {\n  width: 100%;\n  border-radius: var(--radius-lg);\n  overflow: hidden;\n  border: 1px solid var(--border);\n  position: relative;\n  background:\n    linear-gradient(90deg, rgba(148, 163, 184, 0.10) 1px, transparent 1px),\n    linear-gradient(rgba(148, 163, 184, 0.10) 1px, transparent 1px),\n    radial-gradient(circle at center, rgba(15, 118, 110, 0.12), transparent 42%),\n    #0b1020;\n  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%;\n}\n\n.hra-body-viewer {\n  height: 100%;\n  min-height: 720px;\n}\n\n.hra-result-layout {\n  display: grid;\n  grid-template-columns: minmax(390px, 0.9fr) minmax(520px, 1.25fr);\n  gap: 14px;\n  align-items: stretch;\n}\n\n.hra-result-layout > .hra-body-viewer {\n  height: 100%;\n  min-height: 720px;\n}\n\n.d3-organ-intel-panel {\n  min-height: 720px;\n}\n\n.hra-loading {\n  padding: 10px 12px;\n  border-radius: var(--radius-sm);\n  background: rgba(15, 23, 42, 0.92);\n  color: #e0f2fe;\n  font-weight: 800;\n  border: 1px solid rgba(125, 211, 252, 0.35);\n}\n\n.hra-face-mask-label,\n.hra-viewer-chip {\n  padding: 6px 9px;\n  border-radius: 999px;\n  border: 1px solid rgba(45, 212, 191, 0.35);\n  background: rgba(2, 6, 23, 0.74);\n  color: #ccfbf1;\n  font-size: 10px;\n  font-weight: 800;\n  letter-spacing: 0.04em;\n  text-align: center;\n  text-transform: uppercase;\n  backdrop-filter: blur(10px);\n  pointer-events: none;\n}\n\n.hra-viewer-chip {\n  position: absolute;\n  top: 10px;\n  left: 10px;\n  z-index: 5;\n}\n\n.brain-map-layout,\n.relation-graph-layout,\n.reviews-layout {\n  display: grid;\n  grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.75fr);\n  gap: 14px;\n}\n\n.brain-canvas-card {\n  padding: 14px;\n  min-height: 640px;\n}\n\n.brain-canvas-title-group,\n.brain-canvas-toolbar,\n.brain-chip-list,\n.relation-graph-meta,\n.brain-selected-head {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  flex-wrap: wrap;\n}\n\n.brain-canvas-wrap,\n.relation-graph-canvas-wrap {\n  min-height: 560px;\n}\n\n.relation-graph-svg {\n  width: 100%;\n  height: 560px;\n  display: block;\n}\n\n.brain-view-toggle {\n  display: flex;\n  gap: 6px;\n  align-items: center;\n}\n\n.brain-view-toggle button {\n  min-height: 30px;\n  border: 1px solid var(--border);\n  background: var(--surface);\n  color: var(--text);\n  border-radius: 999px;\n  padding: 6px 10px;\n  font-size: var(--fs-xs);\n  font-weight: 800;\n}\n\n.brain-view-toggle button.active {\n  background: var(--primary-800);\n  color: white;\n  border-color: var(--primary-800);\n}\n\n/* ==========================================================================\n   Molecular studio legacy classes\n   ========================================================================== */\n\n.bio-studio-page {\n  gap: 14px;\n}\n\n.bio-studio-shell {\n  overflow: hidden;\n}\n\n.bio-studio-hero {\n  display: grid;\n  grid-template-columns: minmax(0, 1fr) 190px;\n  gap: 14px;\n  align-items: stretch;\n  margin-bottom: 12px;\n  padding: 16px;\n  border-radius: var(--radius-lg);\n  background: var(--surface);\n  border: 1px solid var(--border);\n}\n\n.bio-studio-kicker,\n.compound-designer-head p,\n.molecular-studio-header p,\n.studio-picker-head p {\n  margin: 0 0 5px;\n  color: var(--primary-800);\n  font-size: var(--fs-xs);\n  font-weight: 850;\n  letter-spacing: 0.09em;\n  text-transform: uppercase;\n}\n\n.bio-studio-hero h2,\n.molecular-studio-header h2,\n.compound-designer-head h2,\n.studio-picker-head h2 {\n  margin: 0;\n  color: var(--heading);\n  font-size: clamp(22px, 3vw, 30px);\n  line-height: 1.1;\n}\n\n.bio-studio-hero span,\n.molecular-studio-header span,\n.compound-designer-head span,\n.studio-picker-head span {\n  display: block;\n  margin-top: 8px;\n  color: var(--text-soft);\n  line-height: 1.45;\n  font-size: var(--fs-md);\n}\n\n.bio-studio-metrics {\n  display: grid;\n  align-content: center;\n  padding: 14px;\n  border-radius: var(--radius-md);\n  background: #111827;\n  color: white;\n}\n\n.bio-studio-metrics strong {\n  font-size: var(--fs-2xl);\n  line-height: 1;\n  color: #99f6e4;\n}\n\n.bio-studio-metrics span {\n  margin-bottom: 10px;\n  color: #cbd5e1;\n  font-size: var(--fs-xs);\n  font-weight: 750;\n}\n\n.bio-workbench-mode-tabs {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n  margin: 10px 0 12px;\n  padding: 6px;\n  border-radius: var(--radius-md);\n  background: var(--surface-muted);\n  border: 1px solid var(--border);\n}\n\n.avatar-test-queue-strip {\n  display: flex;\n  justify-content: space-between;\n  gap: 12px;\n  align-items: center;\n  margin: 12px 0 14px;\n  padding: 12px;\n  border-radius: var(--radius-md);\n  background: #111827;\n  color: white;\n}\n\n.avatar-test-queue-strip strong {\n  display: block;\n  color: white;\n  font-size: var(--fs-base);\n}\n\n.avatar-test-queue-strip span {\n  display: block;\n  margin-top: 3px;\n  color: #cbd5e1;\n  font-size: var(--fs-xs);\n}\n\n.avatar-test-actions,\n.molecular-studio-actions,\n.studio-preview-actions {\n  display: flex;\n  flex-wrap: wrap;\n  gap: 8px;\n}\n\n.studio-status-banner {\n  margin-bottom: 12px;\n  padding: 10px 12px;\n  border-radius: var(--radius-sm);\n  background: #ecfeff;\n  border: 1px solid #a5f3fc;\n  color: #155e75;\n  font-weight: 750;\n  font-size: var(--fs-sm);\n}\n\n.molecular-studio-card,\n.avatar-queue-card,\n.studio-control-panel,\n.studio-elements-panel,\n.studio-side-help {\n  border: 1px solid var(--border);\n  border-radius: var(--radius-lg);\n  background: var(--surface);\n  box-shadow: var(--shadow-sm);\n}\n\n.molecular-studio-card,\n.avatar-queue-card {\n  padding: 14px;\n  margin-top: 12px;\n}\n\n.molecular-studio-header,\n.compound-designer-head {\n  display: flex;\n  justify-content: space-between;\n  gap: 14px;\n  align-items: flex-start;\n  margin-bottom: 14px;\n}\n\n.molecular-studio-layout {\n  display: grid;\n  grid-template-columns: 280px minmax(0, 1fr) 240px;\n  gap: 12px;\n  align-items: stretch;\n}\n\n.studio-control-panel,\n.studio-elements-panel,\n.studio-side-help {\n  padding: 12px;\n  background: var(--surface-soft);\n}\n\n.studio-control-panel label {\n  display: block;\n  margin: 10px 0 5px;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  font-weight: 800;\n}\n\n.studio-bond-source-card {\n  display: grid;\n  gap: 6px;\n  margin-top: 12px;\n  padding: 10px;\n  border-radius: var(--radius-sm);\n  background: var(--primary-soft);\n  border: 1px solid rgba(15, 118, 110, 0.18);\n}\n\n.studio-mini-list {\n  display: grid;\n  gap: 6px;\n  margin-top: 12px;\n}\n\n.studio-mini-list button,\n.studio-bond-source-card button {\n  min-height: 32px;\n  border-radius: var(--radius-sm);\n  padding: 7px 9px;\n  background: #111827;\n  color: white;\n  font-weight: 800;\n}\n\n.studio-graph-panel {\n  min-width: 0;\n  position: relative;\n}\n\n.studio-graph-shell {\n  height: 100%;\n  min-height: 620px;\n  padding: 12px;\n  border-radius: var(--radius-lg);\n  background:\n    linear-gradient(90deg, rgba(148, 163, 184, 0.13) 1px, transparent 1px),\n    linear-gradient(rgba(148, 163, 184, 0.13) 1px, transparent 1px),\n    radial-gradient(circle at center, rgba(15, 118, 110, 0.16), transparent 40%),\n    #050816;\n  background-size: 32px 32px, 32px 32px, 100% 100%, 100% 100%;\n  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);\n}\n\n.studio-graph-toolbar {\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n  align-items: center;\n  margin-bottom: 10px;\n  color: white;\n}\n\n.studio-graph-toolbar strong,\n.studio-graph-toolbar span,\n.studio-graph-toolbar small {\n  display: block;\n}\n\n.studio-graph-toolbar span,\n.studio-graph-toolbar small {\n  color: #94a3b8;\n  font-size: var(--fs-xs);\n}\n\n.studio-molecule-svg {\n  width: 100%;\n  min-height: 540px;\n  border-radius: var(--radius-md);\n  overflow: hidden;\n  display: block;\n  background: transparent;\n}\n\n.studio-atom-node {\n  cursor: grab;\n}\n\n.studio-elements-scroll {\n  max-height: 560px;\n  overflow: auto;\n  display: grid;\n  grid-template-columns: repeat(2, minmax(0, 1fr));\n  gap: 6px;\n  padding-right: 3px;\n}\n\n.studio-elements-scroll button {\n  min-height: 50px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  background: var(--surface);\n  cursor: pointer;\n  text-align: left;\n  padding: 8px;\n}\n\n.studio-elements-scroll button:hover {\n  border-color: var(--primary-600);\n  background: var(--table-row-hover);\n}\n\n.studio-elements-scroll strong {\n  display: block;\n  color: var(--heading);\n  font-size: var(--fs-base);\n}\n\n.studio-elements-scroll span {\n  display: block;\n  color: var(--text-soft);\n  font-size: 10px;\n  font-weight: 750;\n}\n\n.studio-medgen-console {\n  min-height: auto;\n  margin-top: 12px;\n  padding: 12px;\n  border: 1px solid rgba(45, 212, 191, 0.24);\n}\n\n.studio-medgen-console strong,\n.studio-medgen-console span {\n  display: block;\n}\n\n.studio-medgen-console span {\n  margin-top: 5px;\n  color: #cbd5e1;\n  font-size: var(--fs-sm);\n}\n\n.studio-node-context-menu {\n  position: fixed;\n  z-index: 9999;\n  width: 220px;\n  max-width: calc(100vw - 28px);\n  padding: 8px;\n  border-radius: var(--radius-md);\n  background: rgba(17, 24, 39, 0.98);\n  border: 1px solid rgba(148, 163, 184, 0.30);\n  box-shadow: var(--shadow-lg);\n  color: #e5e7eb;\n}\n\n.studio-node-context-head {\n  padding: 8px;\n  border-radius: var(--radius-sm);\n  background: rgba(15, 118, 110, 0.24);\n  border: 1px solid rgba(45, 212, 191, 0.24);\n  margin-bottom: 6px;\n}\n\n.studio-node-context-head strong,\n.studio-node-context-head span {\n  display: block;\n}\n\n.studio-node-context-head strong {\n  color: white;\n  font-size: var(--fs-sm);\n}\n\n.studio-node-context-head span,\n.studio-node-context-menu small {\n  color: #cbd5e1;\n  font-size: 10px;\n}\n\n.studio-node-context-menu button {\n  width: 100%;\n  min-height: 30px;\n  border-radius: var(--radius-xs);\n  margin-top: 5px;\n  padding: 6px 8px;\n  background: rgba(148, 163, 184, 0.14);\n  color: #eef2ff;\n  font-size: var(--fs-xs);\n  font-weight: 750;\n  text-align: left;\n}\n\n.studio-node-context-menu button:hover {\n  background: rgba(45, 212, 191, 0.18);\n}\n\n.studio-node-context-menu button.danger {\n  background: rgba(239, 68, 68, 0.16);\n  color: #fecaca;\n}\n\n.studio-context-section {\n  margin-top: 8px;\n  padding-top: 7px;\n  border-top: 1px solid rgba(148, 163, 184, 0.18);\n}\n\n.studio-picker-modal {\n  width: min(1080px, 96vw);\n  max-height: 88vh;\n  overflow: hidden;\n  display: flex;\n  flex-direction: column;\n}\n\n.studio-picker-head {\n  margin: 0;\n  padding: 14px 16px;\n  border-bottom: 1px solid var(--border);\n}\n\n.studio-picker-tabs {\n  display: flex;\n  gap: 7px;\n  padding: 12px 14px 0;\n  flex-wrap: wrap;\n}\n\n.studio-picker-tabs button {\n  min-height: 32px;\n  border-radius: var(--radius-sm);\n  padding: 6px 10px;\n  background: var(--surface-muted);\n  color: var(--text);\n  font-weight: 800;\n  text-transform: capitalize;\n}\n\n.studio-picker-tabs button.active {\n  color: white;\n  background: var(--primary-800);\n}\n\n.studio-picker-bond-row {\n  display: flex;\n  align-items: center;\n  gap: 10px;\n  margin: 12px 14px 0;\n  padding: 10px;\n  border-radius: var(--radius-sm);\n  background: #111827;\n  color: #e0f2fe;\n}\n\n.studio-picker-bond-row select {\n  width: auto;\n  min-height: 30px;\n  border: 0;\n  border-radius: var(--radius-xs);\n  padding: 6px 8px;\n  background: white;\n  color: #111827;\n  font-weight: 750;\n}\n\n.studio-picker-search {\n  margin: 12px 14px;\n}\n\n.studio-picker-grid {\n  overflow: auto;\n  padding: 0 14px 16px;\n  display: grid;\n  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));\n  gap: 10px;\n}\n\n.studio-picker-card {\n  min-height: 96px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  padding: 10px;\n  text-align: left;\n  background: var(--surface);\n  cursor: pointer;\n}\n\n.studio-picker-card:hover {\n  border-color: var(--primary-600);\n  background: var(--table-row-hover);\n}\n\n.studio-picker-card strong,\n.studio-picker-card span,\n.studio-picker-card small {\n  display: block;\n}\n\n.studio-picker-card strong {\n  color: var(--heading);\n  font-size: var(--fs-md);\n}\n\n.studio-picker-card span {\n  color: var(--text);\n  margin-top: 5px;\n  font-weight: 700;\n  font-size: var(--fs-xs);\n}\n\n.studio-picker-card small {\n  color: var(--text-soft);\n  margin-top: 6px;\n  line-height: 1.35;\n  font-size: var(--fs-xs);\n}\n\n/* ==========================================================================\n   Auth / wizard / PDF / voice\n   ========================================================================== */\n\n.loading-screen,\n.auth-screen {\n  min-height: 100vh;\n  display: grid;\n  place-items: center;\n  padding: 18px;\n}\n\n.loading-card,\n.auth-card {\n  background: var(--surface);\n  border: 1px solid var(--border);\n  box-shadow: var(--shadow-md);\n}\n\n.loading-card {\n  border-radius: var(--radius-lg);\n  padding: 18px 24px;\n  font-weight: 750;\n}\n\n.auth-card {\n  width: min(480px, 100%);\n  padding: 28px;\n  border-radius: var(--radius-xl);\n}\n\n.auth-card h2 {\n  font-size: var(--fs-2xl);\n  margin: 12px 0 8px;\n}\n\n.auth-note {\n  margin: 0 0 14px;\n  color: var(--text-soft);\n}\n\n.form-grid {\n  display: grid;\n  gap: 12px;\n  margin-top: 16px;\n}\n\n.form-field {\n  display: grid;\n  gap: 5px;\n}\n\n.form-field label,\n.field-block label {\n  font-size: var(--fs-sm);\n  font-weight: 750;\n  color: var(--heading);\n}\n\n.auth-actions {\n  display: flex;\n  flex-direction: column;\n  align-items: stretch;\n  gap: 12px;\n  margin-top: 18px;\n}\n\n.switch-link {\n  font-weight: 750;\n  color: var(--primary-800);\n  cursor: pointer;\n  margin: 0;\n}\n\n.wizard-modal {\n  width: min(720px, calc(100vw - 28px));\n  padding: 18px;\n}\n\n.wizard-steps,\n.session-tabs {\n  display: flex;\n  gap: 8px;\n  flex-wrap: wrap;\n}\n\n.wizard-steps {\n  margin: 14px 0 16px;\n}\n\n.wizard-step {\n  min-width: 128px;\n}\n\n.wizard-step span {\n  display: inline-grid;\n  place-items: center;\n  width: 24px;\n  height: 24px;\n  border-radius: 999px;\n  background: var(--surface-muted);\n  font-weight: 750;\n  margin-bottom: 6px;\n}\n\n.wizard-step p {\n  margin: 0;\n  font-size: var(--fs-xs);\n  font-weight: 750;\n}\n\n.wizard-step.done {\n  border-color: rgba(22, 163, 74, 0.24);\n}\n\n.questionnaire-shell {\n  padding: 0;\n  overflow: hidden;\n}\n\n.questionnaire-header {\n  padding: 18px 18px 10px;\n}\n\n.questionnaire-mode-switch,\n.questionnaire-step-strip {\n  display: flex;\n  gap: 8px;\n  flex-wrap: wrap;\n}\n\n.questionnaire-mode-switch {\n  margin-bottom: 12px;\n  margin-left: 0;\n}\n\n.questionnaire-step-strip {\n  padding: 0 18px 14px;\n  border-bottom: 1px solid var(--border);\n}\n\n.compact-step {\n  min-width: 118px;\n}\n\n.questionnaire-form-body {\n  padding: 18px 18px 96px;\n}\n\n.questionnaire-sticky-footer {\n  position: sticky;\n  bottom: 0;\n  z-index: 4;\n  display: flex;\n  justify-content: space-between;\n  gap: 10px;\n  padding: 12px 18px 14px;\n  border-top: 1px solid var(--border);\n  background: rgba(255, 255, 255, 0.92);\n  backdrop-filter: blur(12px);\n}\n\nhtml[data-theme=\"dark\"] .questionnaire-sticky-footer {\n  background: rgba(17, 24, 39, 0.92);\n}\n\n.pdf-modal {\n  width: min(1120px, calc(100vw - 28px));\n  padding: 16px;\n}\n\n.pdf-frame-wrap {\n  margin-top: 14px;\n  border-radius: var(--radius-md);\n  overflow: hidden;\n  min-height: 72vh;\n  border: 1px solid var(--border);\n  background: var(--surface-soft);\n}\n\n.pdf-frame,\n.pdf-loading {\n  width: 100%;\n  min-height: 72vh;\n}\n\n.pdf-frame {\n  border: 0;\n}\n\n.pdf-loading {\n  display: grid;\n  place-items: center;\n  color: var(--text-soft);\n}\n\n.voice-chat-card {\n  padding: 14px;\n}\n\n.voice-chat-header {\n  display: flex;\n  align-items: flex-start;\n  justify-content: space-between;\n  gap: 12px;\n  margin-bottom: 12px;\n}\n\n.voice-chat-header h3 {\n  margin: 0;\n  font-size: var(--fs-lg);\n  color: var(--heading);\n}\n\n.voice-lang-select {\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  padding: 8px 10px;\n  background: var(--surface-soft);\n  color: var(--text);\n  font-weight: 700;\n}\n\n.voice-progress {\n  margin-bottom: 12px;\n}\n\n.voice-progress span {\n  display: block;\n  font-size: var(--fs-xs);\n  color: var(--text-soft);\n  font-weight: 800;\n  margin-bottom: 5px;\n}\n\n.voice-progress div {\n  height: 7px;\n  background: var(--surface-muted);\n  border-radius: 999px;\n  overflow: hidden;\n}\n\n.voice-progress i {\n  display: block;\n  height: 100%;\n  background: var(--primary-800);\n  border-radius: 999px;\n}\n\n.voice-chat-window {\n  height: 380px;\n  overflow-y: auto;\n  padding: 12px;\n  background: var(--surface-soft);\n  border: 1px solid var(--border);\n  border-radius: var(--radius-md);\n  margin-bottom: 12px;\n}\n\n.voice-msg {\n  display: flex;\n  margin-bottom: 10px;\n}\n\n.voice-msg.assistant {\n  justify-content: flex-start;\n}\n\n.voice-msg.user {\n  justify-content: flex-end;\n}\n\n.voice-bubble {\n  max-width: 78%;\n  padding: 10px 12px;\n  border-radius: var(--radius-md);\n  font-size: var(--fs-md);\n  line-height: 1.45;\n  white-space: pre-wrap;\n}\n\n.voice-msg.assistant .voice-bubble {\n  background: var(--surface);\n  color: var(--text);\n  border: 1px solid var(--border);\n}\n\n.voice-msg.user .voice-bubble {\n  background: var(--primary-800);\n  color: white;\n}\n\n.voice-input-row {\n  display: flex;\n  gap: 8px;\n  align-items: flex-end;\n}\n\n.voice-input-row textarea {\n  flex: 1;\n  resize: none;\n  min-height: 42px;\n}\n\n.voice-mic-btn {\n  width: 42px;\n  min-width: 42px;\n  height: 38px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-sm);\n  background: var(--surface-soft);\n  cursor: pointer;\n  font-weight: 850;\n}\n\n.voice-mic-btn.recording {\n  width: auto;\n  min-width: 64px;\n  background: var(--danger-soft);\n  color: #b91c1c;\n  border-color: #fecaca;\n}\n\n/* ==========================================================================\n   Face / simple body fallback\n   ========================================================================== */\n\n.face-panel {\n  margin-top: 12px;\n  border: 1px solid var(--border);\n  border-radius: var(--radius-md);\n  padding: 12px;\n  background: var(--surface-soft);\n}\n\n.face-panel h2 {\n  font-size: var(--fs-lg);\n}\n\n.face-panel p {\n  color: var(--text-soft);\n  font-size: var(--fs-sm);\n}\n\n.face-video-wrap {\n  display: grid;\n  gap: 8px;\n  margin: 10px 0;\n}\n\n.face-video-wrap video,\n.face-video-wrap img {\n  width: 100%;\n  border-radius: var(--radius-sm);\n  background: #111827;\n  min-height: 170px;\n  object-fit: cover;\n}\n\n.face-actions {\n  display: flex;\n  gap: 8px;\n  margin-bottom: 10px;\n}\n\n.face-actions button {\n  flex: 1;\n  color: white;\n  background: #111827;\n}\n\n.face-consent {\n  display: flex;\n  gap: 8px;\n  color: var(--text);\n  font-size: var(--fs-sm);\n  margin-bottom: 8px;\n}\n\n.face-status {\n  margin-top: 8px;\n  background: var(--info-soft);\n  color: #075985;\n  padding: 8px;\n  border-radius: var(--radius-sm);\n  font-size: var(--fs-sm);\n}\n\n.human-heatmap-card {\n  display: grid;\n  place-items: center;\n  padding: 16px;\n}\n\n.human-body {\n  position: relative;\n  width: 300px;\n  height: 580px;\n  perspective: 900px;\n}\n\n.body-head,\n.body-neck,\n.body-torso,\n.body-arm,\n.body-leg {\n  position: absolute;\n  background: linear-gradient(145deg, #dbeafe, #eff6ff);\n  border: 1px solid #bfdbfe;\n  box-shadow: inset 0 0 18px rgba(59, 130, 246, 0.14);\n}\n\n.body-head {\n  width: 76px;\n  height: 86px;\n  border-radius: 50%;\n  top: 0;\n  left: 112px;\n}\n\n.body-neck {\n  width: 38px;\n  height: 38px;\n  top: 78px;\n  left: 131px;\n  border-radius: 14px;\n}\n\n.body-torso {\n  width: 170px;\n  height: 290px;\n  top: 112px;\n  left: 65px;\n  border-radius: 48px 48px 72px 72px;\n}\n\n.body-arm {\n  width: 48px;\n  height: 235px;\n  top: 132px;\n  border-radius: 36px;\n}\n\n.body-arm.left {\n  left: 8px;\n  transform: rotate(7deg);\n}\n\n.body-arm.right {\n  right: 8px;\n  transform: rotate(-7deg);\n}\n\n.body-leg {\n  width: 64px;\n  height: 196px;\n  top: 384px;\n  border-radius: 38px;\n}\n\n.body-leg.left {\n  left: 78px;\n}\n\n.body-leg.right {\n  right: 78px;\n}\n\n.organ-node {\n  position: absolute;\n  width: 48px;\n  height: 48px;\n  transform: translate(-50%, -50%);\n  border-radius: 50%;\n  border: 2px solid white;\n  z-index: 10;\n  display: grid;\n  place-items: center;\n  color: white;\n  font-size: 9px;\n  text-align: center;\n  font-weight: 850;\n  cursor: pointer;\n}\n\n.organ-node span {\n  position: absolute;\n  top: 52px;\n  color: var(--heading);\n  background: var(--surface);\n  border: 1px solid var(--border);\n  padding: 2px 6px;\n  border-radius: 999px;\n  white-space: nowrap;\n  font-size: 10px;\n}\n\n.heatmap-legend {\n  display: flex;\n  gap: 12px;\n  margin-top: 14px;\n  justify-content: center;\n  flex-wrap: wrap;\n}\n\n.heatmap-legend span {\n  display: flex;\n  align-items: center;\n  gap: 5px;\n  color: var(--text-soft);\n  font-size: var(--fs-xs);\n  font-weight: 800;\n}\n\n.heatmap-legend i {\n  width: 10px;\n  height: 10px;\n  border-radius: 50%;\n}\n\n.heatmap-legend .low {\n  background: var(--success);\n}\n\n.heatmap-legend .moderate {\n  background: var(--warning);\n}\n\n.heatmap-legend .high {\n  background: var(--danger);\n}\n\n/* ==========================================================================\n   Utilities\n   ========================================================================== */\n\n.full-span {\n  grid-column: 1 / -1;\n}\n\n.compact {\n  padding: 10px 12px;\n}\n\n.capitalize {\n  text-transform: capitalize;\n}\n\n.section-spacer {\n  margin-top: 24px;\n}\n\n.analysis-loading {\n  display: flex;\n  flex-direction: column;\n  align-items: center;\n  padding: 20px;\n  gap: 10px;\n}\n\n.loader {\n  width: 28px;\n  height: 28px;\n  border: 3px solid rgba(15, 23, 42, 0.12);\n  border-top: 3px solid var(--primary-800);\n  border-radius: 50%;\n  animation: spin 1s linear infinite;\n}\n\n@keyframes spin {\n  to { transform: rotate(360deg); }\n}\n\n.analysis-stream-box {\n  width: 100%;\n  margin-top: 14px;\n  padding: 12px;\n  border-radius: var(--radius-md);\n  background: var(--surface-soft);\n  border: 1px solid var(--border);\n  text-align: left;\n}\n\n.analysis-stream-box h4 {\n  margin: 0 0 8px;\n  font-size: var(--fs-md);\n  font-weight: 800;\n}\n\n.risk-pill {\n  text-transform: uppercase;\n  border-radius: 999px;\n  padding: 6px 10px;\n  font-weight: 850;\n  height: fit-content;\n  font-size: var(--fs-xs);\n}\n\n.risk-pill.low {\n  background: var(--success-soft);\n  color: #166534;\n}\n\n.risk-pill.moderate {\n  background: var(--warning-soft);\n  color: #92400e;\n}\n\n.risk-pill.high {\n  background: var(--danger-soft);\n  color: #991b1b;\n}\n\n/* ==========================================================================\n   Responsive\n   ========================================================================== */\n\n@media (max-width: 1280px) {\n  .hra-result-layout {\n    grid-template-columns: 1fr;\n  }\n\n  .hra-result-layout > .hra-body-viewer {\n    height: 620px;\n    min-height: 620px;\n  }\n\n  .d3-organ-intel-panel {\n    min-height: 620px;\n  }\n}\n\n@media (max-width: 1180px) {\n  .desktop-sidebar-panel,\n  .desktop-sidebar-backdrop {\n    display: none;\n  }\n\n  .dashboard-shell,\n  .dashboard-hero-grid,\n  .brain-map-layout,\n  .relation-graph-layout,\n  .reviews-layout,\n  .settings-grid,\n  .research-layout-row,\n  .research-grid,\n  .impact-validate-layout,\n  .bio-library-layout,\n  .molecular-studio-layout,\n  .bio-studio-hero {\n    grid-template-columns: 1fr;\n  }\n\n  .dashboard-sidebar {\n    order: 2;\n  }\n\n  .dashboard-main {\n    order: 1;\n  }\n\n  .patients-grid,\n  .metric-grid,\n  .sessions-grid,\n  .page-stats-grid {\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n  }\n\n  .research-right,\n  .patient-context-pro,\n  .sticky-panel {\n    position: static;\n  }\n\n  .research-warning {\n    min-width: 0;\n  }\n\n  .brain-canvas-card {\n    min-height: 560px;\n  }\n\n  .brain-canvas-wrap,\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg {\n    min-height: 500px;\n    height: 500px;\n  }\n}\n\n@media (max-width: 900px) {\n  .app-layout-shell,\n  .research-page,\n  .admin-page {\n    padding: 12px;\n  }\n\n  .topbar,\n  .page-header,\n  .section-head,\n  .admin-page-header,\n  .research-hero,\n  .research-card-head,\n  .organ-impact-table-head,\n  .voice-chat-header,\n  .molecular-studio-header,\n  .compound-designer-head,\n  .avatar-test-queue-strip {\n    flex-direction: column;\n    align-items: stretch;\n  }\n\n  .topbar-left,\n  .topbar-right {\n    width: 100%;\n    flex-wrap: wrap;\n  }\n\n  .topbar-right .btn,\n  .theme-toggle {\n    flex: 1;\n    min-width: 120px;\n  }\n\n  .research-hero h1,\n  .admin-page-header h1 {\n    font-size: 23px;\n  }\n\n  .summary-grid,\n  .compact-summary-grid,\n  .analysis-grid,\n  .question-grid,\n  .two-col,\n  .session-top-grid,\n  .impact-pro-grid,\n  .impact-grid,\n  .admin-stats-grid,\n  .admin-filter-card,\n  .admin-details-grid,\n  .admin-form-grid,\n  .biometric-row {\n    grid-template-columns: 1fr;\n  }\n\n  .admin-form-grid textarea,\n  .impact-field.wide,\n  .impact-grid textarea {\n    grid-column: span 1;\n  }\n\n  .patients-grid,\n  .metric-grid,\n  .sessions-grid,\n  .page-stats-grid,\n  .chemical-grid,\n  .bio-card-grid,\n  .history-grid-pro,\n  .queue-grid {\n    grid-template-columns: 1fr;\n  }\n\n  .chemical-search-bar,\n  .assist-search-row {\n    grid-template-columns: 1fr;\n  }\n\n  .bio-library-search,\n  .voice-input-row,\n  .impact-action-row,\n  .selected-drugs-actions {\n    flex-direction: column;\n    display: flex;\n  }\n\n  .voice-input-row textarea {\n    width: 100%;\n  }\n\n  .voice-bubble {\n    max-width: 92%;\n  }\n\n  .session-tabs,\n  .research-tabs,\n  .bio-library-tabs,\n  .bio-workbench-mode-tabs,\n  .impact-step-tabs {\n    overflow-x: auto;\n    flex-wrap: nowrap;\n    padding-bottom: 4px;\n  }\n\n  .session-tab,\n  .research-tabs button,\n  .bio-library-tabs button,\n  .bio-workbench-mode-tabs button,\n  .impact-step-tabs button {\n    flex: 0 0 auto;\n    white-space: nowrap;\n  }\n\n  .hra-body-viewer {\n    min-height: 520px;\n  }\n\n  .hra-result-layout > .hra-body-viewer {\n    height: 520px;\n    min-height: 520px;\n  }\n\n  .studio-graph-shell {\n    min-height: 520px;\n  }\n\n  .studio-molecule-svg {\n    min-height: 460px;\n  }\n\n  .studio-picker-backdrop {\n    align-items: flex-start;\n    padding: 10px;\n  }\n\n  .studio-picker-modal {\n    max-height: 94vh;\n    border-radius: var(--radius-lg);\n  }\n\n  .studio-picker-bond-row {\n    flex-wrap: wrap;\n  }\n}\n\n@media (max-width: 600px) {\n  body {\n    font-size: var(--fs-md);\n  }\n\n  .topbar {\n    padding: 10px 12px;\n  }\n\n  .topbar-title {\n    font-size: 20px;\n  }\n\n  .brand-mark {\n    width: 38px;\n    height: 38px;\n  }\n\n  .user-chip {\n    width: 100%;\n    justify-content: flex-start;\n  }\n\n  .card,\n  .research-card,\n  .admin-modal,\n  .wizard-modal,\n  .pdf-modal {\n    padding: 12px;\n    border-radius: var(--radius-md);\n  }\n\n  .research-page,\n  .admin-page,\n  .app-layout-shell {\n    padding: 10px;\n  }\n\n  .research-hero {\n    padding: 14px;\n    border-radius: var(--radius-lg);\n  }\n\n  .research-mini-stats span {\n    font-size: 10px;\n  }\n\n  .admin-table,\n  .organ-impact-table,\n  .impact-table {\n    min-width: 760px;\n  }\n\n  .admin-table th,\n  .organ-impact-table th,\n  .impact-table th,\n  .admin-table td,\n  .organ-impact-table td,\n  .impact-table td {\n    padding: 6px 7px;\n    font-size: 10px;\n  }\n\n  .brain-canvas-wrap,\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg {\n    min-height: 420px;\n    height: 420px;\n  }\n\n  .hra-body-viewer,\n  .hra-result-layout > .hra-body-viewer {\n    height: 460px;\n    min-height: 460px;\n  }\n\n  .voice-chat-window {\n    height: 320px;\n  }\n}\n\n\n/* ==========================================================================\n   STRUCTURE REPAIR PATCH v3\n   --------------------------------------------------------------------------\n   This section intentionally sits at the bottom so it wins over earlier rules.\n   It restores page grids/cards that were lost during the theme rewrite while\n   keeping the compact enterprise font/button/table look.\n   ========================================================================== */\n\n/* ---------- Page width safety ---------- */\n.app-layout-shell,\n.dashboard-shell,\n.session-page,\n.research-page,\n.admin-page,\n.page-content,\n.dashboard-page,\n.brain-page-shell {\n  width: 100%;\n  max-width: 100%;\n  overflow-x: hidden;\n}\n\n.app-layout-main,\n.dashboard-main,\n.full-width-main,\n.research-left,\n.research-right {\n  width: 100%;\n  min-width: 0;\n}\n\n/* ---------- Topbar structure ---------- */\n.topbar {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: center !important;\n  justify-content: space-between !important;\n  width: 100%;\n  gap: 16px;\n}\n\n.topbar-left {\n  display: flex !important;\n  align-items: center !important;\n  justify-content: flex-start !important;\n  flex: 1 1 auto;\n  min-width: 0;\n}\n\n.topbar-right {\n  display: flex !important;\n  align-items: center !important;\n  justify-content: flex-end !important;\n  flex: 0 0 auto;\n  min-width: max-content;\n}\n\n.topbar-title,\n.topbar-subtitle {\n  white-space: nowrap;\n}\n\n.theme-toggle {\n  width: auto !important;\n}\n\n/* ---------- Generic page header ---------- */\n.page-header {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: flex-start !important;\n  justify-content: space-between !important;\n  gap: 18px;\n  margin: 18px 0 18px;\n}\n\n.page-header-copy {\n  min-width: 0;\n  flex: 1 1 auto;\n}\n\n.page-title {\n  margin: 0 0 8px;\n  font-size: clamp(28px, 2.4vw, 38px);\n  line-height: 1.08;\n  font-weight: 900;\n  letter-spacing: -0.045em;\n  color: var(--heading);\n}\n\n.page-subtitle {\n  margin: 0;\n  max-width: 860px;\n  color: var(--text-soft);\n}\n\n.page-header-actions {\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  gap: 10px;\n  flex: 0 0 auto;\n}\n\n/* ---------- Restore dashboard layout ---------- */\n.dashboard-shell {\n  display: grid !important;\n  grid-template-columns: 300px minmax(0, 1fr) !important;\n  gap: 18px;\n  align-items: start;\n  padding-top: 18px;\n}\n\n.dashboard-shell.single-page-shell,\n.single-page-shell {\n  grid-template-columns: minmax(0, 1fr) !important;\n}\n\n.dashboard-sidebar {\n  display: flex !important;\n  flex-direction: column;\n  gap: 16px;\n}\n\n.dashboard-main {\n  display: block;\n}\n\n.dashboard-hero-grid {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1.4fr) minmax(320px, 0.7fr);\n  gap: 16px;\n  align-items: stretch;\n  margin-bottom: 18px;\n}\n\n.hero-panel,\n.ai-side-panel {\n  min-width: 0;\n}\n\n.ai-side-panel {\n  display: flex;\n  flex-direction: column;\n  justify-content: space-between;\n}\n\n.stats-stack,\n.sidebar-actions,\n.status-stack {\n  display: grid;\n  gap: 10px;\n}\n\n/* ---------- Restore stats/metric grids ---------- */\n.page-stats-grid,\n.metric-grid,\n.metric-grid-compact {\n  display: grid !important;\n  gap: 14px !important;\n  align-items: stretch;\n}\n\n.page-stats-grid {\n  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;\n  margin: 18px 0 18px;\n}\n\n.metric-grid {\n  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;\n}\n\n.metric-grid-compact {\n  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;\n}\n\n.metric-card,\n.stat-tile,\n.status-item,\n.summary-item {\n  min-height: 92px;\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  justify-content: center;\n  padding: 18px;\n  border-radius: 12px;\n}\n\n.metric-card-primary,\n.metric-card-blue,\n.metric-card-pink,\n.metric-card-green {\n  background: var(--primary-800) !important;\n  color: white;\n}\n\n/* ---------- Restore filter/toolbars ---------- */\n.page-toolbar {\n  display: flex !important;\n  align-items: center;\n  justify-content: space-between;\n  gap: 12px;\n  margin: 12px 0 16px;\n  flex-wrap: wrap;\n}\n\n.page-toolbar-filters {\n  display: grid !important;\n  grid-template-columns: minmax(260px, 1fr) 220px 220px 170px;\n  gap: 10px;\n  flex: 1 1 auto;\n}\n\n.page-toolbar-actions {\n  display: flex;\n  align-items: center;\n  justify-content: flex-end;\n  gap: 10px;\n}\n\n.section-tools {\n  display: flex !important;\n  align-items: center;\n  justify-content: flex-end;\n  gap: 10px;\n  flex-wrap: wrap;\n}\n\n.filter-input,\n.filter-select {\n  width: auto;\n  min-width: 180px;\n}\n\n/* ---------- Restore patient cards ---------- */\n.patients-grid,\n.patients-page-grid {\n  display: grid !important;\n  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;\n  gap: 16px !important;\n  width: 100%;\n  align-items: stretch;\n}\n\n.patient-card,\n.session-card {\n  width: 100%;\n  height: 100%;\n  text-align: left;\n  border: 1px solid var(--border);\n  background: var(--surface);\n  border-radius: 14px;\n  box-shadow: var(--shadow-sm);\n  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;\n}\n\n.patient-card {\n  display: flex !important;\n  flex-direction: column;\n  gap: 14px;\n  min-height: 172px;\n  padding: 16px;\n}\n\n.patient-card:hover,\n.session-card:hover {\n  border-color: var(--primary-600);\n  box-shadow: var(--shadow-md);\n  transform: translateY(-1px);\n}\n\n.patient-card.selected {\n  border-color: var(--primary-800);\n  box-shadow: 0 0 0 3px var(--primary-soft), var(--shadow-sm);\n}\n\n.patient-card-top {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: flex-start !important;\n  justify-content: space-between !important;\n  gap: 14px;\n}\n\n.patient-meta {\n  min-width: 0;\n  flex: 1 1 auto;\n}\n\n.patient-meta h3 {\n  margin: 0 0 6px;\n  font-size: 18px;\n  line-height: 1.2;\n  font-weight: 850;\n  color: var(--heading);\n  word-break: break-word;\n}\n\n.patient-meta p,\n.session-text {\n  margin: 0;\n  color: var(--text-soft);\n  font-size: 13px;\n}\n\n.patient-avatar {\n  width: 44px;\n  height: 44px;\n  border-radius: 12px;\n  color: var(--primary-900);\n  background: var(--primary-soft);\n  flex: 0 0 auto;\n}\n\n.patient-card-bottom {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: center !important;\n  justify-content: space-between !important;\n  gap: 10px;\n  margin-top: auto;\n  flex-wrap: wrap;\n}\n\n.patient-card-bottom .btn,\n.patient-card .btn {\n  min-height: 34px;\n  padding: 7px 11px;\n}\n\n.patient-actions,\n.patient-card-actions {\n  display: flex;\n  align-items: center;\n  gap: 8px;\n  margin-left: auto;\n  flex-wrap: wrap;\n}\n\n/* Some patient page cards may render without wrapper class; normalize children */\n.patients-grid > *,\n.patients-page-grid > * {\n  min-width: 0;\n}\n\n/* ---------- Restore sessions cards ---------- */\n.sessions-grid,\n.sessions-page-grid {\n  display: grid !important;\n  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr)) !important;\n  gap: 16px !important;\n  width: 100%;\n  align-items: stretch;\n}\n\n.session-card-button {\n  display: block;\n  width: 100%;\n  padding: 0;\n  background: transparent;\n  text-align: left;\n}\n\n.session-card {\n  display: flex;\n  flex-direction: column;\n  min-height: 170px;\n  padding: 16px;\n}\n\n.session-card-head,\n.session-card-head-top {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: flex-start !important;\n  justify-content: space-between !important;\n  gap: 12px;\n}\n\n.session-card h3,\n.session-card h2 {\n  margin: 0 0 6px;\n  font-size: 18px;\n  color: var(--heading);\n}\n\n.session-badge-stack {\n  display: flex;\n  flex-direction: column;\n  gap: 6px;\n  align-items: flex-end;\n  flex: 0 0 auto;\n}\n\n.session-summary-clamp {\n  display: -webkit-box;\n  -webkit-line-clamp: 3;\n  -webkit-box-orient: vertical;\n  overflow: hidden;\n  margin-top: 10px;\n  color: var(--text-soft);\n}\n\n/* ---------- Dashboard patient/session filter areas ---------- */\n.sessions-head,\n.patients-head {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: flex-end !important;\n  justify-content: space-between !important;\n  gap: 14px;\n  margin: 20px 0 12px;\n}\n\n.sessions-head > div:first-child,\n.patients-head > div:first-child {\n  flex: 1 1 auto;\n}\n\n/* ---------- Settings page structure ---------- */\n.settings-grid {\n  display: grid !important;\n  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;\n  gap: 12px;\n}\n\n.settings-section-card {\n  padding: 18px;\n}\n\n.settings-actions,\n.settings-actions-stack {\n  display: flex;\n  flex-direction: column;\n  gap: 10px;\n}\n\n/* ---------- Relation graph structure ---------- */\n.relation-graph-layout {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1.9fr) minmax(360px, 0.9fr) !important;\n  gap: 16px;\n  align-items: stretch;\n}\n\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card {\n  padding: 18px;\n}\n\n.relation-graph-side {\n  display: grid;\n  gap: 16px;\n}\n\n.relation-graph-canvas-wrap {\n  height: 620px;\n  min-height: 620px;\n}\n\n.relation-graph-svg {\n  height: 620px;\n}\n\n/* ---------- Brain page structure ---------- */\n.brain-page-shell {\n  display: grid;\n  gap: 16px;\n}\n\n.brain-map-layout {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1.55fr) minmax(360px, 0.55fr) !important;\n  gap: 16px;\n  align-items: stretch;\n}\n\n.brain-canvas-card,\n.brain-side-card,\n.brain-selected-panel {\n  padding: 18px;\n}\n\n.brain-canvas-card {\n  min-height: 760px;\n}\n\n.brain-canvas-wrap {\n  height: 660px;\n  min-height: 660px;\n}\n\n.brain-side-stack {\n  display: grid;\n  gap: 16px;\n}\n\n/* ---------- Research page structure ---------- */\n.research-page {\n  padding: 18px 24px 32px;\n}\n\n.research-hero {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: stretch !important;\n  justify-content: space-between !important;\n}\n\n.research-grid,\n.research-layout-row {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1fr) 350px !important;\n  gap: 16px !important;\n  align-items: start;\n}\n\n.research-left {\n  display: flex !important;\n  flex-direction: column;\n  gap: 16px;\n}\n\n.research-card.wide,\n.result-card {\n  grid-column: 1 / -1;\n}\n\n.research-tabs {\n  display: flex !important;\n  flex-wrap: wrap !important;\n}\n\n.bio-studio-hero {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1fr) 210px !important;\n}\n\n.molecular-studio-layout {\n  display: grid !important;\n  grid-template-columns: 280px minmax(0, 1fr) 240px !important;\n  gap: 12px;\n  align-items: stretch;\n}\n\n.avatar-test-queue-strip {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: center !important;\n  justify-content: space-between !important;\n}\n\n.studio-graph-shell {\n  min-height: 640px;\n}\n\n.studio-molecule-svg {\n  min-height: 560px;\n}\n\n/* ---------- Admin layout structure ---------- */\n.admin-page {\n  padding: 18px 24px 32px;\n}\n\n.admin-page-header {\n  display: flex !important;\n  flex-direction: row !important;\n  align-items: flex-start !important;\n  justify-content: space-between !important;\n}\n\n.admin-stats-grid {\n  display: grid !important;\n  grid-template-columns: repeat(3, minmax(0, 1fr)) !important;\n}\n\n.admin-filter-card {\n  display: grid !important;\n  grid-template-columns: minmax(260px, 1fr) 220px 220px 130px !important;\n  align-items: center;\n}\n\n.admin-table-card {\n  width: 100%;\n}\n\n.admin-table {\n  min-width: 1120px;\n}\n\n.admin-actions {\n  display: flex !important;\n  align-items: center;\n  justify-content: flex-start;\n  gap: 8px;\n  flex-wrap: wrap;\n}\n\n/* ---------- Cards that should not become weird full-width text blocks ---------- */\n.empty-card,\n.empty-state-box,\n.analysis-block,\n.analysis-summary-card,\n.analysis-doctor-note {\n  width: 100%;\n}\n\n/* ---------- Session page specific ---------- */\n.session-page {\n  display: grid;\n  gap: 16px;\n  padding: 18px 24px 32px;\n}\n\n.session-top-grid {\n  display: grid !important;\n  grid-template-columns: minmax(0, 1.2fr) minmax(360px, 0.8fr) !important;\n  gap: 16px;\n}\n\n.session-tabs {\n  display: flex !important;\n  flex-wrap: wrap !important;\n}\n\n.summary-grid,\n.compact-summary-grid,\n.analysis-grid {\n  display: grid !important;\n  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;\n  gap: 12px;\n}\n\n/* ---------- Form layout restore ---------- */\n.question-grid,\n.two-col {\n  display: grid !important;\n  grid-template-columns: repeat(2, minmax(0, 1fr)) !important;\n  gap: 12px;\n}\n\n.full-span {\n  grid-column: 1 / -1;\n}\n\n/* ---------- Tables keep proper width but don't explode page ---------- */\n.admin-table-card,\n.organ-impact-table-wrap,\n.impact-table-wrap {\n  max-width: 100%;\n  overflow-x: auto;\n}\n\n/* ---------- Improve visual density without breaking layout ---------- */\n.card,\n.research-card,\n.sidebar-card,\n.settings-section-card,\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card,\n.brain-canvas-card,\n.brain-side-card,\n.brain-selected-panel,\n.admin-stat-card,\n.admin-table-card {\n  border-radius: 14px;\n}\n\n/* ---------- Responsive: tablet ---------- */\n@media (max-width: 1280px) {\n  .dashboard-hero-grid,\n  .relation-graph-layout,\n  .brain-map-layout,\n  .research-grid,\n  .research-layout-row,\n  .molecular-studio-layout {\n    grid-template-columns: 1fr !important;\n  }\n\n  .research-right,\n  .patient-context-pro,\n  .sticky-panel {\n    position: static !important;\n  }\n\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg,\n  .brain-canvas-wrap {\n    height: 560px;\n    min-height: 560px;\n  }\n}\n\n/* ---------- Responsive: small laptop / tablet ---------- */\n@media (max-width: 1024px) {\n  .dashboard-shell {\n    grid-template-columns: 1fr !important;\n  }\n\n  .dashboard-sidebar {\n    order: 2;\n  }\n\n  .dashboard-main {\n    order: 1;\n  }\n\n  .page-stats-grid,\n  .metric-grid,\n  .metric-grid-compact,\n  .admin-stats-grid,\n  .settings-grid {\n    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;\n  }\n\n  .page-toolbar-filters,\n  .admin-filter-card {\n    grid-template-columns: 1fr 1fr !important;\n  }\n\n  .research-hero,\n  .admin-page-header,\n  .page-header {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n\n  .page-header-actions {\n    justify-content: flex-start;\n  }\n\n  .bio-studio-hero {\n    grid-template-columns: 1fr !important;\n  }\n}\n\n/* ---------- Responsive: mobile ---------- */\n@media (max-width: 760px) {\n  .topbar {\n    align-items: flex-start !important;\n    gap: 12px;\n  }\n\n  .topbar-left {\n    width: 100%;\n  }\n\n  .topbar-right {\n    width: 100%;\n    justify-content: flex-start !important;\n    flex-wrap: wrap;\n    min-width: 0;\n  }\n\n  .topbar-title,\n  .topbar-subtitle {\n    white-space: normal;\n  }\n\n  .theme-toggle,\n  .topbar-right .btn,\n  .user-chip {\n    width: 100% !important;\n  }\n\n  .app-layout-shell,\n  .research-page,\n  .admin-page,\n  .session-page {\n    padding: 12px !important;\n  }\n\n  .page-stats-grid,\n  .metric-grid,\n  .metric-grid-compact,\n  .admin-stats-grid,\n  .settings-grid,\n  .summary-grid,\n  .compact-summary-grid,\n  .analysis-grid,\n  .question-grid,\n  .two-col,\n  .session-top-grid,\n  .page-toolbar-filters,\n  .admin-filter-card {\n    grid-template-columns: 1fr !important;\n  }\n\n  .patients-grid,\n  .patients-page-grid,\n  .sessions-grid,\n  .sessions-page-grid {\n    grid-template-columns: 1fr !important;\n  }\n\n  .sessions-head,\n  .patients-head,\n  .patient-card-top,\n  .patient-card-bottom,\n  .session-card-head,\n  .session-card-head-top,\n  .avatar-test-queue-strip {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n\n  .patient-actions,\n  .patient-card-actions,\n  .session-badge-stack {\n    align-items: flex-start;\n    margin-left: 0;\n  }\n\n  .research-tabs,\n  .session-tabs,\n  .bio-library-tabs,\n  .bio-workbench-mode-tabs,\n  .impact-step-tabs {\n    overflow-x: auto;\n    flex-wrap: nowrap !important;\n    padding-bottom: 4px;\n  }\n\n  .research-tabs button,\n  .session-tab,\n  .bio-library-tabs button,\n  .bio-workbench-mode-tabs button,\n  .impact-step-tabs button {\n    flex: 0 0 auto;\n    white-space: nowrap;\n  }\n\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg,\n  .brain-canvas-wrap {\n    height: 430px;\n    min-height: 430px;\n  }\n\n  .brain-canvas-card {\n    min-height: 500px;\n  }\n\n  .admin-table {\n    min-width: 980px;\n  }\n}\n\n\n\n/* ==========================================================================\n   VERSION 4 — TYPOGRAPHY + SIMULATION THEME POLISH\n   --------------------------------------------------------------------------\n   Keep this block at the very bottom. It refines:\n   - More professional enterprise font scale\n   - Cleaner clinical/research typography\n   - Theme-matched simulation/HRA/D3/brain dark panels\n   - Less \"AI generated\" rounded/purple look\n   - Better modal and button proportions\n   ========================================================================== */\n\n:root {\n  /* v4 brand tuning: mature teal + clinical slate */\n  --primary: #0f6b63;\n  --primary-500: #147d74;\n  --primary-600: #0f766e;\n  --primary-700: #0d625b;\n  --primary-800: #0b5a52;\n  --primary-900: #083f3a;\n  --primary-soft: rgba(15, 107, 99, 0.10);\n  --primary-softer: rgba(15, 107, 99, 0.055);\n\n  --ink-950: #07111f;\n  --ink-900: #101827;\n  --ink-800: #182335;\n  --ink-700: #26354a;\n\n  --bg: #eef3f6;\n  --bg-page: #f5f8fa;\n  --surface: #ffffff;\n  --surface-strong: #ffffff;\n  --surface-soft: #f7fafc;\n  --surface-muted: #edf3f7;\n  --surface-dark: #0b1320;\n  --surface-dark-2: #111d2d;\n\n  --heading: #111827;\n  --text: #253245;\n  --text-soft: #66758b;\n  --text-muted: #8a98aa;\n\n  --border: #d7e0ea;\n  --border-strong: #b9c7d6;\n\n  --shadow-xs: 0 1px 2px rgba(16, 24, 39, 0.05);\n  --shadow-sm: 0 6px 18px rgba(16, 24, 39, 0.06);\n  --shadow-md: 0 14px 34px rgba(16, 24, 39, 0.10);\n  --shadow-lg: 0 24px 60px rgba(16, 24, 39, 0.16);\n\n  --radius-xs: 5px;\n  --radius-sm: 7px;\n  --radius-md: 10px;\n  --radius-lg: 13px;\n  --radius-xl: 16px;\n  --radius-2xl: 20px;\n\n  --font-main:\n    ui-sans-serif,\n    -apple-system,\n    BlinkMacSystemFont,\n    \"Segoe UI\",\n    Roboto,\n    \"Helvetica Neue\",\n    Arial,\n    \"Noto Sans\",\n    \"Noto Sans Devanagari\",\n    sans-serif;\n\n  --font-display:\n    ui-sans-serif,\n    -apple-system,\n    BlinkMacSystemFont,\n    \"Segoe UI\",\n    Roboto,\n    \"Helvetica Neue\",\n    Arial,\n    sans-serif;\n\n  --font-mono:\n    ui-monospace,\n    SFMono-Regular,\n    Menlo,\n    Monaco,\n    Consolas,\n    \"Liberation Mono\",\n    monospace;\n\n  --fs-xs: 10.5px;\n  --fs-sm: 11.5px;\n  --fs-md: 12.5px;\n  --fs-base: 13px;\n  --fs-lg: 15px;\n  --fs-xl: 18px;\n  --fs-2xl: 24px;\n  --fs-3xl: 31px;\n\n  --table-header-bg: var(--primary-800);\n  --table-header-text: #f8fffd;\n  --table-row-hover: #eef8f6;\n  --grid-line: rgba(98, 116, 142, 0.14);\n\n  /* simulation-specific variables */\n  --sim-bg: #07101d;\n  --sim-bg-2: #0b1628;\n  --sim-panel: #0f1b2d;\n  --sim-panel-soft: #152238;\n  --sim-line: rgba(148, 163, 184, 0.16);\n  --sim-primary: #2dd4bf;\n  --sim-primary-soft: rgba(45, 212, 191, 0.16);\n  --sim-text: #eaf2ff;\n  --sim-muted: #9caec3;\n\n  --risk-low: #16a34a;\n  --risk-moderate: #d97706;\n  --risk-high: #dc2626;\n  --risk-critical: #991b1b;\n}\n\n/* ---------- Base text rendering ---------- */\nhtml {\n  text-rendering: geometricPrecision;\n}\n\nbody {\n  font-family: var(--font-main) !important;\n  font-size: var(--fs-base);\n  letter-spacing: -0.006em;\n  color: var(--text);\n  background:\n    radial-gradient(circle at 4% 0%, rgba(15, 107, 99, 0.075), transparent 24%),\n    radial-gradient(circle at 90% 8%, rgba(8, 63, 58, 0.04), transparent 26%),\n    linear-gradient(180deg, #fbfdfe 0%, var(--bg-page) 100%) !important;\n}\n\nh1, h2, h3, h4, h5, h6,\n.topbar-title,\n.page-title,\n.research-hero h1,\n.bio-studio-hero h2,\n.compound-designer-head h2,\n.molecular-studio-header h2 {\n  font-family: var(--font-display) !important;\n  color: var(--heading);\n  letter-spacing: -0.04em;\n  text-wrap: balance;\n}\n\nh1 {\n  font-size: clamp(28px, 2.8vw, 38px);\n  line-height: 1.04;\n  font-weight: 850;\n}\n\nh2 {\n  font-size: clamp(20px, 2vw, 26px);\n  line-height: 1.12;\n  font-weight: 820;\n}\n\nh3 {\n  font-size: 17px;\n  line-height: 1.18;\n  font-weight: 780;\n}\n\np,\nsmall,\n.subtitle,\n.muted,\n.page-subtitle,\n.topbar-subtitle {\n  letter-spacing: -0.01em;\n}\n\np {\n  line-height: 1.55;\n}\n\nstrong {\n  font-weight: 760;\n}\n\npre,\ncode,\n.medgen-stream-box pre,\n.medgen-final-box pre,\n.analysis-stream-box pre {\n  font-family: var(--font-mono) !important;\n}\n\n/* ---------- More sober density ---------- */\n.app-layout-shell {\n  padding: 10px 24px 28px !important;\n}\n\n.topbar {\n  min-height: 62px !important;\n  padding: 8px 12px !important;\n}\n\n.brand-mark {\n  width: 36px !important;\n  height: 36px !important;\n  border-radius: 9px !important;\n  font-size: 11px !important;\n  letter-spacing: 0.02em;\n  box-shadow: 0 7px 18px rgba(15, 107, 99, 0.18);\n}\n\n.topbar-title {\n  font-size: 20px !important;\n  font-weight: 850 !important;\n  line-height: 1.05;\n}\n\n.topbar-subtitle {\n  font-size: 11px !important;\n  margin-top: 2px !important;\n  color: var(--text-soft);\n}\n\n.hamburger-btn {\n  width: 34px !important;\n  height: 34px !important;\n  border-radius: 8px !important;\n  box-shadow: var(--shadow-xs);\n}\n\n.user-chip {\n  min-height: 36px !important;\n  padding: 5px 10px !important;\n  border-radius: 18px !important;\n}\n\n.user-avatar {\n  width: 26px !important;\n  height: 26px !important;\n  font-size: 10px !important;\n}\n\n.user-name {\n  font-size: 11.5px !important;\n  font-weight: 760 !important;\n}\n\n.user-role {\n  font-size: 9.5px !important;\n}\n\n/* ---------- Buttons: less toy-like, more console/admin ---------- */\n.btn,\nbutton,\n.research-primary,\n.impact-next-btn,\n.impact-secondary-btn,\n.ask-llm-btn,\n.admin-primary-btn,\n.admin-filter-card button,\n.admin-pagination button,\n.admin-modal-actions button,\n.admin-actions button,\n.chemical-search-bar button,\n.chemical-pagination button,\n.bio-library-search button,\n.selected-drugs-open-btn,\n.face-actions button,\n.avatar-test-actions button,\n.molecular-studio-actions button,\n.studio-preview-actions button,\n.studio-library-open-btn {\n  border-radius: 7px !important;\n  min-height: 34px;\n  padding: 8px 13px;\n  font-size: 12px;\n  font-weight: 720;\n  letter-spacing: -0.01em;\n  box-shadow: none;\n}\n\n.btn-primary,\n.research-primary,\n.impact-next-btn,\n.admin-primary-btn,\n.chemical-search-bar button,\n.chemical-pagination button,\n.bio-library-search button,\n.avatar-test-actions button.primary,\n.molecular-studio-actions button.primary,\n.studio-preview-actions button.primary {\n  background: var(--primary-800) !important;\n  color: white !important;\n}\n\n.btn-primary:hover,\n.research-primary:hover,\n.impact-next-btn:hover,\n.admin-primary-btn:hover {\n  background: var(--primary-900) !important;\n}\n\n.btn-secondary,\n.impact-secondary-btn,\n.btn-ghost,\n.theme-toggle {\n  background: #f8fafc !important;\n  border: 1px solid var(--border) !important;\n  color: var(--heading) !important;\n}\n\n.ask-llm-btn,\n.admin-filter-card button,\n.chemical-search-bar button.dark,\n.btn-dark {\n  background: var(--ink-900) !important;\n  color: white !important;\n}\n\n/* ---------- Inputs ---------- */\ninput,\nselect,\ntextarea,\n.research-input,\n.filter-input,\n.filter-select {\n  min-height: 36px !important;\n  border-radius: 7px !important;\n  padding: 8px 11px !important;\n  font-size: 12.5px !important;\n  font-weight: 520 !important;\n  color: var(--text);\n  background: #ffffff !important;\n}\n\ntextarea {\n  line-height: 1.5;\n}\n\nlabel,\n.research-label,\n.impact-field label,\n.dose-row label,\n.form-field label,\n.field-block label {\n  font-size: 11px !important;\n  font-weight: 760 !important;\n  letter-spacing: 0.015em;\n  color: #44546a !important;\n}\n\n/* ---------- Cards and surfaces ---------- */\n.card,\n.research-card,\n.sidebar-card,\n.settings-section-card,\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card,\n.brain-canvas-card,\n.brain-side-card,\n.brain-selected-panel,\n.admin-stat-card,\n.admin-table-card,\n.voice-chat-card,\n.patient-card,\n.session-card,\n.wizard-modal,\n.admin-modal,\n.pdf-modal,\n.selected-drugs-card,\n.studio-picker-modal {\n  border-radius: 12px !important;\n  border-color: var(--border) !important;\n  box-shadow: var(--shadow-sm) !important;\n}\n\n.research-card,\n.settings-section-card,\n.admin-stat-card,\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card,\n.brain-canvas-card,\n.brain-side-card,\n.brain-selected-panel {\n  background: rgba(255, 255, 255, 0.92) !important;\n}\n\n/* ---------- Hero/header polish ---------- */\n.research-hero {\n  padding: 18px 22px !important;\n  border-radius: 14px !important;\n}\n\n.research-hero h1 {\n  font-size: clamp(24px, 2.2vw, 32px) !important;\n  font-weight: 850 !important;\n}\n\n.research-hero p {\n  font-size: 13px !important;\n  color: var(--text-soft);\n}\n\n.research-warning {\n  background: linear-gradient(135deg, var(--ink-900), #07101d) !important;\n  border: 1px solid rgba(148, 163, 184, 0.16);\n  border-radius: 12px !important;\n}\n\n.research-kicker,\n.eyebrow,\n.bio-studio-kicker,\n.compound-designer-head p,\n.molecular-studio-header p,\n.studio-picker-head p {\n  color: var(--primary-800) !important;\n  font-size: 10px !important;\n  font-weight: 820 !important;\n  letter-spacing: 0.14em !important;\n}\n\n/* ---------- Pills/tabs: theme matching ---------- */\n.research-mini-stats span,\n.pill,\n.saved-pill,\n.selected-patient-chip,\n.bmi-chip {\n  background: var(--primary-softer) !important;\n  border-color: rgba(15, 107, 99, 0.14) !important;\n  color: var(--primary-900) !important;\n  font-size: 10px !important;\n  font-weight: 780 !important;\n}\n\n.research-tabs button,\n.session-tab,\n.wizard-step,\n.bio-library-tabs button,\n.bio-workbench-mode-tabs button,\n.impact-step-tabs button,\n.chemical-az-filter button {\n  border-radius: 7px !important;\n  background: #eef3f7 !important;\n  border-color: #dbe4ee !important;\n  color: #213047 !important;\n  font-size: 11.5px !important;\n  font-weight: 760 !important;\n  min-height: 32px !important;\n}\n\n.research-tabs button.active,\n.session-tab.active,\n.wizard-step.active,\n.bio-library-tabs button.active,\n.bio-workbench-mode-tabs button.active,\n.impact-step-tabs button.active,\n.chemical-az-filter button.active {\n  background: var(--primary-800) !important;\n  border-color: var(--primary-800) !important;\n  color: #ffffff !important;\n}\n\n/* ---------- Table polish ---------- */\n.admin-table th,\n.organ-impact-table th,\n.impact-table th {\n  height: 32px !important;\n  background: linear-gradient(180deg, var(--primary-800), var(--primary-900)) !important;\n  font-size: 10.5px !important;\n  font-weight: 760 !important;\n  letter-spacing: 0.015em !important;\n}\n\n.admin-table td,\n.organ-impact-table td,\n.impact-table td {\n  font-size: 11.5px !important;\n  color: #27364b !important;\n  padding: 8px 9px !important;\n}\n\n.admin-table tbody tr:hover td,\n.organ-impact-table tbody tr:hover td,\n.impact-table tbody tr:hover td {\n  background: #eef8f6 !important;\n}\n\n/* ---------- Dark panels must match theme, not purple ---------- */\n.dark-card,\n.ai-side-panel,\n.medgen-side-panel,\n.studio-medgen-console,\n.avatar-test-queue-strip,\n.research-warning {\n  background:\n    radial-gradient(circle at 18% 0%, rgba(20, 184, 166, 0.10), transparent 30%),\n    linear-gradient(145deg, var(--surface-dark), var(--surface-dark-2)) !important;\n  border: 1px solid rgba(148, 163, 184, 0.16) !important;\n  color: var(--sim-text) !important;\n}\n\n.dark-card h1,\n.dark-card h2,\n.dark-card h3,\n.dark-card h4,\n.dark-card strong,\n.ai-side-panel h1,\n.ai-side-panel h2,\n.ai-side-panel h3,\n.ai-side-panel strong,\n.medgen-side-panel h3,\n.studio-medgen-console strong {\n  /* color: var(--sim-text) !important; */\n  color: #020617 !important;\n}\n\n.dark-card p,\n.dark-card small,\n.ai-side-panel p,\n.ai-side-panel small,\n.medgen-side-panel p,\n.studio-medgen-console span {\n  color: var(--sim-muted) !important;\n}\n\n/* ---------- Simulation / HRA / D3 color alignment ---------- */\n.hra-body-viewer,\n.brain-canvas-wrap,\n.relation-graph-canvas-wrap,\n.avatar-3d-container,\n.studio-graph-shell {\n  border: 1px solid rgba(15, 107, 99, 0.24) !important;\n  background:\n    linear-gradient(90deg, rgba(148, 163, 184, 0.105) 1px, transparent 1px),\n    linear-gradient(rgba(148, 163, 184, 0.105) 1px, transparent 1px),\n    radial-gradient(circle at 50% 42%, rgba(20, 184, 166, 0.14), transparent 34%),\n    radial-gradient(circle at 80% 12%, rgba(15, 107, 99, 0.12), transparent 26%),\n    linear-gradient(180deg, var(--sim-bg-2), var(--sim-bg)) !important;\n  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%, 100% 100% !important;\n  box-shadow:\n    inset 0 0 0 1px rgba(255, 255, 255, 0.025),\n    0 18px 46px rgba(7, 16, 29, 0.18) !important;\n}\n\n.hra-result-layout .d3-organ-intel-panel,\n.d3-organ-intel-panel,\n.brain-canvas-card {\n  background:\n    radial-gradient(circle at 18% 0%, rgba(20, 184, 166, 0.10), transparent 32%),\n    linear-gradient(145deg, #0b1320, #0e1a2b) !important;\n  color: var(--sim-text) !important;\n  border: 1px solid rgba(148, 163, 184, 0.18) !important;\n}\n\n.d3-organ-intel-panel h2,\n.d3-organ-intel-panel h3,\n.d3-organ-intel-panel strong,\n.brain-canvas-card h2,\n.brain-canvas-card h3 {\n  color: var(--sim-text) !important;\n}\n\n.d3-organ-intel-panel p,\n.d3-organ-intel-panel span,\n.d3-organ-intel-panel small,\n.brain-canvas-card p,\n.brain-canvas-card small {\n  color: var(--sim-muted);\n}\n\n.score-meter div,\n.organ-bar div {\n  background: linear-gradient(90deg, var(--primary-700), var(--sim-primary)) !important;\n}\n\n.brain-view-toggle button.active,\n.d3-organ-intel-panel button.active,\n.hra-result-layout button.active {\n  background: var(--primary-800) !important;\n  border-color: var(--primary-700) !important;\n}\n\n.hra-face-mask-label,\n.hra-viewer-chip {\n  border-color: rgba(45, 212, 191, 0.35) !important;\n  background: rgba(7, 16, 29, 0.82) !important;\n  color: #ccfbf1 !important;\n}\n\n/* Generic SVG graph colors where CSS can reach */\n.relation-graph-svg text,\n.d3-organ-intel-panel svg text,\n.studio-molecule-svg text {\n  font-family: var(--font-main) !important;\n  letter-spacing: -0.01em;\n}\n\n.d3-organ-intel-panel svg .node rect,\n.d3-organ-intel-panel svg rect {\n  rx: 6px;\n}\n\n.d3-organ-intel-panel svg line,\n.d3-organ-intel-panel svg path,\n.relation-graph-svg path,\n.relation-graph-svg line {\n  stroke-opacity: 0.72;\n}\n\n/* ---------- Simulation history cards ---------- */\n.history-grid-pro,\n.queue-grid,\n.chemical-grid,\n.bio-card-grid {\n  gap: 12px !important;\n}\n\n.history-tile,\n.queue-card,\n.chemical-card,\n.bio-select-card,\n.bio-library-item {\n  border-radius: 8px !important;\n  border-color: #dbe4ee !important;\n  background: #ffffff !important;\n  box-shadow: none !important;\n}\n\n.history-tile.active,\n.queue-card.active,\n.chemical-card.selected,\n.bio-select-card.selected,\n.bio-library-item.active {\n  border-color: var(--primary-700) !important;\n  box-shadow: 0 0 0 2px rgba(15, 107, 99, 0.08) !important;\n  background: #f4fbfa !important;\n}\n\n.history-tile strong,\n.queue-card strong,\n.chemical-card-top strong,\n.bio-select-card strong,\n.bio-library-item strong {\n  font-size: 12.5px !important;\n  font-weight: 780 !important;\n  letter-spacing: -0.02em;\n}\n\n/* ---------- Modal / wizard polish ---------- */\n.admin-modal-backdrop,\n.modal-backdrop,\n.pdf-backdrop,\n.selected-drugs-modal,\n.studio-picker-backdrop {\n  background: rgba(17, 24, 39, 0.46) !important;\n  backdrop-filter: blur(7px) saturate(0.9) !important;\n}\n\n.wizard-modal,\n.admin-modal,\n.selected-drugs-card,\n.studio-picker-modal {\n  border-radius: 14px !important;\n  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.26) !important;\n}\n\n.wizard-modal h2,\n.admin-modal h2 {\n  font-size: 22px !important;\n  font-weight: 820 !important;\n}\n\n.wizard-step {\n  min-width: 126px !important;\n  border-radius: 8px !important;\n}\n\n.wizard-step.active {\n  background: var(--primary-800) !important;\n}\n\n.wizard-step span {\n  width: 22px !important;\n  height: 22px !important;\n  font-size: 10px;\n}\n\n/* ---------- Risk colors ---------- */\n.risk-pill.low,\n.organ-risk-chip.low,\n.pill.low {\n  background: rgba(22, 163, 74, 0.12) !important;\n  color: #166534 !important;\n}\n\n.risk-pill.moderate,\n.organ-risk-chip.moderate,\n.pill.moderate {\n  background: rgba(217, 119, 6, 0.13) !important;\n  color: #92400e !important;\n}\n\n.risk-pill.high,\n.organ-risk-chip.high,\n.pill.high {\n  background: rgba(220, 38, 38, 0.12) !important;\n  color: #991b1b !important;\n}\n\n.risk-pill.critical,\n.organ-risk-chip.critical,\n.pill.critical {\n  background: rgba(153, 27, 27, 0.13) !important;\n  color: #7f1d1d !important;\n}\n\n/* ---------- Patient/session card typography ---------- */\n.patient-meta h3,\n.session-card h3,\n.session-card h2 {\n  font-size: 17px !important;\n  font-weight: 820 !important;\n}\n\n.patient-meta p,\n.session-text,\n.session-summary-clamp {\n  font-size: 12.5px !important;\n  color: var(--text-soft);\n}\n\n.patient-avatar {\n  border-radius: 10px !important;\n}\n\n/* ---------- Research studio controls ---------- */\n.studio-node-context-menu {\n  background: rgba(11, 19, 32, 0.98) !important;\n  border-color: rgba(45, 212, 191, 0.18) !important;\n}\n\n.studio-node-context-head {\n  background: rgba(15, 107, 99, 0.22) !important;\n  border-color: rgba(45, 212, 191, 0.22) !important;\n}\n\n.studio-node-context-menu button {\n  background: rgba(148, 163, 184, 0.12) !important;\n}\n\n.studio-node-context-menu button:hover {\n  background: rgba(45, 212, 191, 0.16) !important;\n}\n\n/* ---------- Dark mode v4 ---------- */\nhtml[data-theme=\"dark\"] {\n  --bg: #07101d;\n  --bg-page: #0b1320;\n  --surface: #111d2d;\n  --surface-strong: #111d2d;\n  --surface-soft: #152238;\n  --surface-muted: #1b2a40;\n  --heading: #f3f7fb;\n  --text: #d7e2ee;\n  --text-soft: #9caec3;\n  --text-muted: #7d8da3;\n  --border: rgba(148, 163, 184, 0.20);\n  --border-strong: rgba(203, 213, 225, 0.28);\n}\n\nhtml[data-theme=\"dark\"] body {\n  background:\n    radial-gradient(circle at 8% 0%, rgba(20, 184, 166, 0.13), transparent 26%),\n    linear-gradient(180deg, #07101d 0%, #0b1320 100%) !important;\n}\n\nhtml[data-theme=\"dark\"] input,\nhtml[data-theme=\"dark\"] select,\nhtml[data-theme=\"dark\"] textarea {\n  background: #0f1b2d !important;\n  color: var(--text) !important;\n  border-color: rgba(148, 163, 184, 0.22) !important;\n}\n\nhtml[data-theme=\"dark\"] .btn-secondary,\nhtml[data-theme=\"dark\"] .impact-secondary-btn,\nhtml[data-theme=\"dark\"] .btn-ghost,\nhtml[data-theme=\"dark\"] .theme-toggle {\n  background: #111d2d !important;\n  color: #eaf2ff !important;\n}\n\n/* ---------- Responsive fine tuning ---------- */\n@media (max-width: 760px) {\n  .app-layout-shell {\n    padding: 10px 12px 22px !important;\n  }\n\n  .topbar {\n    padding: 8px 6px !important;\n  }\n\n  .topbar-title {\n    font-size: 18px !important;\n  }\n\n  h1 {\n    font-size: 25px !important;\n  }\n\n  h2 {\n    font-size: 20px !important;\n  }\n}\n\n/* ==========================================================================\n   MEDGEN LOGO THEME + RESPONSIVE STABILITY PATCH v5\n   --------------------------------------------------------------------------\n   Place this block at the bottom of App.css, or use the generated complete\n   App.medgen-logo-v5.css file as a full replacement.\n\n   Brand direction based on the supplied MedGen logo:\n   - Deep black/navy clinical background\n   - Electric medical blue highlights\n   - Clean white typography\n   - Less green, less generic dashboard feel\n   - Strong mobile/tablet responsiveness using existing class names\n   ========================================================================== */\n\n:root {\n  /* MedGen logo palette */\n  --mg-black: #03060c;\n  --mg-navy-950: #06101f;\n  --mg-navy-900: #081525;\n  --mg-navy-850: #0b1b30;\n  --mg-navy-800: #10243a;\n  --mg-blue: #1f7bc1;\n  --mg-blue-500: #2d8fe0;\n  --mg-blue-400: #21a7f3;\n  --mg-cyan: #18b7ff;\n  --mg-white: #ffffff;\n  --mg-slate: #6f8198;\n  --mg-soft-blue: rgba(31, 123, 193, 0.12);\n  --mg-glow: 0 0 0 1px rgba(24, 183, 255, 0.18), 0 18px 44px rgba(31, 123, 193, 0.14);\n\n  /* Override previous green clinical palette */\n  --primary: var(--mg-blue);\n  --primary-500: var(--mg-blue-500);\n  --primary-600: var(--mg-blue-500);\n  --primary-700: #1769a9;\n  --primary-800: #125a94;\n  --primary-900: #0b3d66;\n  --primary-soft: rgba(31, 123, 193, 0.12);\n  --primary-softer: rgba(31, 123, 193, 0.065);\n\n  --bg: #eef4fa;\n  --bg-page: #f6f9fc;\n  --surface: #ffffff;\n  --surface-strong: #ffffff;\n  --surface-soft: #f7fbff;\n  --surface-muted: #edf4fb;\n  --surface-dark: var(--mg-navy-950);\n  --surface-dark-2: var(--mg-navy-900);\n\n  --heading: #07111f;\n  --text: #243449;\n  --text-soft: #687a91;\n  --text-muted: #91a0b2;\n  --text-inverse: #ffffff;\n\n  --border: #d6e1ee;\n  --border-strong: #b5c6da;\n  --grid-line: rgba(31, 123, 193, 0.11);\n\n  --shadow-xs: 0 1px 2px rgba(7, 17, 31, 0.05);\n  --shadow-sm: 0 8px 22px rgba(7, 17, 31, 0.07);\n  --shadow-md: 0 16px 42px rgba(7, 17, 31, 0.11);\n  --shadow-lg: 0 28px 70px rgba(7, 17, 31, 0.18);\n\n  --table-header-bg: #0b3d66;\n  --table-header-text: #ffffff;\n  --table-row-hover: #eef7ff;\n\n  --sim-bg: #03060c;\n  --sim-bg-2: #06101f;\n  --sim-panel: #081525;\n  --sim-panel-soft: #10243a;\n  --sim-line: rgba(24, 183, 255, 0.18);\n  --sim-primary: var(--mg-cyan);\n  --sim-primary-soft: rgba(24, 183, 255, 0.16);\n  --sim-text: #f4f9ff;\n  --sim-muted: #9bb0c8;\n}\n\nhtml[data-theme=\"dark\"] {\n  --primary: var(--mg-blue-500);\n  --primary-500: var(--mg-blue-500);\n  --primary-600: var(--mg-cyan);\n  --primary-700: var(--mg-blue);\n  --primary-800: #1769a9;\n  --primary-900: #0b3d66;\n  --primary-soft: rgba(24, 183, 255, 0.15);\n  --primary-softer: rgba(24, 183, 255, 0.08);\n\n  --bg: #03060c;\n  --bg-page: #050b14;\n  --surface: #081525;\n  --surface-strong: #0b1b30;\n  --surface-soft: #10243a;\n  --surface-muted: #132b45;\n  --surface-dark: #03060c;\n  --surface-dark-2: #06101f;\n\n  --heading: #f6fbff;\n  --text: #dbe8f6;\n  --text-soft: #9db0c7;\n  --text-muted: #75869d;\n  --border: rgba(141, 174, 211, 0.22);\n  --border-strong: rgba(194, 218, 244, 0.30);\n  --table-row-bg: #081525;\n  --table-row-alt: #06101f;\n  --table-row-hover: rgba(24, 183, 255, 0.10);\n  --table-cell-border: rgba(141, 174, 211, 0.20);\n}\n\nbody {\n  background:\n    radial-gradient(circle at 6% 0%, rgba(24, 183, 255, 0.11), transparent 25%),\n    radial-gradient(circle at 88% 2%, rgba(31, 123, 193, 0.07), transparent 28%),\n    linear-gradient(180deg, #fbfdff 0%, var(--bg-page) 100%) !important;\n}\n\nhtml[data-theme=\"dark\"] body {\n  background:\n    radial-gradient(circle at 8% 0%, rgba(24, 183, 255, 0.18), transparent 26%),\n    radial-gradient(circle at 92% 8%, rgba(31, 123, 193, 0.15), transparent 28%),\n    linear-gradient(180deg, #03060c 0%, #050b14 100%) !important;\n}\n\n/* ---------- Brand/topbar ---------- */\n.brand-mark {\n  background:\n    radial-gradient(circle at 82% 18%, rgba(255,255,255,0.96) 0 13%, transparent 14%),\n    conic-gradient(from 210deg, transparent 0deg, transparent 40deg, rgba(24,183,255,0.95) 105deg, transparent 165deg),\n    linear-gradient(135deg, var(--mg-blue), #0b4e84) !important;\n  color: #ffffff !important;\n  box-shadow: 0 10px 26px rgba(31,123,193,0.28), 0 0 22px rgba(24,183,255,0.16) !important;\n}\n\n.topbar-title,\n.page-title,\n.research-hero h1,\n.bio-studio-hero h2,\n.compound-designer-head h2,\n.molecular-studio-header h2 {\n  letter-spacing: -0.055em !important;\n}\n\n.topbar {\n  position: sticky;\n  top: 0;\n  z-index: 200;\n  background: rgba(246, 249, 252, 0.76) !important;\n  backdrop-filter: blur(16px) saturate(1.1);\n  border-bottom: 1px solid rgba(214, 225, 238, 0.65);\n}\n\nhtml[data-theme=\"dark\"] .topbar {\n  background: rgba(3, 6, 12, 0.72) !important;\n  border-bottom-color: rgba(141, 174, 211, 0.16);\n}\n\n/* ---------- Buttons and active UI ---------- */\n.btn-primary,\n.research-primary,\n.impact-next-btn,\n.admin-primary-btn,\n.chemical-search-bar button,\n.chemical-pagination button,\n.bio-library-search button,\n.avatar-test-actions button.primary,\n.molecular-studio-actions button.primary,\n.studio-preview-actions button.primary,\n.research-tabs button.active,\n.session-tab.active,\n.wizard-step.active,\n.bio-library-tabs button.active,\n.bio-workbench-mode-tabs button.active,\n.impact-step-tabs button.active,\n.chemical-az-filter button.active,\n.app-nav-link.active {\n  background: linear-gradient(135deg, var(--mg-blue), #0f5f9d) !important;\n  border-color: rgba(24, 183, 255, 0.24) !important;\n  color: #ffffff !important;\n  box-shadow: 0 8px 22px rgba(31, 123, 193, 0.18) !important;\n}\n\n.btn-primary:hover,\n.research-primary:hover,\n.impact-next-btn:hover,\n.admin-primary-btn:hover {\n  background: linear-gradient(135deg, var(--mg-blue-500), var(--mg-blue)) !important;\n  transform: translateY(-1px);\n}\n\n.ask-llm-btn,\n.admin-filter-card button,\n.btn-dark {\n  background: linear-gradient(135deg, #06101f, #10243a) !important;\n  color: #ffffff !important;\n  border: 1px solid rgba(24, 183, 255, 0.22) !important;\n}\n\ninput:focus,\nselect:focus,\ntextarea:focus,\n.research-input:focus,\n.filter-input:focus,\n.filter-select:focus {\n  border-color: var(--mg-blue-500) !important;\n  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.13) !important;\n}\n\n/* ---------- Cards and panels ---------- */\n.card,\n.research-card,\n.sidebar-card,\n.settings-section-card,\n.relation-graph-canvas-card,\n.relation-detail-card,\n.relation-legend-card,\n.brain-side-card,\n.brain-selected-panel,\n.admin-stat-card,\n.admin-table-card,\n.voice-chat-card,\n.patient-card,\n.session-card,\n.wizard-modal,\n.admin-modal,\n.pdf-modal,\n.selected-drugs-card,\n.studio-picker-modal,\n.auth-card,\n.brain-page-meta-card,\n.brain-source-card {\n  border-color: rgba(185, 204, 224, 0.85) !important;\n  box-shadow: 0 10px 30px rgba(7, 17, 31, 0.065) !important;\n}\n\nhtml[data-theme=\"dark\"] .card,\nhtml[data-theme=\"dark\"] .research-card,\nhtml[data-theme=\"dark\"] .sidebar-card,\nhtml[data-theme=\"dark\"] .settings-section-card,\nhtml[data-theme=\"dark\"] .relation-detail-card,\nhtml[data-theme=\"dark\"] .relation-legend-card,\nhtml[data-theme=\"dark\"] .brain-side-card,\nhtml[data-theme=\"dark\"] .brain-selected-panel,\nhtml[data-theme=\"dark\"] .admin-stat-card,\nhtml[data-theme=\"dark\"] .admin-table-card,\nhtml[data-theme=\"dark\"] .voice-chat-card,\nhtml[data-theme=\"dark\"] .patient-card,\nhtml[data-theme=\"dark\"] .session-card {\n  background: rgba(8, 21, 37, 0.92) !important;\n  border-color: rgba(141, 174, 211, 0.20) !important;\n}\n\n.patient-card:hover,\n.session-card:hover,\n.chemical-card:hover,\n.bio-select-card:hover,\n.history-tile:hover,\n.bio-library-item:hover {\n  border-color: rgba(31, 123, 193, 0.55) !important;\n  box-shadow: var(--mg-glow) !important;\n}\n\n.patient-card.selected,\n.chemical-card.selected,\n.bio-select-card.selected,\n.history-tile.active,\n.bio-library-item.active {\n  border-color: var(--mg-blue) !important;\n  background: #f3f9ff !important;\n  box-shadow: 0 0 0 3px rgba(31, 123, 193, 0.13), var(--shadow-sm) !important;\n}\n\nhtml[data-theme=\"dark\"] .patient-card.selected,\nhtml[data-theme=\"dark\"] .chemical-card.selected,\nhtml[data-theme=\"dark\"] .bio-select-card.selected,\nhtml[data-theme=\"dark\"] .history-tile.active,\nhtml[data-theme=\"dark\"] .bio-library-item.active {\n  background: rgba(31, 123, 193, 0.12) !important;\n}\n\n.patient-avatar,\n.user-avatar,\n.research-mini-stats span,\n.pill,\n.saved-pill,\n.selected-patient-chip,\n.bmi-chip {\n  background: rgba(31, 123, 193, 0.10) !important;\n  color: #0b4e84 !important;\n  border-color: rgba(31, 123, 193, 0.18) !important;\n}\n\nhtml[data-theme=\"dark\"] .patient-avatar,\nhtml[data-theme=\"dark\"] .user-avatar,\nhtml[data-theme=\"dark\"] .research-mini-stats span,\nhtml[data-theme=\"dark\"] .pill,\nhtml[data-theme=\"dark\"] .saved-pill,\nhtml[data-theme=\"dark\"] .selected-patient-chip,\nhtml[data-theme=\"dark\"] .bmi-chip {\n  background: rgba(24, 183, 255, 0.13) !important;\n  color: #bfeaff !important;\n  border-color: rgba(24, 183, 255, 0.22) !important;\n}\n\n/* ---------- Dark simulation panels: fix black text issue ---------- */\n.dark-card,\n.ai-side-panel,\n.medgen-side-panel,\n.studio-medgen-console,\n.avatar-test-queue-strip,\n.research-warning,\n.brain-canvas-card,\n.hra-result-layout .d3-organ-intel-panel,\n.d3-organ-intel-panel {\n  background:\n    radial-gradient(circle at 18% 0%, rgba(24, 183, 255, 0.16), transparent 30%),\n    radial-gradient(circle at 92% 8%, rgba(31, 123, 193, 0.14), transparent 34%),\n    linear-gradient(145deg, #03060c, #081525 58%, #06101f) !important;\n  border: 1px solid rgba(24, 183, 255, 0.18) !important;\n  color: var(--sim-text) !important;\n}\n\n.dark-card h1,\n.dark-card h2,\n.dark-card h3,\n.dark-card h4,\n.dark-card strong,\n.dark-card span,\n.ai-side-panel h1,\n.ai-side-panel h2,\n.ai-side-panel h3,\n.ai-side-panel strong,\n.ai-side-panel span,\n.medgen-side-panel h3,\n.medgen-side-panel strong,\n.studio-medgen-console strong,\n.brain-canvas-card h1,\n.brain-canvas-card h2,\n.brain-canvas-card h3,\n.brain-canvas-card h4,\n.brain-canvas-card strong,\n.d3-organ-intel-panel h1,\n.d3-organ-intel-panel h2,\n.d3-organ-intel-panel h3,\n.d3-organ-intel-panel h4,\n.d3-organ-intel-panel strong {\n  color: #f6fbff !important;\n}\n\n.dark-card p,\n.dark-card small,\n.ai-side-panel p,\n.ai-side-panel small,\n.medgen-side-panel p,\n.medgen-side-panel small,\n.studio-medgen-console span,\n.brain-canvas-card p,\n.brain-canvas-card small,\n.d3-organ-intel-panel p,\n.d3-organ-intel-panel small {\n  color: var(--sim-muted) !important;\n}\n\n/* ---------- Brain / graph / canvas style matching logo ---------- */\n.hra-body-viewer,\n.brain-canvas-wrap,\n.relation-graph-canvas-wrap,\n.avatar-3d-container,\n.studio-graph-shell {\n  border: 1px solid rgba(24, 183, 255, 0.22) !important;\n  background:\n    linear-gradient(90deg, rgba(24, 183, 255, 0.055) 1px, transparent 1px),\n    linear-gradient(rgba(24, 183, 255, 0.055) 1px, transparent 1px),\n    radial-gradient(circle at 48% 42%, rgba(24, 183, 255, 0.18), transparent 34%),\n    radial-gradient(circle at 80% 14%, rgba(31, 123, 193, 0.18), transparent 28%),\n    linear-gradient(180deg, #06101f, #03060c) !important;\n  background-size: 34px 34px, 34px 34px, 100% 100%, 100% 100%, 100% 100% !important;\n  box-shadow:\n    inset 0 0 0 1px rgba(255, 255, 255, 0.025),\n    0 20px 54px rgba(3, 6, 12, 0.26) !important;\n}\n\n.brain-view-toggle button.active,\n.d3-organ-intel-panel button.active,\n.hra-result-layout button.active {\n  background: linear-gradient(135deg, var(--mg-blue), var(--mg-cyan)) !important;\n  border-color: rgba(24, 183, 255, 0.45) !important;\n  color: #ffffff !important;\n}\n\n.score-meter div,\n.organ-bar div,\n.organ-score-track i,\n.voice-progress i {\n  background: linear-gradient(90deg, var(--mg-blue), var(--mg-cyan)) !important;\n}\n\n.medgen-stream-box,\n.medgen-final-box,\n.analysis-stream-box {\n  background: rgba(3, 6, 12, 0.38) !important;\n  border-color: rgba(24, 183, 255, 0.18) !important;\n  color: #dceeff !important;\n}\n\n/* ---------- Logo aware hero surfaces ---------- */\n.research-hero,\n.impact-workbench-header,\n.bio-studio-hero {\n  background:\n    radial-gradient(circle at 100% 0%, rgba(31, 123, 193, 0.10), transparent 30%),\n    linear-gradient(180deg, #ffffff, #f8fbff) !important;\n}\n\nhtml[data-theme=\"dark\"] .research-hero,\nhtml[data-theme=\"dark\"] .impact-workbench-header,\nhtml[data-theme=\"dark\"] .bio-studio-hero {\n  background:\n    radial-gradient(circle at 100% 0%, rgba(24, 183, 255, 0.16), transparent 30%),\n    linear-gradient(145deg, #081525, #06101f) !important;\n}\n\n/* ---------- Layout / responsiveness cleanup ---------- */\n.app-layout-shell,\n.research-page,\n.admin-page,\n.session-page,\n.brain-page-shell,\n.dashboard-shell,\n.main-content,\n.app-layout-main {\n  max-width: 100vw !important;\n  overflow-x: hidden !important;\n}\n\nimg,\nvideo,\ncanvas,\nsvg,\niframe {\n  max-width: 100%;\n}\n\n.page-toolbar,\n.sessions-head,\n.patients-head,\n.topbar,\n.topbar-left,\n.topbar-right,\n.patient-card-top,\n.patient-card-bottom,\n.session-card-head,\n.session-card-head-top,\n.research-card-head,\n.admin-page-header,\n.page-header {\n  min-width: 0;\n}\n\n.filter-input,\n.filter-select,\n.page-toolbar-filters input,\n.page-toolbar-filters select,\n.page-toolbar-actions .btn,\n.section-tools .btn {\n  width: 100%;\n}\n\n/* ---------- Large screens ---------- */\n@media (min-width: 1440px) {\n  .app-layout-shell {\n    padding-left: 28px !important;\n    padding-right: 28px !important;\n  }\n\n  .patients-grid,\n  .patients-page-grid {\n    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr)) !important;\n  }\n\n  .sessions-grid,\n  .sessions-page-grid {\n    grid-template-columns: repeat(auto-fill, minmax(390px, 1fr)) !important;\n  }\n}\n\n/* ---------- Tablet / small laptop ---------- */\n@media (max-width: 1180px) {\n  .topbar {\n    position: sticky;\n  }\n\n  .dashboard-shell,\n  .dashboard-hero-grid,\n  .brain-map-layout,\n  .relation-graph-layout,\n  .reviews-layout,\n  .settings-grid,\n  .research-layout-row,\n  .research-grid,\n  .impact-validate-layout,\n  .bio-library-layout,\n  .molecular-studio-layout,\n  .bio-studio-hero,\n  .session-top-grid,\n  .hra-result-layout {\n    grid-template-columns: 1fr !important;\n  }\n\n  .research-right,\n  .patient-context-pro,\n  .sticky-panel {\n    position: static !important;\n  }\n\n  .page-toolbar {\n    align-items: stretch !important;\n  }\n\n  .page-toolbar-filters {\n    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;\n  }\n\n  .page-toolbar-actions,\n  .section-tools {\n    justify-content: flex-start !important;\n  }\n}\n\n/* ---------- Tablet ---------- */\n@media (max-width: 900px) {\n  .app-layout-shell,\n  .research-page,\n  .admin-page,\n  .session-page {\n    padding: 12px !important;\n  }\n\n  .topbar {\n    padding: 10px 12px !important;\n    gap: 10px !important;\n    flex-wrap: wrap;\n  }\n\n  .topbar-left {\n    flex: 1 1 calc(100% - 48px);\n  }\n\n  .topbar-right {\n    width: 100%;\n    justify-content: flex-start !important;\n    flex-wrap: wrap;\n  }\n\n  .theme-toggle,\n  .topbar-right .btn,\n  .topbar-right button,\n  .user-chip {\n    flex: 1 1 150px;\n    width: auto !important;\n  }\n\n  .page-header,\n  .research-hero,\n  .admin-page-header,\n  .sessions-head,\n  .patients-head,\n  .organ-impact-table-head,\n  .voice-chat-header,\n  .molecular-studio-header,\n  .compound-designer-head,\n  .avatar-test-queue-strip {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n\n  .page-header-actions,\n  .page-toolbar-actions,\n  .section-tools,\n  .patient-actions,\n  .patient-card-actions {\n    justify-content: flex-start !important;\n  }\n\n  .page-stats-grid,\n  .metric-grid,\n  .metric-grid-compact,\n  .admin-stats-grid,\n  .summary-grid,\n  .compact-summary-grid,\n  .analysis-grid,\n  .question-grid,\n  .two-col,\n  .impact-pro-grid,\n  .impact-grid,\n  .admin-details-grid,\n  .admin-form-grid,\n  .settings-grid {\n    grid-template-columns: 1fr !important;\n  }\n\n  .page-toolbar-filters,\n  .admin-filter-card,\n  .chemical-search-bar,\n  .assist-search-row {\n    grid-template-columns: 1fr !important;\n  }\n\n  .patients-grid,\n  .patients-page-grid,\n  .sessions-grid,\n  .sessions-page-grid,\n  .chemical-grid,\n  .bio-card-grid,\n  .history-grid-pro,\n  .queue-grid,\n  .studio-picker-grid {\n    grid-template-columns: 1fr !important;\n  }\n\n  .research-tabs,\n  .session-tabs,\n  .bio-library-tabs,\n  .bio-workbench-mode-tabs,\n  .impact-step-tabs,\n  .chemical-az-filter {\n    display: flex !important;\n    overflow-x: auto;\n    flex-wrap: nowrap !important;\n    padding-bottom: 6px;\n    scroll-snap-type: x proximity;\n    -webkit-overflow-scrolling: touch;\n  }\n\n  .research-tabs button,\n  .session-tab,\n  .bio-library-tabs button,\n  .bio-workbench-mode-tabs button,\n  .impact-step-tabs button,\n  .chemical-az-filter button {\n    flex: 0 0 auto;\n    white-space: nowrap;\n    scroll-snap-align: start;\n  }\n\n  .brain-canvas-wrap,\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg,\n  .studio-graph-shell,\n  .studio-molecule-svg {\n    height: 460px !important;\n    min-height: 460px !important;\n  }\n\n  .brain-canvas-card {\n    min-height: 540px !important;\n  }\n\n  .hra-body-viewer,\n  .hra-result-layout > .hra-body-viewer,\n  .d3-organ-intel-panel {\n    height: 540px !important;\n    min-height: 540px !important;\n  }\n}\n\n/* ---------- Mobile ---------- */\n@media (max-width: 600px) {\n  body {\n    font-size: 12.5px !important;\n  }\n\n  .app-layout-shell,\n  .research-page,\n  .admin-page,\n  .session-page {\n    padding: 10px !important;\n  }\n\n  .topbar {\n    padding: 8px 10px !important;\n    border-radius: 0;\n  }\n\n  .topbar-left {\n    gap: 8px !important;\n    width: 100%;\n  }\n\n  .topbar-title {\n    font-size: 17px !important;\n    white-space: normal !important;\n  }\n\n  .topbar-subtitle {\n    display: none;\n  }\n\n  .brand-mark {\n    width: 34px !important;\n    height: 34px !important;\n    flex: 0 0 auto;\n  }\n\n  .hamburger-btn {\n    width: 34px !important;\n    height: 34px !important;\n    flex: 0 0 auto;\n  }\n\n  .theme-toggle,\n  .topbar-right .btn,\n  .topbar-right button,\n  .user-chip {\n    flex: 1 1 100% !important;\n    width: 100% !important;\n  }\n\n  .page-title,\n  h1 {\n    font-size: 25px !important;\n    line-height: 1.08 !important;\n  }\n\n  .page-subtitle,\n  .research-hero p {\n    font-size: 12px !important;\n  }\n\n  .page-header,\n  .research-hero {\n    margin-top: 10px !important;\n    margin-bottom: 12px !important;\n  }\n\n  .metric-card,\n  .stat-tile,\n  .status-item,\n  .summary-item {\n    min-height: 82px !important;\n    padding: 14px !important;\n  }\n\n  .patient-card,\n  .session-card,\n  .research-card,\n  .sidebar-card,\n  .settings-section-card,\n  .brain-side-card,\n  .brain-selected-panel,\n  .brain-canvas-card,\n  .admin-stat-card,\n  .admin-table-card,\n  .card {\n    border-radius: 11px !important;\n    padding: 12px !important;\n  }\n\n  .patient-card-top,\n  .patient-card-bottom,\n  .session-card-head,\n  .session-card-head-top {\n    flex-direction: column !important;\n    align-items: stretch !important;\n  }\n\n  .patient-avatar {\n    width: 38px !important;\n    height: 38px !important;\n  }\n\n  .patient-meta h3,\n  .session-card h3,\n  .session-card h2 {\n    font-size: 15.5px !important;\n  }\n\n  .patient-actions,\n  .patient-card-actions,\n  .session-badge-stack,\n  .admin-actions,\n  .admin-modal-actions,\n  .impact-action-row,\n  .voice-input-row,\n  .bio-library-search,\n  .selected-drugs-actions {\n    width: 100%;\n    display: flex !important;\n    flex-direction: column !important;\n    align-items: stretch !important;\n    margin-left: 0 !important;\n  }\n\n  .patient-actions .btn,\n  .patient-card-actions .btn,\n  .admin-actions button,\n  .impact-action-row button,\n  .voice-input-row button {\n    width: 100%;\n  }\n\n  .mobile-drawer {\n    width: min(330px, 88vw) !important;\n  }\n\n  .brain-canvas-wrap,\n  .relation-graph-canvas-wrap,\n  .relation-graph-svg,\n  .studio-graph-shell,\n  .studio-molecule-svg {\n    height: 390px !important;\n    min-height: 390px !important;\n  }\n\n  .brain-canvas-card {\n    min-height: 470px !important;\n  }\n\n  .hra-body-viewer,\n  .hra-result-layout > .hra-body-viewer,\n  .d3-organ-intel-panel {\n    height: 460px !important;\n    min-height: 460px !important;\n  }\n\n  .voice-chat-window {\n    height: 300px !important;\n  }\n\n  .admin-table,\n  .organ-impact-table,\n  .impact-table {\n    min-width: 760px !important;\n  }\n\n  .studio-picker-modal,\n  .wizard-modal,\n  .admin-modal,\n  .pdf-modal {\n    width: calc(100vw - 20px) !important;\n    max-height: 92vh !important;\n  }\n}\n\n@media (max-width: 380px) {\n  .page-title,\n  h1 {\n    font-size: 22px !important;\n  }\n\n  .topbar-title {\n    font-size: 16px !important;\n  }\n\n  .btn,\n  button,\n  input,\n  select,\n  textarea {\n    font-size: 12px !important;\n  }\n}\n\n/* ==========================================================================\n   MEDGEN DARK TOPBAR PATCH\n   ========================================================================== */\n\n.medgen-topbar {\n  min-height: 68px !important;\n  padding: 10px 18px !important;\n  background:\n    radial-gradient(circle at 8% 0%, rgba(24, 183, 255, 0.16), transparent 28%),\n    linear-gradient(135deg, #03060c 0%, #06101f 48%, #0b1b30 100%) !important;\n  border-bottom: 1px solid rgba(24, 183, 255, 0.18) !important;\n  box-shadow: 0 14px 38px rgba(3, 6, 12, 0.22) !important;\n}\n\n.medgen-topbar .topbar-left,\n.medgen-topbar .topbar-right {\n  display: flex !important;\n  align-items: center !important;\n}\n\n.medgen-topbar .topbar-title {\n  color: #ffffff !important;\n  font-weight: 900 !important;\n  letter-spacing: -0.03em !important;\n}\n\n.medgen-topbar .topbar-subtitle {\n  color: rgba(210, 230, 248, 0.72) !important;\n}\n\n.brand-mark-logo {\n  padding: 0 !important;\n  background: rgba(255, 255, 255, 0.08) !important;\n  border: 1px solid rgba(24, 183, 255, 0.22) !important;\n  overflow: hidden !important;\n  box-shadow: 0 10px 26px rgba(24, 183, 255, 0.16) !important;\n}\n\n.brand-mark-logo img {\n  width: 100% !important;\n  height: 100% !important;\n  display: block !important;\n  object-fit: contain !important;\n  border-radius: inherit !important;\n}\n\n/* Buttons on dark topbar */\n.medgen-topbar .theme-toggle,\n.medgen-topbar .btn-secondary {\n  background: rgba(255, 255, 255, 0.96) !important;\n  color: #07111f !important;\n  border: 1px solid rgba(194, 218, 244, 0.55) !important;\n  box-shadow: 0 8px 22px rgba(3, 6, 12, 0.16) !important;\n}\n\n.medgen-topbar .user-chip {\n  background: rgba(255, 255, 255, 0.97) !important;\n  border: 1px solid rgba(194, 218, 244, 0.55) !important;\n  box-shadow: 0 8px 22px rgba(3, 6, 12, 0.16) !important;\n}\n\n.medgen-topbar .user-name {\n  color: #07111f !important;\n}\n\n.medgen-topbar .user-role {\n  color: #66778e !important;\n}\n\n.medgen-topbar .user-avatar {\n  background: rgba(31, 123, 193, 0.12) !important;\n  color: #0b4e84 !important;\n}\n\n/* Hamburger on dark topbar */\n.medgen-topbar .hamburger-btn {\n  background: rgba(255, 255, 255, 0.96) !important;\n  border: 1px solid rgba(194, 218, 244, 0.55) !important;\n}\n\n.medgen-topbar .hamburger-btn span {\n  background: #07111f !important;\n}\n\n@media (max-width: 760px) {\n  .medgen-topbar {\n    padding: 10px 12px !important;\n  }\n\n  .medgen-topbar .topbar-subtitle {\n    display: none !important;\n  }\n}\n\n\n/* ==========================================================================\n   SESSION PAGE TEXT VISIBILITY PATCH\n   Fixes white text inside light cards placed inside dark session panels\n   ========================================================================== */\n\n.session-page,\n.session-workspace,\n.session-detail-page,\n.session-shell {\n  --session-text: #07111f;\n  --session-muted: #66778e;\n  --session-border: #cddcec;\n  --session-light-card: #f7fbff;\n  --session-white: #ffffff;\n  --session-dark: #06101f;\n}\n\n/* Main light cards on session page */\n.session-page .card,\n.session-page .panel,\n.session-page .info-card,\n.session-page .detail-card,\n.session-page .patient-card,\n.session-page .clinical-card,\n.session-workspace .card,\n.session-workspace .panel,\n.session-workspace .info-card,\n.session-workspace .detail-card,\n.session-workspace .patient-card,\n.session-workspace .clinical-card {\n  color: var(--session-text) !important;\n}\n\n/* Labels inside patient/session detail cards */\n.session-page label,\n.session-page .label,\n.session-page .field-label,\n.session-page .meta-label,\n.session-page .card-label,\n.session-page .detail-label,\n.session-workspace label,\n.session-workspace .label,\n.session-workspace .field-label,\n.session-workspace .meta-label,\n.session-workspace .card-label,\n.session-workspace .detail-label {\n  color: var(--session-muted) !important;\n}\n\n/* Values inside patient/session detail cards */\n.session-page .value,\n.session-page .field-value,\n.session-page .meta-value,\n.session-page .card-value,\n.session-page .detail-value,\n.session-workspace .value,\n.session-workspace .field-value,\n.session-workspace .meta-value,\n.session-workspace .card-value,\n.session-workspace .detail-value {\n  color: var(--session-text) !important;\n}\n\n/* The white boxes inside the dark Session Status panel */\n.session-page .session-status-card,\n.session-page .status-card,\n.session-page .status-box,\n.session-page .status-item,\n.session-page .summary-box,\n.session-page .analysis-box,\n.session-page .review-box,\n.session-workspace .session-status-card,\n.session-workspace .status-card,\n.session-workspace .status-box,\n.session-workspace .status-item,\n.session-workspace .summary-box,\n.session-workspace .analysis-box,\n.session-workspace .review-box {\n  background: var(--session-light-card) !important;\n  color: var(--session-text) !important;\n  border: 1px solid var(--session-border) !important;\n}\n\n/* Any text inside those white status boxes */\n.session-page .session-status-card *,\n.session-page .status-card *,\n.session-page .status-box *,\n.session-page .status-item *,\n.session-page .summary-box *,\n.session-page .analysis-box *,\n.session-page .review-box *,\n.session-workspace .session-status-card *,\n.session-workspace .status-card *,\n.session-workspace .status-box *,\n.session-workspace .status-item *,\n.session-workspace .summary-box *,\n.session-workspace .analysis-box *,\n.session-workspace .review-box * {\n  color: inherit !important;\n}\n\n/* Keep headings inside dark panels white */\n.session-page .dark-panel > h1,\n.session-page .dark-panel > h2,\n.session-page .dark-panel > h3,\n.session-page .dark-panel > h4,\n.session-page .session-status-panel > h1,\n.session-page .session-status-panel > h2,\n.session-page .session-status-panel > h3,\n.session-page .session-status-panel > h4,\n.session-workspace .dark-panel > h1,\n.session-workspace .dark-panel > h2,\n.session-workspace .dark-panel > h3,\n.session-workspace .dark-panel > h4,\n.session-workspace .session-status-panel > h1,\n.session-workspace .session-status-panel > h2,\n.session-workspace .session-status-panel > h3,\n.session-workspace .session-status-panel > h4 {\n  color: #ffffff !important;\n}\n\n/* Paragraphs directly on dark panels should stay readable */\n.session-page .dark-panel > p,\n.session-page .session-status-panel > p,\n.session-workspace .dark-panel > p,\n.session-workspace .session-status-panel > p {\n  color: #a9bdd3 !important;\n}\n\n.app-sidebar-title-small {\n  margin-top: 18px;\n  font-size: 11px;\n  opacity: 0.72;\n  letter-spacing: 0.16em;\n  text-transform: uppercase;\n}"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
